/* StickerFactory - Advanced color selector (Stage 1)
 * Reusable, theme-editor configurable. Reads a JSON config rendered by the
 * snippet, builds the UI, and writes the customer's choices as line item
 * properties (name="properties[...]") attached to the product form via the
 * HTML5 form= attribute. Colors are NEVER variants; only price points are.
 *
 * Pricing rule (honest by design): the displayed price ALWAYS equals the price
 * of the variant the form will submit. A surcharge/option price is only shown
 * when a matching price-point variant is configured (priceMap), so the cart can
 * never charge a different amount than the page shows.
 */
(function () {
  let ACTIVE = null; // selector instance that currently owns the shared popovers

  if (customElements.get('sticker-color-selector')) return;

  customElements.define('sticker-color-selector', class extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      const cfgEl = this.querySelector('.scs-config');
      if (!cfgEl) {
        // Upgraded before children parsed (Shopify section re-render via innerHTML). Retry.
        if (this.isConnected && (this._tries = (this._tries || 0) + 1) < 50) setTimeout(() => this.connectedCallback(), 0);
        return;
      }
      let cfg;
      try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { console.error('[scs] invalid config JSON', e); return; }
      this._init = true; // only after a successful parse, so a fixed re-render can recover
      this.cfg = cfg;
      // Swatch catalog lives in its OWN script so a malformed catalog can't kill the whole selector.
      this.cfg.cut = this.cfg.cut || {};
      const swEl = this.querySelector('.scs-swatches');
      if (swEl) { try { this.cfg.cut.swatches = JSON.parse(swEl.textContent); } catch (e) { console.error('[scs] invalid swatches JSON, using none', e); this.cfg.cut.swatches = this.cfg.cut.swatches || []; } }
      // The `vinilo` panel is merged ON TOP of that catalog, per colour code, in its
      // own script so a malformed panel cannot take the 84 down with it.
      let panelSw = [];
      const spEl = this.querySelector('.scs-swatches-panel');
      if (spEl) { try { const p = JSON.parse(spEl.textContent); if (Array.isArray(p)) panelSw = p; } catch (e) { console.error('[scs] invalid panel swatches JSON, ignoring', e); } }
      this.cfg.cut.swatches = this.placeAfter(this.orderSwatches(this.mergeSwatches(this.cfg.cut.swatches || [], panelSw)));
      // Real-photo gallery (client spec point 3). The media feed lives in its OWN
      // script so a malformed feed can never take the selector down, and so the
      // source can be swapped later (metaobjects, an API, a panel export) by
      // changing only the snippet that prints it. Shape: { "A01": { fotos: [], videos: [] } }.
      this.media = {};
      const gmEl = this.querySelector('.scs-gallery-media');
      if (gmEl) { try { this.media = JSON.parse(gmEl.textContent) || {}; } catch (e) { console.error('[scs] invalid gallery media JSON, ignoring', e); } }
      // The panel he edits is merged ON TOP of the hand-authored theme setting, per
      // colour code. Never the other way round: leftover demo data in the setting must
      // not be able to silently swallow an upload he just made. Its own script tag so
      // a malformed setting cannot take the panel down with it.
      const gpEl = this.querySelector('.scs-gallery-panel');
      if (gpEl) {
        try {
          const p = JSON.parse(gpEl.textContent) || {};
          Object.keys(p).forEach(k => { this.media[k] = p[k]; });
        } catch (e) { console.error('[scs] invalid gallery panel JSON, ignoring', e); }
      }
      this.cfg.galeria = this.cfg.galeria || {};
      // Featured combinations (client spec points 1 + 2). Same treatment as the media
      // feed: its own script, its own snippet, so the source can change without
      // touching this file and a bad feed degrades to "no combos" instead of a dead page.
      this.combos = [];
      const cbEl = this.querySelector('.scs-combos-data');
      if (cbEl) {
        try {
          const p = JSON.parse(cbEl.textContent);
          // `disponible: false` is the client's own off switch and means the combination
          // must not appear at all ("no aparece para seleccionar"), which is NOT the same
          // as a combination whose colour ran out of stock. Dropping it here rather than
          // at render time keeps combosEnabled(), the option indices and the sub-tab rule
          // all consistent for free: filter everything out and the tabs vanish, which is
          // exactly what a product with no combinations should do.
          if (Array.isArray(p)) this.combos = p.filter(c => c && c.disponible !== false);
        }
        catch (e) { console.error('[scs] invalid combos JSON, ignoring', e); }
      }
      this.cfg.combos = this.cfg.combos || {};
      this.combo = null;        // the combination currently applied, if any
      this.cfg.printed = this.cfg.printed || {};
      this.cfg.detalles = this.cfg.detalles || { type: 'none' };
      this.cfg.labels = this.cfg.labels || {};

      this.sectionId = this.dataset.sectionId || '';
      this.formId = this.cfg.formId || '';
      this.form = this.formId ? document.getElementById(this.formId) : this.closest('form');
      if (!this.form) console.warn('[scs] product form "' + this.formId + '" not found; line item properties may not submit.');

      this.state = { option: 'custom', cat: ['', ''], color: [null, null], printed: [], printedSeedHex: [] };
      const cats = this.cfg.cut.categories || [];
      if (cats.length) this.state.cat = [cats[0].id, cats[0].id];

      // Native mode: the product uses real Shopify variants for Opción + Color
      // (native image swap + price). We then render ONLY the custom color palette
      // and reveal it only while a "Color Personalizado" variant is selected.
      this.native = !!this.cfg.nativeVariants;
      this.customIds = new Set((this.cfg.customVariantIds || []).map(String));
      // Out-of-stock colors (client #17): one global list of swatch codes, matched
      // case-insensitively. OOS colors stay visible but block the order.
      this.oos = this.buildOos();

      this.body = this.querySelector('.scs-body');
      this.props = {};
      this.ensurePopovers();
      this.installGate();
      // 2026-10-01: las correcciones de variante de la carga (ensureSimilVariant para un default de
      // categoria oculta, o setNativeOptionValue al sembrar una combinacion) disparan un re-render de
      // Dawn que hace focus() en el radio elegido; si ese radio cae debajo del pliegue, el navegador
      // scrollea la pagina sola y la ficha "aparece mas abajo". Una sola vez por carga se abre una
      // ventana en la que focus() no scrollea (ver noScrollFocusWindow).
      if (!window.__scsLoadScrollGuard) { window.__scsLoadScrollGuard = true; this.noScrollFocusWindow(3500); this.holdPriceDuringLoad(); }
      this.render();
      this.seedDefaults();
      this.applyDeepLink();
    }

    // Shopify re-renders sections (theme editor edits, variant changes), which
    // disconnects this element and would leave its document-level listeners behind to
    // pile up and keep firing against detached markup. A merely MOVED element
    // reconnects synchronously, so only tear down if it is still gone next tick.
    disconnectedCallback() {
      setTimeout(() => {
        if (this.isConnected) return;
        if (this._onIdChange) { document.removeEventListener('change', this._onIdChange, true); this._onIdChange = null; }
        if (this._cbOutside) { document.removeEventListener('click', this._cbOutside); this._cbOutside = null; }
        try { if (this._unsub) { this._unsub(); this._unsub = null; } } catch (e) { /* pub/sub already gone */ }
        if (this._mediaObserver) { this._mediaObserver.disconnect(); this._mediaObserver = null; }
        this.removeComboShot();     // never strand an overlay on the page
        // The Símil note was relocated into Shopify's markup, so it does not go away
        // with this element. Take it with us or a section swap leaves two behind.
        const moved = document.querySelector('variant-selects .scs-simil-note');
        if (moved) moved.remove();
      }, 0);
    }

    /* ---------- helpers ---------- */
    cur() { return this.cfg.currency || 'UYU'; }
    money(n) { return '$' + Number(n || 0).toLocaleString('es-UY') + ',00 ' + this.cur(); }
    /* The client positions each colour in his admin panel ("si al principio, a la
     * mitad o al final"), which arrives as a numeric `orden`. Metaobject fields are
     * Liquid drops, so sorting in Liquid would sort the drop and not its value; the
     * catalog emits `orden` and we sort here instead.
     *
     * The hardcoded fallback catalog carries no `orden`, so leave that untouched:
     * its file order IS the intended order. Sorting is stable via the original
     * index, so equal or missing `orden` never reshuffles colours. */
    /* Fold the `vinilo` panel over the built-in catalog, per colour code.
     *
     * A panel row carries ONLY the fields he actually filled in, so an entry that
     * just renames a colour keeps that colour's existing swatch, group and position.
     * A code the catalog does not have is a new colour, which is a thing he asked
     * for; it lands at the end unless he gave it an orden.
     *
     * Every base colour gets an implicit orden of 10, 20, 30 ... first. Without that,
     * orderSwatches sorts anything without an orden to the very end, so the moment he
     * set a position on one colour it would leap over all 84. The 10-spacing is also
     * what docs/panel-vinilos.md tells him to expect, so "45 puts it between 40 and
     * 50" is true against the built-in list and not only against an imported one.
     *
     * Stock is layered here rather than in Liquid because the panel has to be able to
     * REMOVE a code from the general out-of-stock list, not just add to it. See the
     * three layers documented in sticker-color-selector.liquid.
     */
    mergeSwatches(base, panel) {
      const norm = c => String(c == null ? '' : c).trim().toUpperCase();
      const out = (Array.isArray(base) ? base : []).map((s, i) => Object.assign({}, s, {
        orden: Number.isFinite(Number(s && s.orden)) ? Number(s.orden) : (i + 1) * 10,
      }));
      const at = new Map();
      out.forEach((s, i) => at.set(norm(s.code), i));
      let tail = out.length ? Math.max.apply(null, out.map(s => Number(s.orden))) : 0;

      (Array.isArray(panel) ? panel : []).forEach(p => {
        if (!p || typeof p !== 'object') return;
        const code = norm(p.code);
        if (!code) return;
        // Only keys he actually set. `stock:false` and `orden:0` are real values, so
        // this tests for presence, never for truthiness.
        const patch = {};
        ['name', 'cat', 'img'].forEach(k => { if (typeof p[k] === 'string' && p[k].trim()) patch[k] = p[k]; });
        if (Number.isFinite(Number(p.orden))) patch.orden = Number(p.orden);
        if (typeof p.stock === 'boolean') patch.stock = p.stock;
        if (typeof p.off === 'boolean') patch.off = p.off;
        if (typeof p.after === 'string' && p.after.trim()) patch.after = norm(p.after);

        if (at.has(code)) {
          Object.assign(out[at.get(code)], patch);
        } else {
          tail += 10;
          out.push(Object.assign({ code: code, name: code, cat: 'comunes', img: '', orden: tail }, patch));
        }
      });
      // "Quitar de la web" is applied HERE, once, at the end. Every consumer downstream
      // reads this one array (palette, gallery, swatch(), the combinations, buildOos),
      // so dropping the row is the whole feature: there is no second place to remember
      // and no way for a removed colour to survive in one screen but not another.
      return out.filter(s => s.off !== true);
    }
    /* The out-of-stock set, assembled in the order described in the Liquid:
     * general list, then the panel per colour (which can add OR remove), then the
     * per-product list, which only ever adds. */
    buildOos() {
      const norm = c => String(c == null ? '' : c).trim().toUpperCase();
      const set = new Set((this.cfg.outOfStock || []).map(norm));
      (this.cfg.cut.swatches || []).forEach(s => {
        if (typeof s.stock !== 'boolean') return;
        if (s.stock) set.delete(norm(s.code)); else set.add(norm(s.code));
      });
      (this.cfg.outOfStockProduct || []).forEach(c => set.add(norm(c)));
      set.delete('');
      return set;
    }
    orderSwatches(list) {
      if (!Array.isArray(list)) return [];
      const numbered = list.filter(s => s && Number.isFinite(Number(s.orden)));
      if (!numbered.length) return list;
      return list
        .map((s, i) => ({ s, i }))
        .sort((a, b) => {
          const av = Number.isFinite(Number(a.s.orden)) ? Number(a.s.orden) : Infinity;
          const bv = Number.isFinite(Number(b.s.orden)) ? Number(b.s.orden) : Infinity;
          return av - bv || a.i - b.i;
        })
        .map(x => x.s);
    }
    /* "Ponerlo después de": position a colour by the CODE it should follow.
     *
     * `orden` alone was correct but not usable. To slot a new Glitter between two
     * existing ones he had to work out their positions among 84 colours, which is
     * exactly the counting he asked not to have to do ("no armarme un mareo").
     * Naming the neighbour needs no arithmetic and the codes are already on screen.
     *
     * Runs AFTER the numeric sort, so `despues_de` wins when both are set. That is
     * deliberate: it means the guide can tell him to use one control and ignore the
     * other, instead of explaining how two orderings interact.
     *
     * An unknown target leaves the colour where the sort put it, rather than dropping
     * it or sending it to the end. A typo therefore costs him nothing visible, and a
     * colour positioned after one he later removes just stays put.
     */
    placeAfter(list) {
      if (!Array.isArray(list)) return [];
      const norm = c => String(c == null ? '' : c).trim().toUpperCase();
      const moves = list.filter(s => s && s.after && norm(s.after) !== norm(s.code));
      if (!moves.length) return list;
      const out = list.slice();
      const placed = new Set();
      moves.forEach(s => {
        const target = norm(s.after);
        const from = out.findIndex(x => norm(x.code) === norm(s.code));
        if (from < 0) return;
        if (out.findIndex(x => norm(x.code) === target) < 0) return; // target does not exist
        const moved = out.splice(from, 1)[0];
        const ti = out.findIndex(x => norm(x.code) === target);      // recompute: the splice shifted it
        if (ti < 0) { out.splice(from, 0, moved); return; }          // target WAS the one we lifted
        // Several colours can name the same neighbour. Skip past the ones already
        // placed behind it so they keep the order he listed them in, instead of each
        // new arrival jumping in front of the previous one.
        let ins = ti + 1;
        while (ins < out.length && placed.has(norm(out[ins].code)) &&
               out[ins].after && norm(out[ins].after) === target) ins++;
        out.splice(ins, 0, moved);
        placed.add(norm(moved.code));
      });
      return out;
    }

    /* ---------- production lead time ----------
     * Client 2026-08-13: the days must be editable by him ("en invierno que hay menos
     * demanda"), and the buyer must read a DATE, not a count. He asked to kill
     * "¿qué día está pronto?", and that question exists precisely because nobody
     * counts business days.
     *
     * The date is computed here rather than in Liquid on purpose: Shopify caches the
     * rendered page, so a Liquid date would freeze and eventually promise a day in
     * the past. Computing on load means it is always correct without cache-busting. */
    leadDays() {
      const n = Number((this.cfg.lead || {}).days);
      return Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
    }
    /* The days he does not work, on top of the weekend. He asked for this himself:
     * "Lo único que me preocupa es que el sistema no entienda si hay feriados en
     * uruguay de por medio." It is a list HE edits rather than a table baked in here,
     * because a hardcoded calendar rots silently and starts lying a year later, and
     * because his own shutdowns are not public holidays. Cached: leadDate walks it
     * once per day counted. */
    leadHolidays() {
      if (this._hol) return this._hol;
      const set = new Set();
      const pad = n => String(n).padStart(2, '0');
      ((this.cfg.lead || {}).holidays || []).forEach(v => {
        const s = String(v == null ? '' : v).trim();
        // He types these by hand, so accept the shapes he is actually likely to write:
        // the padded form we preload, an unpadded 2026-8-5, and 25/08/2026 which is the
        // normal Uruguayan order. Without normalising, an unpadded date silently does
        // nothing, and a silent no-op in a date list is the worst possible failure here.
        let m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
        if (!m) {
          const dmy = /^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/.exec(s);
          if (dmy) m = [null, dmy[3], dmy[2], dmy[1]];
        }
        if (!m) return;                                  // anything else is a typo, ignore it
        const mo = +m[2], da = +m[3];
        if (mo < 1 || mo > 12 || da < 1 || da > 31) return;
        set.add(m[1] + '-' + pad(mo) + '-' + pad(da));
      });
      this._hol = set;
      return set;
    }
    static ymd(d) {
      return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' +
             String(d.getDate()).padStart(2, '0');
    }
    /* Business days = calendar days skipping Saturday, Sunday and anything on his
     * non-working list. `from` exists so the arithmetic is testable against a fixed day.
     * The step cap is a backstop: if he ever pasted a list that blanks out a whole year
     * this returns a wrong date instead of hanging the page on an infinite loop. */
    leadDate(days, from) {
      const d = from ? new Date(from.getTime()) : new Date();
      d.setHours(12, 0, 0, 0); // midday, so a DST jump cannot roll the date over
      const hol = this.leadHolidays();
      let left = Math.max(0, Math.floor(Number(days) || 0));
      let steps = 0;
      while (left > 0 && steps < 3650) {
        d.setDate(d.getDate() + 1);
        steps++;
        const w = d.getDay();
        if (w === 0 || w === 6) continue;
        if (hol.has(this.constructor.ymd(d))) continue;
        left--;
      }
      return d;
    }
    leadFecha(from, withYear) {
      const days = this.leadDays();
      if (days === null) return '';
      const L = this.cfg.lead || {};
      const d = this.leadDate(days, from);
      const opts = { weekday: 'long', day: 'numeric', month: 'long' };
      if (withYear) opts.year = 'numeric';
      let out;
      try { out = d.toLocaleDateString(L.locale || 'es-UY', opts); }
      catch (e) { return d.getDate() + '/' + (d.getMonth() + 1) + (withYear ? '/' + d.getFullYear() : ''); }
      // es-UY renders "jueves, 20 de agosto". The comma is correct on its own but reads
      // wrong inside his sentence ("listo para el jueves, 20 de agosto"), so drop the
      // one that follows the weekday and leave the rest of the locale's format alone.
      return String(out).replace(/^([^\s,]+),\s+/, '$1 ');
    }
    // The visible line. Blanking the text setting removes it entirely.
    leadText(from) {
      const days = this.leadDays();
      if (days === null) return '';
      const tpl = String((this.cfg.lead || {}).text || '');
      if (!tpl.trim()) return '';
      return tpl.split('{fecha}').join(this.leadFecha(from)).split('{dias}').join(String(days));
    }
    /* La misma linea, pero con el dia en negrita. Cliente 2026-09-16, punto 3: "Cambiar el
     * texto a este y el dia en negrita", con la captura "Comprando hoy, queda pronto para el
     * **viernes 4** de septiembre".
     *
     * Solo se pone en negrita el DIA, o sea "viernes 4", no el mes. La fecha del locale viene
     * como "viernes 4 de septiembre", asi que se corta en el primer " de ": lo de antes va en
     * negrita y lo de despues queda como esta. Si el locale falla, leadFecha() devuelve "4/9",
     * que no tiene " de " y entonces se marca entera.
     *
     * La plantilla la escribe el; se escapa igual que siempre y el unico HTML que se inyecta
     * es el <strong> que ponemos aca, con su contenido tambien escapado. Se mantiene leadText()
     * en texto plano porque es la que viaja al pedido y al carrito, donde no se dibuja HTML. */
    leadTextHTML(from) {
      const days = this.leadDays();
      if (days === null) return '';
      const tpl = String((this.cfg.lead || {}).text || '');
      if (!tpl.trim()) return '';
      const e = this.esc;
      const fecha = this.leadFecha(from);
      let fechaHTML = '';
      if (fecha) {
        const corte = fecha.indexOf(' de ');
        fechaHTML = corte > 0
          ? '<strong>' + e(fecha.slice(0, corte)) + '</strong>' + e(fecha.slice(corte))
          : '<strong>' + e(fecha) + '</strong>';
      }
      return e(tpl).split('{fecha}').join(fechaHTML).split('{dias}').join(e(String(days)));
    }
    // Titulo opcional arriba del recuadro. Vacio no dibuja nada, igual que el texto.
    leadTitle() {
      return String((this.cfg.lead || {}).title || '').trim();
    }
    /* ---------- fits over the original decals ----------
     * The FAQ that costs him money: "La gente no lo lee en el manual que les envío y
     * luego me envían videos diciendo que no calzan arriba."
     *
     * Returns null when he has not set it on this product, and that is deliberate. An
     * unset field means nobody has checked this model yet, so the page says nothing
     * rather than guessing. A wrong "apto" here is worse than no answer at all. */
    fitInfo() {
      const f = this.cfg.fit || {};
      if (f.set !== true) return null;
      const ok = f.ok === true;
      const text = String((ok ? f.yesText : f.noText) || '').trim();
      if (!text) return null;
      return { ok: ok, text: text, note: String(f.note || '').trim() };
    }
    /* Rides on the order so the same answer can be repeated in the WhatsApp despatch
     * message later. That is the whole point: they do not read the paper manual, they
     * do read the message. Short canonical values, not the on-page sentence, so a
     * template can branch on them. */
    fitProp() {
      const label = this.clave((this.cfg.fit || {}).propertyLabel, '');
      const info = this.fitInfo();
      if (!label || !info) return null;
      return { k: label, v: info.ok ? 'Apto sobre los originales' : 'Hay que retirar los originales' };
    }

    /* Travels with the order so the production panel and the WhatsApp message can use
     * the same promise the buyer was shown, instead of recalculating it and drifting.
     *
     * R7-3, cliente 2026-08-28. El escribio como quiere que se lea la linea:
     *   "Tiempo estimado: Viernes 4 de septiembre"
     * Dos cambios respecto de lo que habia. Va con mayuscula inicial, que es como lo escribio y
     * ademas como se leen las otras lineas del carrito. Y va SIN año: lo llevaba a proposito,
     * porque un panel que cruza fin de año podria confundirse, pero lo cierto es que asi el
     * pedido decia una fecha distinta de la que el comprador leyo en la ficha, que no lleva año.
     * Que las dos digan lo mismo vale mas que un año que en la practica siempre es el proximo
     * mes. Si algun dia el panel lo necesita, se agrega ahi y no en lo que ve el comprador. */
    leadProp(from) {
      const L = this.cfg.lead || {};
      const label = this.clave(L.propertyLabel, '');
      const dias = this.leadDays();
      if (!label || dias === null) return null;
      const fecha = this.mayusInicial(this.leadFecha(from, false));
      if (!fecha) return null;
      /* R7-4, pregunta del cliente 2026-08-28: "que pasa con los tiempos distintos segun el
       * producto en el carrito de varios kit? ... un kit demora 5 dias y otro 7, el cliente debe
       * entender que la demora va a ser 7".
       *
       * Para poder decir eso, el carrito tiene que poder COMPARAR las fechas de sus lineas, y el
       * texto que lee el comprador ("Viernes 4 de septiembre") no se puede comparar. Viaja
       * entonces la misma fecha dos veces en un solo dato: ordenable adelante, legible atras.
       * La clave empieza con guion bajo, que es la convencion de Shopify para un dato que va en
       * el pedido pero no se muestra al comprador, asi que no le agrega ninguna linea al carrito
       * ni al checkout. Y sale de ACA y no de un calculo aparte, para que no puedan discrepar. */
      const d = this.leadDate(dias, from);
      const p2 = n => (n < 10 ? '0' : '') + n;
      const iso = d ? d.getFullYear() + '-' + p2(d.getMonth() + 1) + '-' + p2(d.getDate()) : '';
      return { k: label, v: fecha, iso: iso };
    }
    mayusInicial(t) {
      const s = String(t || '');
      return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
    }

    swatch(code) { return (this.cfg.cut.swatches || []).find(s => s.code === code); }
    cat(id) { return (this.cfg.cut.categories || []).find(c => c.id === id); }
    catName(id) { const c = this.cat(id); return c ? c.name : id; }
    isOos(code) { return !!(this.oos && code && this.oos.has(String(code).trim().toUpperCase())); }
    normHex(h) { h = String(h || '').trim().replace('#', ''); if (/^[0-9a-fA-F]{3}$/.test(h)) h = h.split('').map(x => x + x).join(''); return /^[0-9a-fA-F]{6}$/.test(h) ? '#' + h.toLowerCase() : '#cccccc'; }
    // Returns a background-IMAGE value (url or gradient) safe to assign via
    // element.style.backgroundImage. Kept as an image (solids -> flat gradient)
    // so it never touches the `background` shorthand, which would reset the CSS
    // background-size:cover and make photo swatches tile/misalign.
    swatchBg(s) {
      if (s && s.img) return "url('" + String(s.img).replace(/['")]/g, '') + "')";
      const c = (s && s.css) || '#eee';
      return /gradient/i.test(c) ? c : 'linear-gradient(' + c + ',' + c + ')';
    }

    /* ---------- render ---------- */
    render() {
      const c = this.cfg, L = c.labels, e = this.esc, N = c.notes || {};
      let html = '';
      // Lead time goes FIRST: he asked for it visible before the buy button, and it is
      // true for every option on the page, so it does not belong inside any one of them.
      /* T6, client 2026-08-21: "esto pasarlo para abajo como esta en Simil Original y en ese lugar
       * me gustaria tener un texto editable que pueda explicar resumido Que en Color personalizado
       * contamos con 2 opciones de venta ..." y en el hilo "en vinilo impreso tambien va para
       * abajo". El plazo se sigue dibujando aca, pero en Colores Personalizados moveSimilChrome()
       * lo baja hasta el aviso de colocacion, que es donde queda en Simil Original: cerca del
       * precio. Se mueve el nodo en vez de duplicarlo, para no leerlo dos veces. */
      /* El plazo pasa a ser un envoltorio con titulo opcional + recuadro, en vez de un solo
       * recuadro con texto. `.scs-lead` sigue siendo el nodo que se mueve y el que miran
       * placeLeadAndNote() y keepVisible(), asi que nada de eso cambia. */
      const leadLine = this.leadTextHTML();
      if (leadLine) {
        const leadTit = this.leadTitle();
        html += `<div class="scs-lead">`;
        if (leadTit) html += `<div class="scs-lead-title">${e(leadTit)}</div>`;
        html += `<div class="scs-lead-box">${leadLine}</div></div>`;
      }
      if (N.customOptionsNote) html += `<div class="scs-cnote" hidden>${e(N.customOptionsNote)}</div>`;
      // In native mode Opción + the Simil color list ARE Shopify variants, so we
      // don't render our own copies (that is what broke the native image swap).
      if (!this.native) {
        html += `<div class="scs-field"><label>${e(L.option ?? 'Opción:')}</label>
          <select class="scs-select scs-opcion">
            <option value="simil">${e(L.similOriginal ?? 'Símil Original')}</option>
            <option value="custom" selected>${e(L.custom ?? 'Colores Personalizados')}</option>
          </select></div>`;
        // Client spec page 1: the note belongs BETWEEN the Opción control and the
        // Símil colour list, so it is emitted here rather than after both of them.
        if (N.simil) html += `<div class="scs-simil-note" hidden>${e(N.simil)}</div>`;
        const sim = (c.similOptions || []).map(o => `<option>${e(o)}</option>`).join('');
        html += `<div class="scs-field scs-simil" hidden><label>${e(L.simil ?? 'Color:')}</label>
          <select class="scs-select scs-simil-sel">${sim}</select></div>`;
      } else if (N.simil) {
        // Native mode: Opción and "Color de la moto" are Shopify's own markup and sit
        // ABOVE this whole block, so the note is created here and then MOVED into
        // position by placeSimilNote(). See that method for why it has to re-run.
        html += `<div class="scs-simil-note" hidden>${e(N.simil)}</div>`;
      }
      // Client 2026-08-15: on a combo (a recipe HE built), Detalles is his own preset,
      // shown read-only inside the combo's own detail (see the "Detalle: -" row below).
      // It is only an open field for the buyer on Fabricación Personalizada, so it must
      // live INSIDE that pane's markup, not appended after the whole cut/printed block
      // where it would render regardless of which pane is active.
      const d = c.detalles;
      let detHtml = '';
      /* R7-7, cliente 2026-08-28: "como puedo hacer para que cuando un detalle tiene una sola
       * opcion que no abra un menu sino que aparezca solo texto ya que el cliente no tiene
       * opciones para elegir ... que se ponga el menu cuando hay 2 opciones o mas pero si hay una
       * aparezca una linea de texto fija informativa Fondo: Transparente".
       *
       * Mismo criterio que R5-3 con el boton de Opcion: un selector con un solo valor no es una
       * eleccion, es un cartel, y hacerle abrir un menu para elegir lo unico que hay es trabajo
       * de mas. Se decide por la CANTIDAD de opciones que el cargo, no por un ajuste nuevo, asi
       * que no hay nada que se pueda olvidar de tildar: carga una segunda opcion y el menu vuelve
       * solo.
       *
       * El dato sigue viajando igual al pedido; lo unico que cambia es como se muestra. Eso lo
       * cubre syncProps(), que lee del span cuando no hay select. */
      const detOpts = (d.options || []).filter(o => String(o).trim() !== '');
      if (d.type === 'dropdown' && detOpts.length > 1) {
        const opts = detOpts.map(o => `<option>${e(o)}</option>`).join('');
        detHtml = `<div class="scs-field scs-det-field"><label>${e(d.label ?? 'Detalles:')}</label><select class="scs-select scs-det-sel">${opts}</select></div>`;
      } else if (d.type === 'dropdown' && detOpts.length === 1) {
        detHtml = `<div class="scs-field scs-det-field scs-det-fija"><label>${e(d.label ?? 'Detalles:')}</label><span class="scs-det-fijo">${e(detOpts[0])}</span></div>`;
      } else if (d.type === 'textarea') {
        detHtml = `<div class="scs-field scs-det-field"><label>${e(d.label ?? 'Notas')}</label><textarea class="scs-textarea scs-det-text" placeholder="${e(d.label ?? 'Notas')}"></textarea></div>`;
      }
      // Client 2026-08-15: "en vinilo impreso faltan también los textos que hiciste
      // del formato nuevo" - these two blocks are generic settings (s.custom_title_1/2,
      // s.made_title/text), not cut-specific, so both product types get them. Singular
      // wording for a one-colour product either way: no second cut selector, or a
      // printed product configured for exactly one CMYK colour.
      const single = c.type === 'cut' ? !this.secondActive() : (+c.printed.cmykCount || 1) <= 1;
      const it = single ? N.customTitle1 : N.customTitle2;
      const ix = single ? N.customText1 : N.customText2;
      const intro = (it || ix)
        ? `<div class="scs-intro">${it ? `<div class="scs-intro-t">${e(it)}</div>` : ''}${ix ? `<div class="scs-intro-x">${e(ix)}</div>` : ''}</div>` : '';
      /* T10, client 2026-08-21: "aparece esto en vinilo impreso y ahi no hay galeria (de momento) ...
       * creo que ese texto estaba para otra cosa y como yo lo simplifique no estaba pensado para no
       * mostrarse en vinilo impreso". El texto dice literalmente que uses la galeria para ver fotos
       * reales, asi que en un producto sin galeria manda a un lado que no existe.
       * Se ata a galleryEnabled() y no al tipo de producto, justamente por su "de momento": el dia
       * que un impreso tenga galeria, el bloque vuelve solo. */
      /* Cliente 2026-09-16, punto 4: "El bloque de Explorar galeria que sea como el ejemplo que te
       * habia hecho". Su boceto es UNA fila: foto chica a la izquierda, titulo en negrita y texto
       * en cursiva al medio, y el boton negro a la derecha.
       *
       * Antes el titulo, el texto y el boton eran tres cosas sueltas una abajo de la otra. Ahora
       * van dentro del mismo `.scs-made`, que es el que ya tenia la linea de arriba que lo separa
       * del resto (R5-1). Cada pieza sigue siendo opcional: sin foto arranca por el texto, y sin
       * titulo ni texto queda la fila con la foto y el boton. */
      const madeParts = [];
      if (N.madeTitle) madeParts.push(`<div class="scs-made-t">${e(N.madeTitle)}</div>`);
      if (N.madeText) madeParts.push(`<div class="scs-made-x">${e(N.madeText)}</div>`);
      const made = ((N.madeTitle || N.madeText) && this.galleryEnabled())
        ? `<div class="scs-made-txt">${madeParts.join('')}</div>` : '';
      /* Cliente 2026-09-23: fotos/video explicando como elegir los colores, "que aparezcan
       * debajo como en el resto pero que no se pasen para Simil original ni Colores disponibles.
       * Que queden en Elegir mis colores". Por eso se calcula aca y se agrega al final de `inner`
       * en las dos ramas (corte e impreso): `inner` es lo que wrapPanes() mete en la pestana de
       * Colores Personalizados, asi que este bloque nunca llega ni a Simil ni a Colores disponibles. */
      const explica = this.explicaHTML();
      if (c.type === 'cut') {
        // Client spec point 3: entry point to the colour gallery. Rendered only when
        // there is a catalog to show, so a misconfigured product never offers an
        // empty overlay. Impresos have no gallery (their colours are free HEX).
        const G = c.galeria || {};
        /* La fila entera del punto 4. El boton queda ADENTRO de `.scs-made` para poder ponerlo a
         * la derecha del texto; el manejador lo busca con q('.scs-gal-open'), que es una consulta
         * por descendiente, asi que anidarlo no lo desconecta. La nota de abajo, cuando la usa,
         * sigue colgando debajo de la fila y ocupando el ancho entero. */
        let gal = '';
        if (this.galleryEnabled()) {
          const foto = G.image
            ? `<img class="scs-made-img" src="${e(G.image)}" alt="${e(G.imageAlt || '')}" loading="lazy" width="88" height="88">`
            : '';
          const boton = `<button type="button" class="scs-gal-open">${e(G.buttonLabel ?? 'Ver')}</button>`;
          gal = `<div class="scs-made">${foto}${made}${boton}</div>` +
                (G.buttonNote ? `<div class="scs-gal-note-sm">${e(G.buttonNote)}</div>` : '');
        }
        /* Client 2026-08-21, with a sketch: "la ubicación de Detalles debería estar en la
         * Receta" - Color 1 / Color 2 / separator / Detalles. It used to sit after the gallery
         * block, which put it below a paragraph of explanatory text and far from the choices it
         * belongs to. Moved up against the colour rows; `scs-det-field` keeps its own separator
         * so the grouping reads the way he drew it. */
        const detBlock = detHtml ? `<div class="scs-receta-det">${detHtml}</div>` : '';
        const inner = `${intro}${this.cutRow(0)}` + (this.secondActive() ? this.cutRow(1) : '') + `${detBlock}${gal}${explica}`;
        html += `<div class="scs-cut">${this.wrapPanes(inner)}</div>`;
      } else {
        const n = Math.max(1, Math.min(4, c.printed.cmykCount || 1));
        let dots = '';
        for (let i = 0; i < n; i++) {
          if (!this.state.printed[i]) {
            const s0 = this.printedSeed(i);
            this.state.printed[i] = s0.st;
            this.state.printedSeedHex[i] = s0.hex; // show the client's EXACT configured colour until the buyer picks
          }
          const hex = this.state.printedSeedHex[i] || this.colorFromState(this.state.printed[i]).hex;
          dots += `<div class="scs-cmyk-item">
            <div class="scs-dot" data-i="${i}" style="background:${hex}"></div>
            <div class="scs-nm">${e(L.colorWord ?? 'Color')} ${i + 1}</div>
            <div class="scs-hx" data-hx="${i}">${hex.toUpperCase()}</div></div>`;
        }
        // Client 2026-08-15: Impreso has no gallery (its colours are free HEX), so the
        // buyer needs telling that nothing here is a live preview - the plotter prints
        // whatever tone they pick. Cut does not need this, it has the real gallery.
        /* T7 + T9, client 2026-08-21: "Detalles va mas arriba tambien en el bloque de la receta.
         * Los 4 selectores y debajo Detalles si esta cargado", y sobre la captura del desplegable
         * "una separacion aqui tambien". En corte esto ya se hizo en la ronda 3; en impreso Detalles
         * quedaba al final, despues de los textos, y sin ninguna linea que lo separara. Se sube
         * contra la fila de tonos y se envuelve en el mismo `.scs-receta-det` que usa corte, que es
         * el que trae la linea y el aire. */
        const detBlockImp = detHtml ? `<div class="scs-receta-det">${detHtml}</div>` : '';
        const inner = `${intro}<div class="scs-cmyk-row">${dots}</div>${detBlockImp}` +
          (c.disclaimer ? `<div class="scs-disclaimer">${e(c.disclaimer)}</div>` : '') +
          (c.noPreviewNotice ? `<div class="scs-disclaimer">${e(c.noPreviewNotice)}</div>` : '') +
          // En impreso no hay boton de galeria, asi que el texto va solo y hay que darle el
          // envoltorio `.scs-made` a mano: es el que trae la linea de arriba y el aire.
          (made ? `<div class="scs-made">${made}</div>` : '') + explica;
        html += `<div class="scs-printed">${this.wrapPanes(inner)}</div>`;
      }
      // Last thing in the body, which puts it immediately above the add-to-cart button.
      // Deliberately NOT inside the collapsed questions: this is the one he loses money
      // on, so it has to be unmissable rather than discoverable.
      const fit = this.fitInfo();
      if (fit) {
        html += `<div class="scs-fit ${fit.ok ? 'is-ok' : 'is-no'}">` +
          `<span class="scs-fit-i" aria-hidden="true">${fit.ok ? '&#10004;' : '&#10006;'}</span>` +
          `<span class="scs-fit-t">${e(fit.text)}` +
          (fit.note ? `<span class="scs-fit-n">${e(fit.note)}</span>` : '') +
          `</span></div>`;
      }
      html += `<div class="scs-warn" hidden></div>`;
      // A previously relocated Símil note lives OUTSIDE this element, so replacing the
      // body would strand it. Drop it here; placeSimilNote() puts a fresh one back.
      const strayNote = document.querySelector('variant-selects .scs-simil-note');
      if (strayNote) strayNote.remove();
      this.body.innerHTML = html;
      if (this.combosEnabled()) this.buildCombosPane();
      this.bind();
      if (this.native) this.initNative(); else this.onOptionChange();
    }

    /* R6-5, cliente 2026-08-26: "hay forma de poder poner colores predeterminados en los selectores
     * de Elegir mis colores? me parece que seria mas intuitivo que cuando entras a esa seccion
     * pueda dejar pre seteado que muestre el color que coincide con la imagen".
     *
     * Se siembra SOLO lo que este vacio. Tres cosas que no se pueden pisar, en este orden:
     *  - una combinacion que se auto-eligio (selectCombo ya escribio state.color, asi que aca no
     *    queda nada vacio y no se siembra). Por eso NO se usa pickSwatch(): pickSwatch llama a
     *    clearCombo() y borraria la combinacion que el cliente marco como predeterminada.
     *    OJO, eso vale mientras el comprador esta MIRANDO las combinaciones. Cuando se pasa a la
     *    otra pestaña esa combinacion deja de correr y reseedDefaults() libera las ranuras para
     *    que esto vuelva a sembrar. Ver R7-2 mas abajo: era justo lo que el cliente reclamaba.
     *  - un ?color= de Instagram: applyDeepLink() corre DESPUES y pisa lo sembrado, que es la
     *    precedencia correcta, porque el link es una intencion explicita del comprador.
     *  - lo que el comprador ya eligio: si cambia de categoria, onCatChange() limpia el color y
     *    esto no vuelve a correr por su cuenta. Desde R7-2 tambien corre al entrar a la pestaña
     *    de colores propios, pero solo cuando la combinacion que se estaba mostrando la habia
     *    elegido el sistema, nunca cuando la eligio el comprador.
     *
     * Un codigo retirado, mal escrito o sin stock se ignora en silencio, igual que en los deep
     * links: el cliente puede sacar un color de la web mucho despues de haberlo puesto de default,
     * y eso no puede dejar el producto sembrado con algo que no se puede comprar.
     *
     * Ojo, efecto buscado y hay que avisarle: con un color ya puesto, el candado de "elegi un
     * color para continuar" deja de proteger en esa pestaña. Es exactamente lo que pidio. El
     * candado de la pestaña de combinaciones no se toca: sigue exigiendo elegir una combinacion. */
    seedDefaults() {
      const c = this.cfg;
      if (c.type !== 'cut') return;      // impreso ya siembra sus hex dentro de render()
      const defs = (c.cut && c.cut.defaults) || [];
      if (!defs.length) return;
      const max = this.secondActive() ? 2 : 1;
      let algo = false;
      for (let i = 0; i < max; i++) {
        if (this.state.color[i]) continue;
        const code = String(defs[i] || '').trim().toUpperCase();
        if (!code) continue;
        const s = this.swatch(code);
        if (!s || this.isOos(s.code)) continue;
        if (s.cat) {
          this.state.cat[i] = s.cat;
          const sel = this.qa('.scs-cat')[i]; if (sel) sel.value = s.cat;
        }
        this.state.color[i] = s.code;
        const fld = this.qa('.scs-swatch-field')[i];
        if (fld) fld.querySelector('.scs-cf-text').textContent = s.code + ' - ' + s.name;
        this.updatePreview(i);
        algo = true;
      }
      if (algo) { this.buildPalette(); this.recalc(); }
    }
    /* Devuelve la pestaña de colores propios a los predeterminados que cargo el cliente.
     *
     * R7-2 la escribio para corte y solo cuando la combinacion la habia elegido el sistema: si la
     * elegia el comprador, sus colores viajaban a esta pestaña, con la idea de que sirviera para
     * "me gusta esta pero le cambio uno".
     *
     * R8, cliente 2026-08-28 con un video del Wave S 2021: "cambias de combinacion y se cambia el
     * color predeterminado en la otra pestaña". No quiere eso. Sus predeterminados mandan siempre,
     * sin importar quien eligio la combinacion, asi que la distincion se saco entera junto con la
     * bandera que la sostenia. Queda mas simple y, sobre todo, predecible: esa pestaña muestra lo
     * que el dejo puesto, punto.
     *
     * En el mismo lote reporto que "los colores en vinilo impreso no quedan predeterminados". Era
     * el mismo defecto: esto devolvia temprano en impreso, asi que la combinacion auto-elegida le
     * pisaba los tonos y nada los reponia. Ahora cubre los dos tipos.
     *
     * Sin predeterminados cargados no hace nada, ni en corte ni en impreso. Importa: en impreso,
     * reponer "sin datos" pintaria los cuatro tonos de gris y borraria los de la combinacion, que
     * es peor que no hacer nada. */
    reseedDefaults() {
      const c = this.cfg;
      if (c.type === 'cut') {
        const defs = (c.cut && c.cut.defaults) || [];
        if (!defs.length) return;
        const max = this.secondActive() ? 2 : 1;
        let algo = false;
        for (let i = 0; i < max; i++) {
          const code = String(defs[i] || '').trim().toUpperCase();
          if (!code) continue;
          const s = this.swatch(code);
          if (!s || this.isOos(s.code)) continue;
          this.state.color[i] = null;   // liberarla para que seedDefaults() la llene
          algo = true;
        }
        if (algo) this.seedDefaults();
        return;
      }
      // impreso
      const defs = ((c.printed || {}).defaults || []).filter(d => d !== null && d !== undefined && String(d).trim() !== '');
      if (!defs.length) return;
      const n = Math.max(1, Math.min(4, (c.printed || {}).cmykCount || 1));
      for (let i = 0; i < n; i++) {
        const s0 = this.printedSeed(i);
        this.state.printed[i] = s0.st;
        this.state.printedSeedHex[i] = s0.hex;
        const hex = s0.hex || this.colorFromState(s0.st).hex;
        const dot = this.q(`.scs-dot[data-i="${i}"]`); if (dot) dot.style.background = hex;
        const hx = this.q(`[data-hx="${i}"]`); if (hx) hx.textContent = String(hex).toUpperCase();
      }
      this.recalc();
    }
    /* Con que arranca el tono `i` de un impreso, segun los predeterminados del producto.
     * Escrito una sola vez a proposito: lo usan render() al construir y reseedDefaults() al volver
     * a la pestaña, y si fueran dos copias la ficha podria mostrar un color al cargar y otro al
     * cambiar de pestaña sin que nadie entendiera por que. */
    printedSeed(i) {
      const dv = ((this.cfg.printed || {}).defaults || [])[i];
      if (dv && typeof dv === 'object' && 'hue' in dv) {
        return { st: { hue: +dv.hue || 0, sat: +dv.sat || 0, k: +dv.k || 0 }, hex: null };
      }
      const h = this.normHex(typeof dv === 'string' ? dv : '#cccccc');
      return { st: this.hexToState(h), hex: h };
    }

    /* ---------- deep links for Instagram ----------
     * Client 2026-08-13: "Lo del link que me propones está genial." What he does today
     * is either a story that merely NAMES a colour, or one that sends people to Mercado
     * Libre; either way they land somewhere and have to find the thing again, and he
     * described that as "no entendían qué hacer".
     *
     *   ?color=C06     colour 1 preselected, on the custom tab
     *   ?color2=A02    the second colour, on two-colour kits
     *   ?combo=<ref>   one of his featured combinations, by its own reference
     *   ?ver=C06       opens the colour gallery straight on that colour's real photos
     *
     * Every unknown value is ignored rather than reported. A story stays up for years
     * and a colour can be retired in between (that is what "Quitar de la web" does), so
     * an old link has to land on a working product page, never on an error. */
    /* En modo nativo la Opción (Símil Original / Colores Personalizados) es una variante de
     * Shopify, no un control nuestro. Un deep link aterriza en la variante por defecto, que es
     * Símil, así que el color o la combinación que se aplican abajo quedan puestos pero escondidos
     * bajo Símil y el visitante ve la moto lisa. Esto conmuta la Opción a Colores Personalizados
     * para que lo elegido se vea. Devuelve false sin hacer nada si no es nativo, si ya está en
     * custom, o si el producto no tiene esa opción (custom-only).
     * 2026-09-23: sin esto los links de Instagram caían siempre en Símil Original desde que la
     * tienda pasó a variantes nativas (tema portada, 2026-09-11). setNativeOptionValue dispara el
     * change nativo, que corre applyNativeState en captura y muestra la sección custom; recién
     * después se aplican showPane/pickSwatch/selectCombo, sobre la sección ya visible. */
    selectCustomNative() {
      if (!this.native || this.isCustomSelected()) return false;
      const vs = document.querySelector('variant-selects');
      if (!vs) return false;
      let val = null;
      vs.querySelectorAll('input[type=radio], option').forEach(el => {
        if (val === null && /personaliz/i.test(String(el.value || ''))) val = el.value;
      });
      if (val === null) return false;
      this.setNativeOptionValue(String(val));
      return true;
    }

    applyDeepLink() {
      let p;
      try { p = new URLSearchParams(window.location.search); } catch (e) { return; }
      const norm = k => String(p.get(k) || '').trim().toUpperCase();

      // A combination carries BOTH colours, so it wins outright and returns: letting a
      // stray ?color= run afterwards would half-overwrite it and clear the combo ref.
      const ref = String(p.get('combo') || '').trim();
      if (ref && this.combosEnabled()) {
        const i = this.combos.findIndex(c => c &&
          String(c.ref || c.id || '').trim().toUpperCase() === ref.toUpperCase());
        if (i >= 0 && !this.comboUnavailable(this.combos[i])) {
          this.selectCustomNative();   // nativo: pasar la Opción a Colores Personalizados
          this.showPane('combos');
          this.selectCombo(i);
          return;
        }
      }

      let moved = false;
      [norm('color'), norm('color2')].forEach((code, i) => {
        if (!code) return;
        if (i === 1 && !this.secondActive()) return;
        const s = this.swatch(code);
        if (!s) return;                       // retired or mistyped: leave the page as it was
        if (!moved) {
          this.selectCustomNative();          // nativo: pasar la Opción a Colores Personalizados
          if (this.combosEnabled()) this.showPane('custom');
        }
        moved = true;
        // The group drives the price variant, so it has to move with the colour.
        if (s.cat) {
          this.state.cat[i] = s.cat;
          const sel = this.qa('.scs-cat')[i]; if (sel) sel.value = s.cat;
        }
        this.palTarget = i;
        this.pickSwatch(code);
      });

      // Deliberately last: it opens an overlay, so anything set above is already applied
      // underneath and is still there when the buyer closes the gallery.
      // 2026-10-06 (cliente): para mandar los colores por WhatsApp de motos que aun no estan en la web,
      // ?galeria=1 (o ?ver= sin un color valido) abre la GRILLA completa de colores directo al cargar.
      // ?ver=CODE sigue abriendo la galeria entrando en ese color puntual, como antes.
      const ver = norm('ver');
      const galeria = String(p.get('galeria') || '').trim();
      if (this.galleryEnabled()) {
        if (galeria) {
          this.openGallery(null);                 // grilla completa de colores
        } else if (ver && this.swatch(ver)) {
          this.openGallery(null);
          this.openGalleryDetail(ver);            // directo en ese color (como antes; un codigo invalido no abre nada)
        }
      }
    }

    /* ---------- native variant integration (Opción + Color are variants) ---------- */
    initNative() {
      this.applyNativeState();
      // Every variant change dispatches a bubbling `change` on input[name="id"]
      // (product-info.js#updateVariantInputs); that is our reliable, theme-version
      // agnostic signal. Also subscribe to the pub/sub event when it is available.
      this._onIdChange = (ev) => {
        const t = ev.target;
        if (!t) return;
        // React to the variant id changing AND to native option (Opción/Color) picks,
        // so we detect "Color Personalizado" even before a variant resolves.
        if ((t.name === 'id' && this.form && this.form.contains(t)) ||
            (t.closest && t.closest('variant-selects'))) {
          /* This listener is registered in the CAPTURE phase, so it runs before Dawn's own
           * bubble-phase handler reads the form. Repairing the second option HERE means Dawn
           * resolves the correct variant on its first pass and the buyer never sees a wrong
           * frame. Doing it afterwards worked, but painted the stale state first. */
          const v0 = String(t.value || '');
          if (/personaliz/i.test(v0)) {
            this.rememberSimilMedia();          // saliendo de Simil: guardar lo que se ve ahora
          } else if (/s[íi]mil/i.test(v0)) {
            this.ensureSimilVariant(true);
            this.restoreSimilMedia();           // volviendo: reponerla ya, sin esperar el fetch
          }
          this.applyNativeState();
          /* Cliente 2026-09-30: "el scroll automatico al seleccionar un color en simil original hace
           * un error sube y baja". Medido en vivo: al elegir un color en Simil, revealMediaIfNeeded()
           * hacia un scroll suave hasta el tope de la pagina (rama top=0) y despues el re-render tardio
           * de Dawn la volvia a bajar -> el "sube y baja". La foto igual se cambia sola en su lugar
           * (Dawn cambia la imagen de la variante y el scroll de Dawn ya esta suprimido), asi que ese
           * auto-scroll no aporta y molesta. Se saca en Simil. La revelacion en Colores disponibles
           * (al elegir una combinacion) NO se toca: esa es otra interaccion y va aparte. */
        }
      };
      document.addEventListener('change', this._onIdChange, true);
      try {
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
          this._unsub = subscribe(PUB_SUB_EVENTS.variantChange, () => this.applyNativeState());
        }
      } catch (e) { /* pub/sub not present; the DOM listener already covers it */ }
      this.watchMediaHost();
    }
    /* Client report 2026-08-20: on a native-variant product ("Colores disponibles"),
     * picking a combo can (a) leave the site's own top nav hidden/unreachable while
     * scrolling, and (b) jump the whole page back up to the photo, which can look like
     * proof the model changed color when nothing did. Both trace to the SAME Dawn
     * call: our native variant change reaches Dawn's product-info.js, which calls
     * media-gallery's setActiveMedia() to switch to the new variant's image; that
     * method unconditionally calls preventStickyHeader() (sets <sticky-header>.
     * preventReveal = true) and, in its own deferred callback, does a smooth
     * window.scrollTo() back to the photo if it has scrolled out of view.
     *
     * The first version of this fix hung both corrections off _onIdChange via a bare
     * setTimeout(fn, 0), timed to run after Dawn's SAME-EVENT bubble-phase handler.
     * That assumption was wrong: product-info.js's variant->media flow goes through
     * handleOptionValueChange() -> renderProductInfo(), which does a real fetch() for
     * the updated variant HTML - setActiveMedia() only runs once that network request
     * resolves, an unpredictable delay later, not within the same tick or even the
     * same macrotask as the original event. A setTimeout(0) fired and "expired" long
     * before Dawn's actual call ever happened, so it did nothing in a real browser
     * (this was only ever exercised against a same-tick synthetic simulation in
     * tests, not the real async Dawn code - the offline test proved the RACE LOGIC
     * worked, not that the trigger timing matched reality).
     *
     * Fixed by hooking into watchMediaHost()'s MutationObserver instead (below),
     * which already exists for exactly this reason - client report 2026-08-19's
     * combo-shot bug taught the same lesson: don't guess when Dawn's async DOM
     * update happens, watch for the mutation itself and react to it, however long it
     * takes. That observer callback is a microtask, so it always runs AFTER Dawn's
     * synchronous mutation + preventStickyHeader() call but BEFORE Dawn's own
     * deferred scrollTo (a macrotask, scheduled after the mutation) - exactly the
     * ordering these two fixes need. */
    releaseStickyHeader() {
      const sh = document.querySelector('sticky-header');
      if (sh) sh.preventReveal = false;
    }
    // Patches window.scrollTo to a no-op just long enough for Dawn's already-queued
    // deferred scrollTo call to run as a no-op, then restores it. Called from the
    // MutationObserver callback (a microtask), so the patch is in place before
    // Dawn's setTimeout(0)-scheduled scroll call (a macrotask) can run, and the
    // single setTimeout(0) restore below - scheduled AFTER Dawn's call is already
    // queued - lands after it, not before. Never touches the OTHER scrollTo in the
    // same Dawn function (the thumbnail strip's own horizontal scroll, a contained,
    // genuinely useful scroll that was never part of the complaint).
    /* Client 2026-08-21: "era retirarlo solo en 'Fabricacion Personalizada' en Simil original
     * y Colores disponibles si debe estar habilitado porque ahora quedo deshabilitado en todo.
     * Y esos si es necesario que muestre el cambio de lo seleccionado."
     *
     * The original complaint was never "the page scrolls", it was "the page scrolls to a photo
     * that did not change", which reads as proof the model changed colour when it did not. That
     * only happens while building a custom pick, where the image is fixed. On Simil Original and
     * on Colores disponibles the image DOES swap with the choice, so the jump is the feedback he
     * wants - suppressing it there was over-reach on our side, not what he asked for. */
    shouldSuppressMediaScroll() {
      /* Always. Dawn's scroll is now never the one that moves the page: it fires only when the
       * pick happens to cross into another price variant, it waits for its own fetch to resolve
       * (the lag the client described on Simil Original), and it lands the photo underneath the
       * sticky header. revealMediaIfNeeded() replaces it on the two interactions that should
       * move - a combination pick and a colour pick inside Simil Original - and stays out of the
       * way on the custom build tab, where the image does not change at all. */
      return true;
    }
    suppressMediaScroll() {
      const real = window.scrollTo;
      window.scrollTo = () => {};
      setTimeout(() => { window.scrollTo = real; }, 0);
    }
    /* Self-healing backstop for the combo photo overlay (client report 2026-08-19: the
     * combination photo never shows on a native-variant product, only the small strip
     * below it does). Root cause: our own re-seat call in applyNativeState() is wired to
     * a document-level `change` listener registered in the CAPTURE phase (see initNative,
     * a few lines up), so on a native variant swap we run and re-insert the shot BEFORE
     * Dawn's own variant-selects/media-gallery bubble-phase handlers have swapped the
     * active slide. Our shot lands on the slide that is about to become inactive, Dawn's
     * handler then runs and hides it, and nothing calls placeComboShot() again after that
     * point since no further `change` event fires.
     *
     * Reordering the existing listener risks the other things applyNativeState() does
     * (revealing the custom palette, hiding tier options) needing that early capture
     * timing for their own reasons untested here. Instead this watches the media area
     * directly and re-asserts the shot after ANY mutation, which is correct regardless
     * of exactly when or how Dawn swaps the slide - it does not need to know why the DOM
     * changed, only that it did. placeComboShot() is already idempotent (bails out if the
     * shot is already correctly seated), so this cannot loop against its own insertion. */
    watchMediaHost() {
      const g = document.querySelector('media-gallery') || document.body;
      if (!g || typeof MutationObserver === 'undefined') return;
      this._mediaObserver = new MutationObserver(() => {
        if (this._shotUrl && this.isCustomSelected()) this.placeComboShot();
        // Runs on every real Dawn media swap this observer catches, not just when a
        // combo shot is active - see releaseStickyHeader()/suppressMediaScroll() for
        // why this specific callback (not _onIdChange) is the correct trigger point.
        this.releaseStickyHeader();
        if (this.shouldSuppressMediaScroll()) this.suppressMediaScroll();
        // Dawn's re-render is what interrupts our scroll, so finish the job right where it lands.
        if (this._revealUntil && Date.now() < this._revealUntil) this.revealMediaIfNeeded(true);
      });
      // Both childList (Dawn replacing/re-creating slide markup) and the `class`
      // attribute (Dawn instead just toggling which slide carries `is-active`) are
      // real ways a theme can swap the active slide - watch both rather than assume.
      this._mediaObserver.observe(g, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    }

    currentVariantId() {
      const inp = this.form && this.form.querySelector('[name="id"]');
      return inp ? String(inp.value || '') : '';
    }

    // "Custom" is true if the selected Opción value says so (works even before a
    // variant resolves) or the resolved variant is a known custom one.
    isCustomSelected() {
      /* The "Opción" control is the source of truth whenever it actually says something.
       *
       * This used to return true for "Personalizado" and otherwise FALL THROUGH to the variant-id
       * check. That looked harmless and caused a visible flash on every page load: at first paint
       * the form still carries a price-tier variant id (e.g. "Comunes" $1.200) even though the
       * SÍMIL radio is the one checked, so the fall-through reported custom, the combination photo
       * and strip painted under Símil Original, and only when Dawn finished syncing the variant did
       * it correct itself. Reported 2026-08-21 with before/after screenshots of a hard refresh.
       * So: if the option group tells us which side we are on, trust it and stop. Only fall back to
       * the variant id when there is no option group at all, which is the case on the products that
       * are custom-only (no Símil variants) - there customIds is the only signal available. */
      const vs = document.querySelector('variant-selects');
      if (vs) {
        let sawOption = false, custom = false;
        vs.querySelectorAll('input[type=radio]:checked, select').forEach(el => {
          const v = String(el.value || '');
          if (/personaliz/i.test(v)) { sawOption = true; custom = true; }
          else if (/s[íi]mil/i.test(v)) { sawOption = true; }
        });
        if (sawOption) return custom;
      }
      return this.customIds.has(this.currentVariantId());
    }

    /* R5-3, cliente 2026-08-26 (adjuntos/2026-08-26-r5-03-*.png): en un kit que no lleva Simil
     * Original queda un solo boton "Color Personalizado" que no hace nada al tocarlo, con su
     * etiqueta "Opcion" arriba. Sus palabras: "no es necesario el boton que no cumple funcion".
     *
     * Se decide por la CANTIDAD DE VALORES, no por un ajuste nuevo ni por el tipo de producto.
     * Un selector con una sola opcion no es una eleccion, es un cartel. Y asi se cumple sola la
     * otra mitad de su mensaje: el dia que cargue unas llantas que la moto SI trae de fabrica, ese
     * producto tendra dos valores y el selector vuelve a aparecer sin que el toque nada.
     *
     * Se oculta el grupo entero (con su etiqueta), no el boton: dejar la etiqueta "Opcion" colgada
     * sobre la nada seria peor que ahora. Los radios siguen en el DOM, igual que en
     * hideTierOptions(), porque isCustomSelected() los lee para saber en que estado esta. */
    hideLoneOpcion() {
      const vs = document.querySelector('variant-selects'); if (!vs) return;
      vs.querySelectorAll('fieldset, .product-form__input').forEach(g => {
        const campos = Array.from(g.querySelectorAll('input[type=radio], option'));
        if (!campos.some(el => /personaliz/i.test(el.value || ''))) return;   // no es el de Opcion
        const visibles = campos.filter(el => {
          if (el.hidden || el.disabled) return false;
          const lbl = (el.id && vs.querySelector('label[for="' + el.id + '"]')) || el.closest('label');
          if (el.style.display === 'none' || (lbl && lbl.style.display === 'none')) return false;
          return true;
        });
        g.style.display = visibles.length <= 1 ? 'none' : '';
      });
    }

    applyNativeState() {
      this.hideTierOptions(); // Dawn re-renders the picker on every change; re-hide tiers
      this.hideLoneOpcion();  // ...y por lo mismo hay que volver a evaluar el grupo de Opcion
      const custom = this.isCustomSelected();
      this.state.option = custom ? 'custom' : 'simil';
      const note = this.placeSimilNote();
      if (note) note.hidden = custom;
      // Once the note has been relocated into Shopify's markup it is no longer ours to
      // keep on screen, so the host can go back to hiding completely under Símil. If
      // relocation failed the note is still inside us, and then the host must stay.
      // The lead time is true under Símil as well, and collapsing the host would hide
      // it from exactly the buyer most likely to want it (the adult buying the repuesto).
      const keepVisible = !!this.q('.scs-simil-note') || !!this.q('.scs-lead') || !!this.q('.scs-fit');
      this.style.display = (custom || keepVisible) ? '' : 'none';
      this.toggleSimilColorPicker(custom); // client: hide the Simil color list under custom
      const cut = this.q('.scs-cut'); if (cut) cut.hidden = !custom;
      const pr = this.q('.scs-printed'); if (pr) pr.hidden = !custom;
      // These were hidden implicitly by display:none on the host before.
      const det = this.q('.scs-det-field'); if (det) det.hidden = !custom;
      this.placeLeadAndNote(custom);
      if (!custom) this.showWarn(false);
      // A variant change can make the theme switch the active media slide. Re-seat the
      // combination photo so it follows, instead of being left on a hidden slide.
      // The combination photo AND its thumbnail strip both live in the shared product
      // gallery, so both have to follow the Opcion, not just the variant. Removing only
      // the photo left the strip and its "Imagenes de esa combinacion elegida" caption
      // sitting under the Simil kit, which is the "mezclado" the client reported.
      if (custom) {
        // Arriving from Símil, selectCombo() deliberately skipped the media work, so
        // _shotUrl can still be empty even though a combo is selected. Seed it from the
        // combo here, otherwise the strip comes back on its own and the big photo never
        // does. (Caught by the live run: strip true, shot false.)
        if (!this._shotUrl && this.combo) {
          this.swapProductImage(this.combo.imagen || (this.combo.fotos || [])[0]);
        } else if (this._shotUrl) {
          this.placeComboShot();
        }
        if (this.combo) this.placeComboStrip(this.combo);
      } else {
        this.removeComboShot();
        this.removeComboStrip();
      }
      /* T3: last, on purpose. Everything above has already been applied, so when the click below
       * makes Dawn fire another change and this method runs again, the second pass is a no-op
       * apart from the media Dawn swaps. Guarded by _fixingSimil so the two passes cannot loop. */
      if (!custom) this.ensureSimilVariant();
      this.recalc();
    }

    /* Client spec page 1: the Símil helper paragraph must sit BETWEEN the "Opción"
     * buttons and the "Color de la moto" list. Both are Shopify's own markup and render
     * ABOVE this whole block, so the note has to be moved into them.
     *
     * It is re-placed rather than moved once, because Dawn re-renders variant-selects on
     * every variant change and destroys anything we put inside it. Hence the recreate
     * path, and hence being called from applyNativeState(), which already re-runs on
     * every change for exactly the same reason (see hideTierOptions).
     *
     * Returns the note wherever it ended up, or null if there is no note configured, so
     * the caller can toggle it without caring which branch ran. */
    placeSimilNote() {
      const txt = (this.cfg.notes || {}).simil;
      if (!txt || !this.native) return this.q('.scs-simil-note');
      const vs = document.querySelector('variant-selects');
      if (!vs) return this.q('.scs-simil-note');
      // The Opción group is the one offering a "...personalizado/s" value.
      let opcion = null;
      vs.querySelectorAll('fieldset, .product-form__input').forEach(g => {
        if (opcion) return;
        if (Array.from(g.querySelectorAll('input, option')).some(el => /personaliz/i.test(el.value || ''))) opcion = g;
      });
      // Only one option group (no Color list) means there is nothing to sit between;
      // leave the note where it is rather than moving it somewhere arbitrary.
      if (!opcion || !opcion.nextElementSibling) return this.q('.scs-simil-note');
      let note = vs.querySelector('.scs-simil-note') || this.q('.scs-simil-note');
      if (!note) { note = this.mk('div', 'scs-simil-note'); note.textContent = txt; note.hidden = true; }
      if (note.previousElementSibling !== opcion) opcion.insertAdjacentElement('afterend', note);
      return note;
    }

    // Client request: when "Color Personalizado" is active, hide the native
    // "Simil Original" color options (the moto schemes) since the palette replaces
    // them. We keep the Opción toggle so the buyer can switch back.
    toggleSimilColorPicker(custom) {
      const vs = document.querySelector('variant-selects'); if (!vs) return;
      const grupos = Array.from(vs.querySelectorAll('fieldset, .product-form__input'));
      const esOpcion = g => Array.from(g.querySelectorAll('input, option')).some(el => /personaliz/i.test(el.value || ''));
      /* Cliente 2026-08-31, sticker de Yamaha: antes se escondia TODO grupo que no fuera Opcion.
       * En un kit eso da justo la lista de colores Simil, que es lo que se queria tapar, porque no
       * hay ningun otro eje. Pero el sticker tiene ademas Tamaño, que lo elige el comprador, y
       * quedaba escondido tambien: la ficha aparecia sin los botones de medida.
       * El grupo de color es el ULTIMO, el mismo criterio con el que se arma la clave de precio.
       * Cualquier otro eje se deja como esta. Para un producto de dos opciones esto es identico a
       * lo de antes, porque ahi el unico grupo que no es Opcion ya era el ultimo. */
      const color = grupos.filter(g => !esOpcion(g)).pop();
      grupos.forEach(g => {
        if (esOpcion(g)) return;                       // ese lo maneja hideLoneOpcion
        g.style.display = (g === color && custom) ? 'none' : '';
      });
    }

    // The pre-priced tier variants live on the "Color" option; the buyer must never
    // see those numeric entries in the native picker (the palette drives them).
    /* T3, client 2026-08-21: "cuando estas en Colores personalizados y pasas a 'Simil Original'
     * sigue con la otra imagen. Estaria bueno que ya quede predeterminado una opcion asignada a
     * 'Simil original' para que ya muestre ejemplo 'Moto Blanca', entonces al navegar entre las 3
     * cambia cada imagen junto con el contenido."
     *
     * ROOT CAUSE, measured on the live store before touching anything: the SECOND option keeps
     * whatever the custom flow left in it, which is a colour CATEGORY ("Especiales 2"), and
     * "Simil Original + Especiales 2" is not a real variant. Shopify cannot resolve it, so it
     * leaves the previous photo on screen and the button falls back to "No disponible" - exactly
     * his screenshot r4-01. The Crypton did NOT show the bug only because its second option
     * happened to be holding a real bike colour ("Moto Negra con Rojo").
     *
     * So: whenever Simil Original is the chosen Opcion, the second option must hold a real Simil
     * value. Clicking the control (rather than assigning the variant id ourselves) keeps Dawn in
     * charge of resolving the variant and swapping the media, which is what makes the photo and
     * the content move together the way he asked. */
    similValueRadios(vs) {
      const hide = this.cfg.hideOptionValues || [];
      const radios = Array.from(vs.querySelectorAll('input[type=radio]'));
      const groups = {};
      radios.forEach(r => { (groups[r.name] = groups[r.name] || []).push(r); });
      // The tier group is the one carrying the category values; anything else in it is a Simil colour.
      const tier = Object.keys(groups).map(k => groups[k])
        .find(g => g.some(r => hide.indexOf(r.value) !== -1));
      return { tier: tier || null, hide: hide };
    }
    /* Client 2026-08-22, tras el arreglo de T3: "there is still glance". Al volver a Símil se ve un
     * instante la foto del diseño personalizado antes de que llegue la de la moto. No es nuestro
     * codigo: es Shopify, que deja el render anterior en pantalla mientras busca la seccion de la
     * variante nueva. Medido: ~200ms aca, mas en un celular con red lenta, que es lo que le da
     * tiempo a sacarle la foto.
     *
     * No hace falta esperar esa ida y vuelta para la imagen: la foto de Simil ya estuvo en pantalla
     * hace un momento, o sea que esta en cache. Se guarda al salir de Simil y se repone al volver,
     * en la fase de captura. Cuando el fetch de Dawn aterriza pone exactamente la misma URL, asi
     * que no hay segundo cambio ni parpadeo. */
    similMediaNode() {
      return document.querySelector('.product__media img') || document.querySelector('media-gallery img');
    }
    /* T6: el plazo arriba en Simil Original, abajo (junto al precio) en Colores Personalizados,
     * y la nota editable ocupa el lugar que dejo. Idempotente: se llama en cada applyNativeState()
     * y solo mueve el nodo si no esta ya donde corresponde. */
    placeLeadAndNote(custom) {
      const lead = this.q('.scs-lead');
      const note = this.q('.scs-cnote');
      /* R5-3, cliente 2026-08-26: "ni la explicacion de Color disponible ni Elegir mis colores".
       * Ese parrafo explica LAS DOS PESTAÑAS, su propia ayuda en el panel lo dice ("arriba de las
       * dos pestañas"). En un kit sin combinaciones cargadas no hay pestañas, asi que describe algo
       * que no existe. Se ata a combosEnabled(), que es lo mismo que decide si las pestañas se
       * dibujan, y no a un ajuste nuevo: asi no hay nada que el se pueda olvidar de tildar. */
      if (note) note.hidden = !custom || !this.combosEnabled();
      if (!lead || !this.body) return;
      const fit = this.q('.scs-fit');
      const wantBefore = custom ? (fit || this.q('.scs-warn')) : this.body.firstChild;
      if (custom) {
        if (wantBefore && lead.nextSibling !== wantBefore) this.body.insertBefore(lead, wantBefore);
        else if (!wantBefore && this.body.lastChild !== lead) this.body.appendChild(lead);
      } else if (this.body.firstChild !== lead) {
        this.body.insertBefore(lead, this.body.firstChild);
      }
    }
    similPriceNode() {
      return document.querySelector('[id^="price-"]') ||
             document.querySelector('.product__info-container .price') ||
             document.querySelector('.price');
    }
    rememberSimilMedia() {
      const im = this.similMediaNode();
      if (im) this._similImgSrc = im.currentSrc || im.src || '';
      /* El precio sufre lo mismo que la foto: hasta que no llega la seccion de la variante nueva,
       * en pantalla sigue el precio del diseño personalizado. Cliente 2026-08-22: "there is still
       * glance of that cross text and price". Se guarda el de Simil y se repone al volver. */
      const pr = this.similPriceNode();
      if (pr) this._similPriceHtml = pr.innerHTML;
    }
    restoreSimilMedia() {
      let did = false;
      if (this._similImgSrc) {
        const im = this.similMediaNode();
        if (im && (im.currentSrc || im.src || '') !== this._similImgSrc) {
          // srcset gana sobre src, asi que hay que soltarlo o el navegador vuelve a elegir la vieja.
          im.removeAttribute('srcset');
          im.src = this._similImgSrc;
          did = true;
        }
      }
      if (this._similPriceHtml) {
        const pr = this.similPriceNode();
        if (pr && pr.innerHTML !== this._similPriceHtml) { pr.innerHTML = this._similPriceHtml; did = true; }
      }
      /* Y el tachado. Mientras Dawn no termina, marca como agotados los colores de moto usando el
       * estado anterior, asi que se ven los tres tachados por unos 200ms y parece que no hay nada
       * disponible. Cuando llega el HTML del servidor no quedan tachados, o sea que el estado en
       * reposo ya dice lo contrario: suprimirlo durante la transicion no esconde nada, lo hace
       * coherente. La clase se saca sola enseguida. */
      document.documentElement.classList.add('scs-simil-settling');
      this.releaseSettleWhenReal();
      return did;
    }
    /* Regresion medida el 2026-08-22: soltar el tachado por reloj fijo pierde la carrera cuando la
     * red va lenta. En una corrida, Dawn re-renderizo a los 1513ms, 113ms DESPUES de vencido el
     * plazo de 1400, y el destello volvio (frames 1513 y 1529 del XR y del Wave). Un plazo mas
     * largo solo mueve el problema, y ademas taparia mas tiempo un producto que SI esta agotado.
     *
     * Lo correcto no es esperar un rato sino esperar el hecho: el destello existe unicamente
     * mientras no llego el HTML de la variante nueva. Se suelta con lo que pase primero:
     *   1. Dawn reemplaza el contenido de `variant-selects` (llego la verdad, sea cual sea).
     *   2. Ya no queda ningun color tachado.
     *   3. Tope de 2500ms, por si algo falla y no hay que dejar la clase puesta.
     * El punto 2 se mide sobre el estado de Dawn (el radio `disabled`), no sobre el `line-through`
     * pintado, porque nuestra propia clase lo anula: mirar el pintado daria siempre "limpio" y
     * soltaria en el primer frame, que es volver al destello. */
    releaseSettleWhenReal() {
      const done = () => {
        if (this._settleObs) { this._settleObs.disconnect(); this._settleObs = null; }
        cancelAnimationFrame(this._settleRaf);
        clearTimeout(this._settleT);
        document.documentElement.classList.remove('scs-simil-settling');
      };
      if (this._settleObs) { this._settleObs.disconnect(); this._settleObs = null; }
      cancelAnimationFrame(this._settleRaf);
      clearTimeout(this._settleT);

      const vs = document.querySelector('variant-selects');
      if (!vs) { document.documentElement.classList.remove('scs-simil-settling'); return; }

      this._settleObs = new MutationObserver(muts => {
        if (muts.some(m => m.type === 'childList' && (m.addedNodes.length || m.removedNodes.length))) done();
      });
      this._settleObs.observe(vs, { childList: true, subtree: true });

      const struckCount = () => {
        let n = 0;
        vs.querySelectorAll('input[type="radio"]').forEach(r => {
          const lab = r.nextElementSibling;
          if (!lab || lab.tagName !== 'LABEL') return;
          if (!lab.offsetWidth && !lab.offsetHeight) return;          // solo lo que se ve
          if (r.disabled || r.classList.contains('disabled')) n++;
        });
        return n;
      };
      const step = () => {
        if (!document.documentElement.classList.contains('scs-simil-settling')) return;
        if (struckCount() === 0) { done(); return; }
        this._settleRaf = requestAnimationFrame(step);
      };
      this._settleRaf = requestAnimationFrame(step);
      this._settleT = setTimeout(done, 2500);
    }
    ensureSimilVariant(silent) {
      if (this._fixingSimil) return false;
      const hide = this.cfg.hideOptionValues || [];
      if (!hide.length) return false;
      const vs = document.querySelector('variant-selects');
      if (!vs) return false;

      const { tier } = this.similValueRadios(vs);
      if (tier) {
        const cur = tier.find(r => r.checked);
        if (cur && hide.indexOf(cur.value) === -1) return false;      // already a real Simil colour
        const pick = tier.find(r => hide.indexOf(r.value) === -1 && !r.disabled)
                  || tier.find(r => hide.indexOf(r.value) === -1);
        if (!pick || pick.checked) return false;
        this._fixingSimil = true;
        /* SILENT is the path that matters. Called from the capture-phase listener, it flips the
         * radio BEFORE Dawn's own handler reads the form, so Dawn resolves the right variant on
         * its first and only pass. Client 2026-08-22: with the loud path the buyer saw the wrong
         * frame first - the custom diagram with every Moto option struck through - and only then
         * the correct photo, because that was two renders instead of one. */
        if (silent) {
          pick.checked = true;
        } else {
          /* 2026-10-01: antes se hacia (lbl||pick).click(). Pero .click() sobre un radio lo ENFOCA, y si
           * ese radio cae debajo del pliegue el navegador scrollea la pagina HASTA el (focus-scroll).
           * Al entrar, en productos con "Simil Original" cuyo variante por defecto cae en una categoria
           * oculta (hideOptionValues), este corrector elige el primer color real y el .click() dejaba la
           * ficha "entrando mas abajo" (cliente 2026-10-01: Wave S 2021/2026, XR 125L, Sapucai, Baccio).
           * Se setea el valor SIN click, igual que hacia el fallback de abajo: checked + change deja al
           * comprador en la variante correcta con la foto correcta y NO mueve el scroll. Un radio
           * deshabilitado (todo Simil agotado) igual queda seteado; el boton sigue no-disponible por stock. */
          pick.checked = true;
          pick.dispatchEvent(new Event('change', { bubbles: true }));
        }
        setTimeout(() => { this._fixingSimil = false; }, 900);
        return true;
      }

      // Same rule for a <select>-based picker, which some Dawn versions render instead of radios.
      const sel = Array.from(vs.querySelectorAll('select'))
        .find(s => Array.from(s.options).some(o => hide.indexOf(o.value) !== -1));
      if (!sel) return false;
      if (hide.indexOf(sel.value) === -1) return false;
      const opt = Array.from(sel.options).find(o => hide.indexOf(o.value) === -1 && !o.disabled)
               || Array.from(sel.options).find(o => hide.indexOf(o.value) === -1);
      if (!opt || sel.value === opt.value) return false;
      this._fixingSimil = true;
      sel.value = opt.value;
      if (!silent) sel.dispatchEvent(new Event('change', { bubbles: true }));
      setTimeout(() => { this._fixingSimil = false; }, 900);
      return true;
    }
    /* 2026-10-01: Dawn (product-info.js) hace radio.focus() despues de re-renderizar por un change.
     * En la carga, las correcciones de variante (ensureSimilVariant para el default de categoria oculta,
     * o setNativeOptionValue al sembrar una combinacion) disparan ese re-render, y el focus cae en un
     * radio nativo que vive debajo del pliegue: el navegador scrollea la pagina hasta el y la ficha
     * "aparece mas abajo" (cliente 2026-10-01: Wave S 2021/2026, XR 125L, Sapucai, Baccio Px 125 FX).
     * No controlamos el focus() de Dawn, asi que envolvemos HTMLElement.focus UNA sola vez para que,
     * dentro de una ventana corta tras la carga, use {preventScroll:true}: el radio igual se enfoca
     * (accesibilidad intacta), solo no se scrollea. Fuera de la ventana el wrapper es transparente, asi
     * que la navegacion por teclado y los cambios que hace el comprador despues no cambian en nada. Si
     * quien llama pide preventScroll explicito, se respeta. */
    noScrollFocusWindow(ms) {
      const W = (window.__scsNoFocusScroll = window.__scsNoFocusScroll || { until: 0, patched: false });
      W.until = Date.now() + (ms || 3000);
      if (W.patched) return;
      W.patched = true;
      const real = HTMLElement.prototype.focus;
      HTMLElement.prototype.focus = function (opts) {
        if (Date.now() < W.until) {
          return real.call(this, Object.assign({ preventScroll: true }, (opts && typeof opts === 'object') ? opts : null));
        }
        return real.apply(this, arguments);
      };
    }
    /* 2026-10-01: al entrar, la correccion de variante de la carga dispara un re-render de Dawn que,
     * mientras baja el HTML de la variante, hace PARPADEAR el precio con un valor intermedio equivocado
     * (medido en vivo wave-s-2021: el real $1.100 saltaba a $1.550 y volvia; wave-s-2026 igual con $1.300).
     * El precio correcto YA viene del server y NO lo cambia la correccion (esta solo cambia el COLOR, no
     * el precio: el precio va por tamanio/tier, no por color). Asi que en vez de ocultar el precio (lo que
     * daba blanco/vanish y, si se revelaba tarde, hasta mostraba el $1.550), se CONGELA: se guarda el HTML
     * correcto del server y, si el re-render mete un valor distinto, se restaura al toque (el observer corre
     * como microtask ANTES del paint, asi el $1.550 nunca se ve). El precio se ve correcto desde el primer
     * instante, sin blanco, sin flash. No toca el carrito: el id de variante lo sigue poniendo el re-render
     * de Dawn, aca solo se protege el TEXTO del precio. Se vigila ~4s (lo que tarda el re-render) y listo. */
    holdPriceDuringLoad() {
      const info = document.querySelector('.product__info-container');
      if (!info || typeof MutationObserver === 'undefined') return;
      // OJO: hay que vigilar el CONTENEDOR del precio (#price-<section>, el div role=status), NO `.price`.
      // Medido en vivo: durante el re-render Dawn deja en ese contenedor el texto intermedio equivocado
      // ($1.550) SIN un elemento `.price` adentro, asi que mirar solo `.price` no lo agarra (por eso el
      // cliente seguia viendo el $1.550). Se guarda el HTML correcto del server y, si el contenedor muestra
      // cualquier cosa distinta (el $1.550, o vacio mientras carga), se restaura antes del paint.
      const wrap0 = (() => { const p = info.querySelector('.price'); return p ? (p.closest('[id^="price-"]') || p.parentNode) : info.querySelector('[id^="price-"]'); })();
      if (!wrap0) return;
      const norm = (s) => (s || '').replace(/\s+/g, ' ').trim();
      const serverText = norm(wrap0.textContent);
      const serverHTML = wrap0.innerHTML;
      if (!serverText) return;   // sin precio del server no hay nada que proteger
      let obs = null, restoring = false;
      obs = new MutationObserver(() => {
        if (restoring) return;                 // no reaccionar a nuestra propia restauracion
        const w = info.querySelector('[id^="price-"]');
        if (!w) return;
        if (norm(w.textContent) === serverText) return;   // ya es el correcto: nada que hacer
        restoring = true;
        try { w.innerHTML = serverHTML; } catch (e) {}
        restoring = false;
      });
      obs.observe(info, { childList: true, subtree: true, characterData: true });
      setTimeout(() => { try { obs.disconnect(); } catch (e) {} }, 4000);  // tras asentarse el re-render
    }
    hideTierOptions() {
      const hide = this.cfg.hideOptionValues || [];
      if (!hide.length) return;
      const vs = document.querySelector('variant-selects'); if (!vs) return;
      vs.querySelectorAll('option').forEach(o => { if (hide.indexOf(o.value) !== -1) { o.hidden = true; o.disabled = true; } });
      vs.querySelectorAll('input[type=radio]').forEach(i => {
        if (hide.indexOf(i.value) === -1) return;
        const lbl = (i.id && vs.querySelector('label[for="' + i.id + '"]')) || i.closest('label');
        i.style.display = 'none'; if (lbl) lbl.style.display = 'none';
      });
    }

    cutRow(i) {
      const e = this.esc;
      const cats = (this.cfg.cut.categories || []).map(c => `<option value="${e(c.id)}">${e(c.name)}</option>`).join('');
      const word = e((this.cfg.labels && this.cfg.labels.colorWord) ?? 'Color');
      // Layout (client mockup): controls on the left; a LARGE round color preview
      // on the side, one per selector. Tapping the circle enlarges it (openZoom).
      // The preview stays hidden until a color is chosen.
      const single = !this.secondActive();
      return `<div class="scs-color-row" data-idx="${i}">
        <div class="scs-lbl">${word}${single ? '' : ' ' + (i + 1)}:</div>
        <div class="scs-color-grid">
          <div class="scs-color-controls">
            <select class="scs-select scs-cat" data-i="${i}">${cats}</select>
            <div class="scs-swatch-field" data-i="${i}"><span class="scs-cf-text">${e(this.cfg.chooseLabel ?? 'Elegí un color')}</span></div>
          </div>
          <div class="scs-preview" data-prev="${i}">
            <button type="button" class="scs-big" data-big="${i}" aria-label="Ampliar color"></button>
            <span class="scs-oos-flag">Sin Stock</span>
          </div>
        </div>
      </div>`;
    }

    bind() {
      const op = this.q('.scs-opcion'); if (op) op.addEventListener('change', ev => { this.state.option = ev.target.value; this.onOptionChange(); });
      const sim = this.q('.scs-simil-sel'); if (sim) sim.addEventListener('change', () => this.recalc());
      this.qa('.scs-cat').forEach(sel => sel.addEventListener('change', ev => this.onCatChange(+ev.target.dataset.i)));
      this.qa('.scs-swatch-field').forEach(f => f.addEventListener('click', () => this.openPalette(+f.dataset.i)));
      this.qa('.scs-big').forEach(b => b.addEventListener('click', () => this.openZoom(+b.dataset.big)));
      this.qa('.scs-dot').forEach(dot => dot.addEventListener('click', () => this.openCmyk(+dot.dataset.i)));
      const go = this.q('.scs-gal-open'); if (go) go.addEventListener('click', () => this.openGallery(null));
      // Bloque "elegir mis colores" (cliente 2026-09-23): miniaturas al visor a pantalla completa
      // y el video se reproduce en linea al tocarlo.
      const exCap = ((this.cfg.elegir || {}).titulo) || '';
      this.qa('.scs-explica-img').forEach(b => b.addEventListener('click', () => this.openPhotoZoom(b.dataset.g, exCap)));
      const exVid = this.q('.scs-explica-vid'); if (exVid) { const vb = exVid.querySelector('.scs-explica-vbtn'); if (vb) vb.addEventListener('click', () => this.playExplicaVideo(exVid)); }
      this.qa('.scs-subtab').forEach(b => b.addEventListener('click', () => this.showPane(b.dataset.pane)));
      const ds = this.q('.scs-det-sel'); if (ds) ds.addEventListener('change', () => this.recalc());
      const dt = this.q('.scs-det-text'); if (dt) dt.addEventListener('input', () => this.recalc());
      this.qa('.scs-cat').forEach((sel, i) => { if (this.state.cat[i]) sel.value = this.state.cat[i]; });
    }

    onOptionChange() {
      const custom = this.state.option === 'custom';
      const note = this.q('.scs-simil-note'); if (note) note.hidden = custom;
      const sb = this.q('.scs-simil'); if (sb) sb.hidden = custom;
      const cut = this.q('.scs-cut'); if (cut) cut.hidden = !custom;
      const pr = this.q('.scs-printed'); if (pr) pr.hidden = !custom;
      this.recalc();
    }

    onCatChange(i) {
      this.state.cat[i] = this.qa('.scs-cat')[i].value;
      this.state.color[i] = null;
      const fld = this.qa('.scs-swatch-field')[i]; if (fld) fld.querySelector('.scs-cf-text').textContent = this.cfg.chooseLabel ?? 'Elegí un color';
      this.updatePreview(i);
      this.recalc();
    }

    /* ---------- price + variant (display always == charged) ---------- */
    /* Las tres vistas que el comprador distingue, con los nombres que usa el cliente:
     *   'simil'        -> Simil Original
     *   'disponibles'  -> Colores disponibles (la pestaña de combinaciones)
     *   'elegir'       -> Elegir mis colores
     * Sin pestañas (combinaciones apagadas) Colores Personalizados ES "elegir mis colores",
     * que es lo que se ve en pantalla, asi que `pane` indefinido cae ahi y no en un cuarto caso. */
    vistaActual() {
      if (!this.state || this.state.option !== 'custom') return 'simil';
      return this.pane === 'combos' ? 'disponibles' : 'elegir';
    }
    anunciarVista() {
      const v = this.vistaActual();
      if (v === this._vistaAvisada) return;   // no repetir lo mismo
      this._vistaAvisada = v;
      this.dispatchEvent(new CustomEvent('scs:vista', { bubbles: true, detail: { vista: v } }));
    }
    recalc() {
      /* Cliente 2026-09-16, punto 2: el boton de acceso directo tiene que poder mostrarse solo
       * en algunas de las tres pestañas. Ese bloque vive AFUERA de este elemento (es otro bloque
       * de la seccion), asi que la unica forma de que sepa donde esta parado el comprador es que
       * se lo digamos.
       *
       * Se avisa desde el principio de recalc() y no desde updatePrice(), que seria el lugar
       * obvio: en modo nativo recalc() se va por el return de abajo y updatePrice() no llega a
       * correr nunca. Todos los caminos que cambian la vista terminan aca igual
       * (showPane() y applyNativeState() los dos llaman a recalc()).
       *
       * Quien escucha compara contra lo que ya tiene antes de tocar el DOM, asi que llamarlo
       * seguido no cuesta nada. */
      this.anunciarVista();
      // Native mode: Shopify owns the Simil side. For a CUT custom order we drive
      // the price by selecting the pre-priced tier variant that equals base+surcharge.
      if (this.native) { if (this.state.option === 'custom') this.applyNativePricing(); this.syncProps(); this.updateGate(); return; }
      const price = this.applyPricing();
      this.updatePrice(price);
      this.syncProps(price);
      this.updateGate();
    }

    // Cut custom pricing: the buyer's category pick maps to a pre-created variant, and
    // we charge AND show THAT variant's OWN price. So the client controls every price by
    // editing the variant in the normal product editor (no code, no developer), and the
    // shown price is always exactly what is charged (same variant = one source of truth).
    // Key = "Comunes" for a single color, or "Comunes + Especiales 1" (Color 1 category +
    // Color 2 category) when a second color is chosen. The client sets each variant's
    // price to whatever he wants (we pre-fill them base + surcharge as a starting point).
    /* R7-1, cliente 2026-08-28: "me fije que el segundo selector este apagado en Configuracion
     * del tema. Y lo manejo en la ficha del producto con Verdadero / Falso ... en la mayoria no
     * funciona ... Solo lo vi funcionando en Suzuki AX 100".
     *
     * Tenia razon, y el culpable era el parrafo que vivia aca, de R6-3. Esa version miraba la
     * forma del priceMap y, si todas las claves eran simples, apagaba el segundo selector aunque
     * el ajuste dijera que si. Cuando la escribi el ajuste era COMPARTIDO por plantilla y
     * prenderlo para el XR dejaba otros cuatro productos sin poder comprarse, asi que desconfiar
     * del ajuste era lo correcto. Desde entonces el cliente lo apago en Configuracion del tema y
     * lo maneja producto por producto con el metafield: la contaminacion que motivaba esa
     * desconfianza ya no existe y lo unico que quedaba era el efecto colateral, sus cuatro
     * productos de llantas con el campo en Verdadero mostrando un solo selector.
     *
     * Ahora se respeta lo que el pone, sin adivinar. Lo que esa regla protegia (que la clave de
     * precio no exista y el producto quede incomprable) pasa a resolverlo resolvePrice(), que
     * ademas cubre el caso inverso que la regla nunca vio: el Sapucai, con SOLO claves de par y
     * el segundo selector apagado, estaba incomprable desde antes de este reporte. */
    secondActive() {
      const c = this.cfg;
      return !!(c.cut && c.cut.secondSelector);
    }
    priceKey() {
      const c = this.cfg;
      const n0 = this.catName(this.state.cat[0]);
      if (this.secondActive() && this.state.color[1]) return n0 + ' + ' + this.catName(this.state.cat[1]);
      return n0;
    }
    // Cut custom pricing (native mode): the buyer's category combination maps to a
    // pre-priced Shopify variant. We select that variant and DISPLAY its own price, so
    // the shown price is ALWAYS exactly what Shopify will charge (one source of truth =
    // the variant). The client changes any price by editing that variant in the normal
    // product editor (no code, no developer). `_priceResolved` tells the gate whether a
    // real price variant was found for the current combination.
    /* R7-1. La variante que le corresponde a lo que el comprador tiene elegido AHORA.
     *
     * Primero la clave exacta, que es el caso normal y el unico que existia hasta aca. Los dos
     * respaldos son para cuando la ficha del producto y sus variantes no se dicen lo mismo, algo
     * que en la tienda pasa en los dos sentidos:
     *   - dos selectores y variantes precificadas por categoria simple (los cuatro de llantas):
     *     no hay ninguna clave "Comunes + Reflectivos" que buscar.
     *   - un selector y variantes precificadas solo de a pares (el Sapucai): no hay ninguna
     *     clave "Comunes" que buscar.
     * Antes las dos terminaban igual: sin precio, con el candado bloqueando la compra y sin nada
     * que le dijera al comprador ni al cliente por que.
     *
     * Los dos respaldos eligen una variante REAL y muestran SU precio, asi que se sigue
     * cumpliendo la regla de siempre, lo que se ve es exactamente lo que Shopify cobra. Y con dos
     * colores se elige la mas CARA de las dos categorias, nunca la mas barata: si el dato esta
     * incompleto el error tiene que ser a favor del cliente y no en su contra.
     *
     * Un respaldo NO inventa el precio de dos colores, solo evita el producto muerto. Para
     * cobrarlo de verdad tienen que existir las variantes de par, como en el AX 100. */
    /* Los valores que el comprador tiene elegidos AHORA en los selectores nativos que NO son el
     * de la categoria. Al grupo de la categoria se lo reconoce porque es el unico que contiene la
     * clave que estamos por aplicar, asi que no hace falta saber en que posicion quedo. */
    nativeOtherValues(catKey) {
      const vs = document.querySelector('variant-selects'); if (!vs) return [];
      const out = [];
      vs.querySelectorAll('select').forEach(s => {
        const vals = Array.from(s.options).map(o => o.value);
        if (vals.indexOf(catKey) === -1 && s.value) out.push(s.value);
      });
      const grupos = {};
      vs.querySelectorAll('input[type=radio]').forEach(i => { (grupos[i.name] = grupos[i.name] || []).push(i); });
      Object.keys(grupos).forEach(n => {
        const g = grupos[n];
        if (g.some(i => i.value === catKey)) return;   // ese es el de la categoria
        const sel = g.find(i => i.checked);
        if (sel) out.push(sel.value);
      });
      return out;
    }

    resolvePrice() {
      const map = (this.cfg.priceMap || []).filter(p => p && p.variantId);
      if (!map.length) return null;
      const find = k => map.find(p => String(p.optionValue) === k) || null;
      const key = this.priceKey();

      /* Cliente 2026-08-31, sticker de Yamaha: un producto puede tener un eje mas ademas de Opcion
       * y Color, por ejemplo Tamaño, y ese eje TAMBIEN mueve el precio. Ahi la categoria sola no
       * alcanza para encontrar la variante: hay una por cada cruce de tamaño y categoria, y las
       * cinco del mismo color comparten la misma clave. Hay que cruzarla con lo que el comprador
       * tenga elegido en los demas selectores nativos.
       *
       * Pedido textual suyo: "que si pongo un tamaño menos en otro sticker o un tamaño mas que no
       * se rompa". Por eso NO se cuentan los tamaños en ningun lado: se leen del producto, asi que
       * tres, cinco u ocho funcionan igual sin tocar codigo.
       *
       * Si no hay ninguna variante para ese cruce se devuelve null a proposito, en vez de caer en
       * los respaldos de abajo, que fueron pensados para productos de dos ejes y aca elegirian el
       * precio de OTRO tamaño. Sin precio, el candado bloquea la compra, que es lo correcto. */
      const multi = map.filter(p => Array.isArray(p.opts) && p.opts.length > 2);
      if (multi.length) {
        const otros = this.nativeOtherValues(key);
        const cands = multi.filter(p => p.opts.indexOf(key) !== -1 && otros.every(v => p.opts.indexOf(v) !== -1));
        if (!cands.length) return null;
        return { amount: cands[0].amount, variantId: cands[0].variantId, optionValue: key };
      }

      const exacta = find(key);
      if (exacta) return exacta;

      const n0 = this.catName(this.state.cat[0]);
      if (key.indexOf(' + ') !== -1) {
        const n1 = this.catName(this.state.cat[1]);
        const cands = [find(n0), find(n1)].filter(Boolean);
        if (!cands.length) return null;
        return cands.reduce((a, b) => (+b.amount > +a.amount ? b : a));
      }
      const espejo = find(n0 + ' + ' + n0);
      if (espejo) return espejo;
      const pref = n0 + ' + ';
      const pares = map.filter(p => String(p.optionValue).indexOf(pref) === 0);
      if (!pares.length) return null;
      return pares.reduce((a, b) => (+b.amount < +a.amount ? b : a));
    }
    applyNativePricing() {
      this._priceResolved = false;
      if (this.cfg.type !== 'cut') { this._priceResolved = true; return; } // printed custom = flat native variant
      const hit = this.resolvePrice();
      if (!hit) return; // no variant for this combo -> leave the native price; the gate blocks the order
      // Drive the native option so Shopify resolves + charges the variant (keeps the add
      // button enabled); also set the id directly as a backstop.
      this.setNativeOptionValue(String(hit.optionValue));
      const inp = this.form && this.form.querySelector('[name="id"]');
      if (inp && String(inp.value) !== String(hit.variantId)) inp.value = String(hit.variantId);
      this.updatePrice(+hit.amount); // the variant's own price == what is charged
      this._priceResolved = true;
    }
    setNativeOptionValue(value) {
      const vs = document.querySelector('variant-selects'); if (!vs) return;
      let already = false;
      vs.querySelectorAll('input[type=radio]:checked').forEach(i => { if (i.value === value) already = true; });
      vs.querySelectorAll('select').forEach(s => { if (s.value === value) already = true; });
      if (already) return; // guard against a re-render loop
      let done = false;
      vs.querySelectorAll('input[type=radio]').forEach(i => { if (!done && i.value === value) { i.checked = true; i.dispatchEvent(new Event('change', { bubbles: true })); done = true; } });
      if (!done) vs.querySelectorAll('select').forEach(s => { if (!done && Array.from(s.options).some(o => o.value === value)) { s.value = value; s.dispatchEvent(new Event('change', { bubbles: true })); done = true; } });
    }

    applyPricing() {
      const c = this.cfg, opt = this.state.option;
      let base = 0, surcharge = 0, optVariant = null;
      if (c.type === 'cut') {
        if (opt === 'custom') {
          const c0 = this.cat(this.state.cat[0]) || {};
          base = +c0.price || 0; optVariant = c0.variantId || null;
          if (this.secondActive() && this.state.color[1]) { const c1 = this.cat(this.state.cat[1]) || {}; surcharge = +c1.surcharge || 0; }
        } else {
          base = (c.similPrice != null) ? +c.similPrice : (((c.cut.categories || [])[0] || {}).price || 0);
          optVariant = null;
        }
      } else {
        if (opt === 'custom') { base = (c.printed.customPrice != null) ? +c.printed.customPrice : 0; optVariant = null; }
        else { base = (c.similPrice != null) ? +c.similPrice : 0; optVariant = null; }
      }
      const intended = base + surcharge;
      const map = c.priceMap || [];
      const hit = map.find(p => p.variantId && Math.round(+p.amount) === Math.round(intended));
      let variantId, price;
      if (hit) { variantId = hit.variantId; price = intended; }      // surcharge/option price IS charged
      else { variantId = optVariant; price = base; }                 // not charged -> not displayed (no phantom)
      this._mapped = !!variantId;
      if (variantId && this.form) { const inp = this.form.querySelector('[name="id"]'); if (inp) inp.value = variantId; }
      return price;
    }

    updatePrice(total) {
      /* AGREGADO, no cambia nada de lo que ya hacia: deja el total como numero.
       * El bloque "Tu kit" (sf-resumen) lo necesita para sumarle los extras, y parsear el
       * texto del precio seria fragil: el formato de moneda de la tienda es "$1.200,00 UYU"
       * y cualquier cambio de formato romperia la suma en silencio. */
      this.dataset.precio = String(total || 0);
      this.dispatchEvent(new CustomEvent('scs:precio', { bubbles: true, detail: { total: total } }));

      if (!this.cfg.priceTarget) return;
      const el = document.querySelector(this.cfg.priceTarget);
      if (el) el.innerHTML = `<span class="price-item price-item--regular">${this.esc(this.money(total))}</span>`;
    }

    /* R7-3, cliente 2026-08-28: "cambiaria el orden de las cosas", y escribio el orden que quiere:
     *   Opcion / Color 1 / Color 2 / Fondo / Tiempo estimado
     *
     * El orden de las claves de este objeto ES el orden que se ve en el carrito, porque
     * writeInputs() ordena los campos ocultos como vienen aca. Antes no era un orden sino la
     * historia de creacion de esos campos: "Color 2" aparecia debajo de "Fondo" simplemente
     * porque no existia todavia cuando se creo Fondo, y bastaba con elegir el segundo color mas
     * tarde para que el carrito quedara desordenado. Por eso su captura tenia ese orden raro.
     *
     * La fecha y el calce se arman al principio pero se escriben AL FINAL, con `cola()`. Se
     * arman antes porque tienen que viajar tambien en los pedidos de Simil Original, que salen
     * por el return de mas abajo; se escriben al final porque es donde el los quiere leer. */
    syncProps() {
      const out = {}, opt = this.state.option, c = this.cfg, L = c.labels;
      const custom = opt === 'custom';
      // The promised date rides on EVERY order, Símil included, because production and
      // the status message need it whichever option was bought.
      const lead = this.leadProp();
      // Same reasoning: true for every option, so it rides on Símil orders too.
      const fitp = this.fitProp();
      const cola = () => {
        if (fitp) out[fitp.k] = fitp.v;
        if (lead) {
          out[lead.k] = lead.v;
          // R7-4: la misma fecha, ordenable + legible, oculta para el comprador. Ver leadProp().
          if (lead.iso) out['_listo'] = lead.iso + '|' + lead.v;
        }
      };
      // Native + Simil: the Shopify variant already records Opción + Color, so we must
      // not duplicate those as properties.
      if (this.native && !custom) { cola(); this.writeInputs(out); return; }
      if (!this.native) {
        out['Opción'] = custom ? (L.custom ?? 'Colores Personalizados') : (L.similOriginal ?? 'Símil Original');
        if (!custom) { const s = this.q('.scs-simil-sel'); if (s) out['Color'] = s.value; }
      }
      if (custom && c.type === 'cut') {
        const a = this.state.color[0] ? this.swatch(this.state.color[0]) : null;
        if (a) out['Color 1'] = `${a.code} - ${a.name} (${this.catName(this.state.cat[0])})`;
        if (this.secondActive()) { const b = this.state.color[1] ? this.swatch(this.state.color[1]) : null; if (b) out['Color 2'] = `${b.code} - ${b.name} (${this.catName(this.state.cat[1])})`; }
      } else if (custom) {
        const n = Math.max(1, Math.min(4, c.printed.cmykCount || 1));
        for (let i = 0; i < n; i++) {
          const st = this.state.printed[i];
          if (st) {
            const { cmyk, hex } = this.colorFromState(st);
            // Cliente 2026-09-24: si el color viene de un hex EXACTO (tipeado por el, o el default
            // configurado), el pedido lleva ese hex tal cual, no el aproximado de la rueda. El
            // arrastre borra printedSeedHex, asi que ahi vuelve a valer el color del modelo.
            const seed = this.state.printedSeedHex[i];
            const shownHex = (seed || hex).toUpperCase();
            out[`Color ${i + 1}`] = `CMYK ${this.cmykStr(cmyk)} / HEX ${shownHex}`;
          }
        }
      }
      // Two extra fields the client asked for explicitly: WHICH combination this is,
      // and its first photo, so production can enlarge it as an assembly guide. These
      // are additions to the Shopify -> panel data contract.
      if (custom && this.combo) {
        const CB = c.combos || {};
        const ref = this.combo.ref || this.combo.id || this.comboTitle(this.combo);
        if (ref) out[this.clave(CB.refLabel, 'Combinación')] = String(ref);
        const photo = this.combo.imagen || (this.combo.fotos || [])[0];
        if (photo) out[this.clave(CB.photoLabel, 'Foto de la combinación')] = String(photo);
        if (this.combo.detalle) out[this.clave(CB.detalleLabel, 'Detalle de la combinación')] = String(this.combo.detalle);
      }
      const d = c.detalles;
      // Mismo criterio que el resto: ver clave().
      const detKey = this.clave(d.label, 'Detalles');
      if (d.type === 'dropdown') {
        // R7-7: con una sola opcion no se dibuja un menu sino una linea de texto, pero el dato
        // tiene que llegar al pedido exactamente igual que antes.
        const sel = this.q('.scs-det-sel');
        if (sel && sel.value) out[detKey] = sel.value;
        else { const fijo = this.q('.scs-det-fijo'); const t = fijo ? fijo.textContent.trim() : ''; if (t) out[detKey] = t; }
      }
      else if (d.type === 'textarea') { const t = this.q('.scs-det-text'); if (t && t.value.trim()) out[detKey ?? 'Notas'] = t.value.trim(); }
      cola();
      this.writeInputs(out);
    }

    /* R7-3. Shopify guarda las propiedades en el orden en que se envian, o sea en el orden en que
     * estan los campos ocultos en el formulario, y el carrito las muestra en ese mismo orden.
     * Antes un campo se creaba una sola vez y se quedaba donde habia caido, asi que el orden lo
     * decidia el momento en que el comprador eligio cada cosa. Ahora se re-adjuntan en cada
     * pasada siguiendo el orden de `out`: appendChild sobre un nodo que ya es hijo lo MUEVE al
     * final, asi que recorrer `out` en orden deja los campos en ese orden. Son cuatro o cinco
     * nodos ocultos, no hay nada que optimizar, y el valor de un input no se pierde al moverlo. */
    writeInputs(out) {
      Object.keys(this.props).forEach(k => { if (!(k in out)) { this.props[k].remove(); delete this.props[k]; } });
      Object.entries(out).forEach(([k, v]) => {
        let inp = this.props[k];
        if (!inp) { inp = document.createElement('input'); inp.type = 'hidden'; inp.name = `properties[${k}]`; if (this.formId) inp.setAttribute('form', this.formId); this.props[k] = inp; }
        inp.value = v;
        this.appendChild(inp);
      });
    }

    /* ---------- "color required" gate (no order without a color) ---------- */
    installGate() {
      if (!this.form) return;
      this._submitGuard = (ev) => {
        const code = this.blockCode();
        if (code) { ev.preventDefault(); ev.stopImmediatePropagation(); this.showWarn(true, this.blockMsg(code)); }
      };
      this.form.addEventListener('submit', this._submitGuard, true);
    }
    // Any selected color out of stock? (checks Color 1, and Color 2 when enabled)
    hasOosSelected() {
      if (this.cfg.type !== 'cut' || this.state.option !== 'custom') return false;
      const c = this.state.color, second = this.secondActive();
      return !!((c[0] && this.isOos(c[0])) || (second && c[1] && this.isOos(c[1])));
    }
    /* Reason CODE the order is blocked, or '' when it is OK.
     *
     * Deliberately separate from the MESSAGE. Every message below is a merchant-editable
     * theme-editor field, and `isBlocked()` used to be `!!blockReason()` - so clearing any
     * one of those fields made the reason an empty string, which is falsy, which silently
     * turned the gate OFF and let an out-of-stock or incomplete pick straight into the
     * cart. Audit 2026-08-21 (docs/campos-vaciables-auditoria-20260821.md) confirmed all
     * five gate messages shared that hole. The gate must never depend on copy. */
    blockCode() {
      if (this.cfg.type !== 'cut' || this.state.option !== 'custom') return '';
      // On the combinations tab the buyer picks a combo, not two colours, so the
      // prompt has to name the thing they are actually missing.
      if (this.combosEnabled() && this.pane === 'combos' && !this.combo) return 'combo';
      if (!this.state.color[0]) return 'color1';
      // When the 2nd selector is enabled, BOTH colors are required before checkout -
      // but only while building a custom pick on "Fabricación Personalizada". Client
      // verification 2026-08-21 found this blocking every single-colour combo from
      // ever being added to the cart: `secondSelector` is a shared, per-TEMPLATE
      // setting (see comboUnavailable()'s fix, same root cause), not a statement
      // about any one combo's actual colour count. A combo picked from "Colores
      // disponibles" is already complete according to its OWN data the moment it was
      // selected (comboUnavailable() already refused anything short/malformed) - it
      // must not be re-blocked here for a "missing" second colour that combo never
      // had, just because some OTHER product's combo happens to need two.
      const comboComplete = this.combo && this.comboColors(this.combo).length <= 1;
      if (this.secondActive() && !this.state.color[1] && !comboComplete) return 'color2';
      if (this.hasOosSelected()) return 'oos';
      // Native mode: the chosen category combination must map to a real price variant,
      // or we cannot guarantee the charge (missing variant, renamed category, or a new
      // product not fully set up). Block rather than let a wrong price through.
      if (this.native && this.state.color[0] && !this._priceResolved) return 'price';
      return '';
    }
    /* Copy for a block code. Returns '' when the merchant cleared that field on purpose:
     * the buyer then sees no strip, but the gate above still holds. The `??` fallbacks
     * only cover a MISSING key (an old cached config), never an empty one. */
    blockMsg(code) {
      const c = this.cfg, C = c.combos || {};
      if (code === 'combo')  return C.requireMsg ?? 'Elegí una combinación para continuar.';
      if (code === 'color1') return c.requireColorMsg ?? 'Elegí un color para continuar.';
      if (code === 'color2') return c.requireColor2Msg ?? 'Elegí los dos colores para continuar.';
      if (code === 'oos')    return c.oosMsg ?? 'Elegí un color en stock antes de comprar.';
      if (code === 'price')  return c.priceUnavailableMsg ?? 'Esa combinación no está disponible por ahora. Probá otra.';
      return '';
    }
    // Kept as the message-shaped accessor other code and tests already use.
    blockReason() { return this.blockMsg(this.blockCode()); }
    isBlocked() { return !!this.blockCode(); }
    // Second color still pending (2nd selector on, Color 1 picked, Color 2 empty).
    // Same combo-aware exception as blockReason() above - a selected combo that only
    // ever had one colour is not "still pending" a second one.
    needsSecondColor() {
      const comboComplete = this.combo && this.comboColors(this.combo).length <= 1;
      return !!(this.cfg.type === 'cut' && this.state.option === 'custom' && this.secondActive() && this.state.color[0] && !this.state.color[1] && !comboComplete);
    }
    updateGate() {
      const btn = this.submitBtn();
      const code = this.blockCode();
      if (btn) { btn.classList.toggle('scs-soft-disabled', !!code); btn.setAttribute('aria-disabled', code ? 'true' : btn.getAttribute('data-orig-aria') || 'false'); }
      // Warn proactively for an out-of-stock pick, or when the 2nd color is still
      // pending (both are active bad states). The "no color at all" case stays quiet
      // until the buyer tries to add to cart.
      if (this.hasOosSelected()) this.showWarn(true, this.blockMsg('oos'));
      else if (this.needsSecondColor()) this.showWarn(true, this.blockMsg('color2'));
      else this.showWarn(false);
    }
    submitBtn() { return this.form ? (this.form.querySelector('[name="add"]') || document.getElementById('ProductSubmitButton-' + this.sectionId)) : null; }
    /* An empty message is a merchant choice, not a missing value, so the strip hides
     * instead of substituting some OTHER field's copy - which is what it used to do, and
     * which made a cleared "sin stock" message show the "elegí un color" one. Blocking is
     * unaffected either way: that is blockCode()'s job, not this one's. */
    showWarn(on, msg) {
      const w = this.q('.scs-warn'); if (!w) return;
      const text = String(msg ?? '').trim();
      w.hidden = !on || !text;
      if (on && text) w.textContent = text;
    }

    /* ---------- palette popover (cut) ---------- */
    openPalette(i) { this.palTarget = i; this.palCat = this.state.cat[i]; ACTIVE = this; this.buildPalette(); this.show(this.popPalette, this.qa('.scs-swatch-field')[i]); }
    buildPalette() {
      const e = this.esc, cats = this.cfg.cut.categories || [];
      const gl = this.popPalette.querySelector('.scs-pal-gal'); if (gl) gl.hidden = !this.galleryEnabled();
      const catsEl = this.popPalette.querySelector('.scs-cats');
      catsEl.innerHTML = cats.map(c => `<button type="button" class="${c.id === this.palCat ? 'is-active' : ''}" data-cat="${e(c.id)}">${e(c.name)}</button>`).join('');
      const grid = this.popPalette.querySelector('.scs-grid');
      grid.innerHTML = '';
      (this.cfg.cut.swatches || []).filter(s => s.cat === this.palCat).forEach(s => {
        const d = document.createElement('div');
        const oos = this.isOos(s.code);
        d.className = 'scs-sw' + (this.state.color[this.palTarget] === s.code ? ' is-sel' : '') + (oos ? ' scs-oos' : '');
        d.setAttribute('title', s.code + ' - ' + s.name + (oos ? ' (Sin Stock)' : ''));
        d.dataset.code = s.code;
        d.style.backgroundImage = this.swatchBg(s); // property assignment -> no HTML/attr injection
        // OOS colors stay selectable (so the buyer can see the color big), but the
        // grid marks them and the add-to-cart gate blocks the order.
        if (oos) { const b = document.createElement('span'); b.className = 'scs-oos-badge'; b.textContent = 'Sin stock'; d.appendChild(b); }
        d.addEventListener('click', () => this.pickSwatch(s.code));
        grid.appendChild(d);
      });
      catsEl.querySelectorAll('button').forEach(b => b.addEventListener('click', () => { this.palCat = b.dataset.cat; this.state.cat[this.palTarget] = b.dataset.cat; this.qa('.scs-cat')[this.palTarget].value = b.dataset.cat; this.buildPalette(); this.recalc(); }));
    }
    pickSwatch(code) {
      const s = this.swatch(code); if (!s) return;
      // Editing a colour by hand means this is no longer one of his combinations, so
      // the combo reference and its photo must stop travelling with the order.
      if (this.combo) this.clearCombo();
      const i = this.palTarget;
      this.state.color[i] = code;
      const fld = this.qa('.scs-swatch-field')[i];
      fld.querySelector('.scs-cf-text').textContent = `${s.code} - ${s.name}`;
      this.updatePreview(i);
      this.buildPalette(); this.recalc(); this.close();
    }
    // Paint the large round preview for row i (or hide it when nothing is chosen).
    // Flags out-of-stock so the buyer sees the "Sin Stock" banner on the big color.
    updatePreview(i) {
      const prev = this.q(`[data-prev="${i}"]`); if (!prev) return;
      const big = prev.querySelector('.scs-big');
      const code = this.state.color[i], s = code ? this.swatch(code) : null;
      if (!s) { prev.classList.remove('is-show', 'scs-oos'); if (big) big.style.backgroundImage = 'none'; return; }
      if (big) big.style.backgroundImage = this.swatchBg(s);
      prev.classList.toggle('scs-oos', this.isOos(code));
      prev.classList.add('is-show');
    }
    // Click-to-enlarge (client #16): show the chosen color large over a dimmed
    // backdrop. Uses the shared zoom overlay routed to the active instance.
    /* Full-bleed viewer for a real PHOTO, as opposed to openZoom() below which enlarges a
     * round colour swatch. Same overlay, different shape: a photo needs to fill the screen
     * and keep its aspect ratio, so the square swatch sizing is swapped out via a modifier
     * class. Used for the combination photo, whose tap is otherwise dead because the
     * theme's own zoom is deliberately suppressed over it. */
    /* Cliente 2026-09-23: bloque de fotos/video que explican como elegir los colores. Vive dentro
     * de `inner`, o sea dentro de la pestana de Colores Personalizados, para que no se filtre a
     * Simil Original ni a Colores disponibles. Las miniaturas reusan el visor a pantalla completa
     * (openPhotoZoom) que ya nace pegado al body, y el video se carga en linea al tocarlo (sin
     * overlay) para no chocar con la trampa del transform del ancestro de la columna derecha. */
    explicaHTML() {
      const c = this.cfg, e = this.esc;
      const EX = c.elegir || {};
      const imgs = (EX.imgs || []).filter(im => im && im.ch);
      const emb = this.embedUrl(EX.video);
      if (!imgs.length && !emb) return '';
      let h = '<div class="scs-explica">';
      if (EX.titulo) h += `<div class="scs-explica-t">${e(EX.titulo)}</div>`;
      if (imgs.length) {
        h += '<div class="scs-explica-row">';
        imgs.forEach(im => {
          h += `<button type="button" class="scs-explica-img" data-g="${e(im.g || im.ch)}" aria-label="Ver imagen">` +
               `<img src="${e(im.ch)}" loading="lazy" alt="${e(im.alt || '')}"></button>`;
        });
        h += '</div>';
      }
      if (emb) {
        const lbl = EX.videoLabel || 'Ver el video';
        h += `<div class="scs-explica-vid" data-embed="${e(emb)}">` +
             `<button type="button" class="scs-explica-vbtn"><span class="scs-explica-play" aria-hidden="true">&#9654;</span>${e(lbl)}</button></div>`;
      }
      if (EX.caption) h += `<div class="scs-explica-cap">${e(EX.caption)}</div>`;
      h += '</div>';
      return h;
    }
    // Turn a YouTube/Vimeo watch link into its embeddable player URL. Same parsing shape as
    // sf-video.liquid uses; returns null for anything it does not recognise so the block just
    // renders the images and skips the video button.
    embedUrl(url) {
      const raw = String(url || '').trim();
      if (!raw) return null;
      const u = raw.toLowerCase();
      let id;
      if (u.includes('youtube.com/watch')) { const m = raw.match(/[?&]v=([^&]+)/); id = m && m[1]; if (id) return 'https://www.youtube.com/embed/' + id; }
      if (u.includes('youtu.be/')) { id = raw.split('youtu.be/')[1].split(/[?&#]/)[0]; if (id) return 'https://www.youtube.com/embed/' + id; }
      if (u.includes('youtube.com/shorts/')) { id = raw.split('shorts/')[1].split(/[?&#]/)[0]; if (id) return 'https://www.youtube.com/embed/' + id; }
      if (u.includes('youtube.com/embed/')) return raw;
      if (u.includes('vimeo.com/')) { id = raw.split('vimeo.com/')[1].split(/[?&#]/)[0].replace(/\D/g, ''); if (id) return 'https://player.vimeo.com/video/' + id; }
      return null;
    }
    // Lazy player: swap the poster button for the real iframe on tap. Loaded in place (no
    // overlay), so nothing here is a fixed element and the transform-ancestor trap on the
    // right column (see sf-video.js note) does not apply.
    playExplicaVideo(vid) {
      const emb = vid && vid.getAttribute('data-embed');
      if (!emb || vid.querySelector('iframe')) return;
      const f = document.createElement('iframe');
      f.src = emb + (emb.indexOf('?') > -1 ? '&' : '?') + 'autoplay=1&rel=0';
      f.title = 'Video';
      f.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
      f.setAttribute('allowfullscreen', '');
      vid.innerHTML = '';
      vid.appendChild(f);
      vid.classList.add('is-playing');
    }
    openPhotoZoom(url, caption) {
      if (!this.zoomOverlay || !url) return;
      ACTIVE = this;
      const limpia = String(url).replace(/['")]/g, '');
      if (this.zoomPh) { this.zoomPh.src = limpia; this.zoomPh.classList.remove('scs-zoomed'); }
      else this.zoomImg.style.backgroundImage = `url('${limpia}')`;
      this.zoomImg.style.width = this.zoomImg.style.height = '';
      this.zoomOverlay.classList.remove('scs-zoom-oos', 'is-zoomed');
      this.zoomOverlay.classList.add('scs-zoom-photo');
      this.zoomCap.textContent = caption || '';
      this.zoomOverlay.classList.add('is-show');
      this.lockPage(true);
    }
    openZoom(i) {
      const code = this.state.color[i]; if (!code) return;
      const s = this.swatch(code); if (!s) return;
      ACTIVE = this;
      // Drop the photo shape so the round-swatch sizing below applies again.
      this.zoomOverlay.classList.remove('scs-zoom-photo', 'is-zoomed');
      if (this.zoomPh) { this.zoomPh.removeAttribute('src'); this.zoomPh.classList.remove('scs-zoomed'); }
      this.zoomImg.style.backgroundImage = this.swatchBg(s);
      // Size the enlarge to each PNG's own resolution so a low-res swatch is shown
      // as large as it can go while staying crisp, instead of being blown up into a
      // pixelated blur (client 2026-07-06). Cap = 1.5x native, clamped to the 320px
      // design max. When higher-res files are supplied later, this auto-uses them.
      const url = s.img ? String(s.img).replace(/['")]/g, '') : '';
      if (url) {
        const probe = new Image();
        probe.onload = () => {
          const nat = Math.max(1, probe.naturalWidth);
          const cap = Math.min(320, Math.round(nat * 1.5));
          this.zoomImg.style.width = this.zoomImg.style.height = 'min(74vw, ' + cap + 'px)';
        };
        probe.src = url;
      } else {
        this.zoomImg.style.width = this.zoomImg.style.height = '';
      }
      const oos = this.isOos(code);
      this.zoomCap.textContent = `${s.code} - ${s.name}` + (oos ? ' - Sin Stock' : '');
      this.zoomOverlay.classList.toggle('scs-zoom-oos', oos);
      this.zoomOverlay.classList.add('is-show');
      this.lockPage(true);
    }

    /* R6-1c: mientras el visor esta abierto la pagina de atras no se mueve. Es el mismo recurso
     * que ya usaba la galeria de colores (scs-gal-lock), puesto aparte para que las dos formas de
     * abrir el visor (foto y muestra de color) y el unico cierre pasen todas por aca y no se
     * desincronicen. Ojo: la galeria tiene su propio candado; si alguna vez se abre una sobre la
     * otra, cada una saca solo el suyo y la que sigue abierta mantiene el de ella. */
    lockPage(on) {
      document.documentElement.classList.toggle('scs-zoom-lock', !!on);
    }

    /* ---------- featured combinations (client spec points 1 + 2) ----------
     * Combinations the client has already manufactured, with his real photos. The
     * hard requirement is that a combo must reach the order EXACTLY as if the buyer
     * had picked those colours by hand: same codes, same line item properties, same
     * price variant. So a combo only ever REFERENCES colours from the one vinyl base
     * and then replays the normal selection path. It adds two properties of its own
     * (the combination reference and its first photo) for the production panel. */
    /* R7-5, cliente 2026-08-28 sobre el AX 100: "muestra Colores disponibles pero el que tiene
     * esta desabilitado por falta de stock de un color o algo ... mostrar 1 opcion y no poder
     * seleccionarla no aporta, deberia una regla decir si no hay combinacion disponible
     * habilitada saltarse esa pestaña y que aparezca si hay combinacion elegible".
     *
     * Antes esto contaba combinaciones CARGADAS. Ahora cuenta combinaciones ELEGIBLES, que es lo
     * que hace util a la pestaña. Con cero elegibles no se dibujan las pestañas y la ficha abre
     * directo en el selector de colores, exactamente igual que un producto que nunca tuvo
     * combinaciones. No hay un estado nuevo que mantener: es un camino que ya existia.
     *
     * Vuelve sola. `comboUnavailable()` mira el stock del vinilo, asi que el dia que ese color
     * vuelva a haber la pestaña reaparece sin que el cliente toque nada, que es literalmente lo
     * que pidio ("que aparezca si hay combinacion elegible").
     *
     * Dos cosas que se acomodan solas por estar atadas aca y no a un ajuste aparte:
     *  - el parrafo "tenes dos formas de comprar" ya se ata a esta misma funcion desde R5-3, o
     *    sea que se oculta con las pestañas y no queda explicando algo que no esta;
     *  - `blockCode()` deja de exigir "elegi una combinacion", porque no hay ninguna que elegir.
     *
     * Se evalua en cada llamada a proposito, sin cachear: `this.combos` se puede rearmar en un
     * re-render de seccion y un valor viejo dibujaria una pestaña vacia. `.some()` corta en la
     * primera elegible, asi que en el caso normal ni recorre la lista. */
    combosEnabled() {
      if (this.cfg.combos && this.cfg.combos.enabled === false) return false;
      return (this.combos || []).some(cb => !this.comboUnavailable(cb));
    }
    // Sub-tabs only exist when there is something to put in them. With no combos the
    // markup is identical to before, which is what the regression suite pins down.
    wrapPanes(inner) {
      if (!this.combosEnabled()) return inner;
      const e = this.esc, C = this.cfg.combos || {};
      return `<div class="scs-subtabs" role="tablist">` +
          `<button type="button" class="scs-subtab is-active" data-pane="combos" role="tab" aria-selected="true">${e(C.tabAvailable ?? 'Colores disponibles')}</button>` +
          `<button type="button" class="scs-subtab" data-pane="custom" role="tab" aria-selected="false">${e(C.tabCustom ?? 'Fabricación Personalizada')}</button>` +
        `</div><div class="scs-pane scs-pane-combos"></div><div class="scs-pane scs-pane-custom" hidden>${inner}</div>`;
    }
    showPane(which) {
      this.pane = which;
      this.qa('.scs-subtab').forEach(b => {
        const on = b.dataset.pane === which;
        b.classList.toggle('is-active', on); b.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      const pc = this.q('.scs-pane-combos'), pu = this.q('.scs-pane-custom');
      if (pc) pc.hidden = which !== 'combos';
      if (pu) pu.hidden = which !== 'custom';
      // Leaving the combinations tab means the buyer is building their own, so the
      // combo (and its two panel properties, and its photo) must not travel with it.
      /* R7-2 y despues R8, cliente 2026-08-28: "lo de dejar color o colores predeterminados para
       * mostrar en Elegir mis colores 1 o 2 ... no obedece", y sobre lo que quedo de R7-2:
       * "cambias de combinacion y se cambia el color predeterminado en la otra pestaña".
       *
       * R7-2 distinguia quien habia elegido la combinacion y dejaba que una elegida A MANO se
       * llevara sus colores aca. Su video del Wave S muestra por que no le sirve: elige C21 en la
       * otra pestaña y su predeterminado A11 desaparece de esta. Ahora se reponen SIEMPRE, sin
       * mirar quien eligio. Con `defaults` vacio reseedDefaults() no toca nada, asi que los
       * productos sin predeterminados se comportan igual que antes. */
      if (which !== 'combos' && this.combo) {
        this.clearCombo();
        this.reseedDefaults();
      }
      // Returning to "Colores disponibles" should land on the same sensible default a
      // fresh visit gets (see ensureComboSelected()), not the blank placeholder the
      // line above just created by clearing the previous pick on the way out.
      if (which === 'combos') this.ensureComboSelected();
      this.recalc();
    }
    // The colours a combo is made of, resolved against the ONE vinyl base. A code the
    // base does not know is dropped, so a typo in the feed cannot invent a colour.
    comboColors(cb) {
      return (cb && Array.isArray(cb.colores) ? cb.colores : [])
        .map(code => this.swatch(String(code).trim())).filter(Boolean);
    }
    // Unavailable = built on a colour that is out of stock, or missing colours. Reusing
    // the same stock list the palette uses is the point (spec rule 8).
    comboUnavailable(cb) {
      // `disponible: false` never reaches here: those are filtered out on load, because
      // he wants them gone rather than greyed. This is only about stock and bad data.
      if (!cb) return true;
      if (this.cfg.type === 'cut') {
        // Client report 2026-08-20: "Mostrar segundo selector" LOOKS like a per-product
        // theme-editor toggle, but it lives in the shared default product.json template -
        // every cut-vinyl product without its own template shares the same value. Gating
        // this on cfg.cut.secondSelector (a product-wide, not combo-wide, setting) meant
        // turning it on for one 2-colour product's combos marked EVERY 1-colour combo on
        // EVERY OTHER cut-vinyl product "Momentáneamente no disponible" - his whole
        // catalog sharing one on/off switch. A combo's own data already says how many
        // colours it needs: compare resolved colours against how many codes were ENTERED,
        // so a genuinely malformed combo (a typo'd code that didn't resolve) still gets
        // caught, without trusting a setting that is not actually scoped to this combo.
        const enteredCount = Array.isArray(cb.colores) ? cb.colores.length : 0;
        const cols = this.comboColors(cb);
        if (cols.length === 0) return true;
        if (cols.length < enteredCount) return true;
        if (cols.some(s => this.isOos(s.code))) return true;
      }
      return false;
    }
    comboTitle(cb) {
      if (cb && cb.titulo) return String(cb.titulo);
      if (this.cfg.type !== 'cut') return (cb && Array.isArray(cb.hex) ? cb.hex.join(' + ') : '');
      return this.comboColors(cb).map(s => s.code).join(' + ');
    }
    /* T5, client 2026-08-21: "Del menu quitaria los codigos que aparezcan los 4 tonos nomas".
     * En impreso el rotulo era la lista de hex encadenados ("#000000 + #653D94 + ..."), que ademas
     * se cortaba. Las muestras de color ya se dibujan al lado, asi que el texto sobra: queda solo
     * lo que el haya escrito en `titulo`, si escribio algo.
     * Se separa del `comboTitle()` de siempre a proposito, porque ese sigue usandose como
     * referencia interna para el panel de produccion cuando la entrada no trae `ref`. */
    comboLabel(cb) {
      if (cb && cb.titulo) return String(cb.titulo);
      if (this.cfg.type !== 'cut') return '';
      return this.comboTitle(cb);
    }
    comboSubtitle(cb) {
      if (this.cfg.type !== 'cut') return '';
      return this.comboColors(cb).map(s => s.name).join(' + ');
    }
    buildCombosPane() {
      const e = this.esc, C = this.cfg.combos || {}, host = this.q('.scs-pane-combos');
      if (!host) return;
      this.pane = 'combos';   // spec: the combinations tab is the one that opens first
      // A section re-render rebuilds this pane; drop the previous outside-click
      // listener so they do not stack up on the document.
      if (this._cbOutside) document.removeEventListener('click', this._cbOutside);
      host.innerHTML =
        `<div class="scs-cb-field"><label>${e(C.label ?? 'Combinaciones disponibles:')}</label>` +
          `<button type="button" class="scs-cb-toggle" aria-haspopup="listbox" aria-expanded="false">` +
            `<span class="scs-cb-chips"></span>` +
            `<span class="scs-cb-text">${e(C.placeholder ?? 'Elegí una combinación')}</span></button>` +
          `<div class="scs-cb-list" role="listbox" hidden></div></div>` +
        `<div class="scs-cb-summary" hidden></div>` +
        `<div class="scs-cb-media"></div>`;
      // Client 2026-08-20: dropped the "¿No encontraste el color que buscabas? -> Crear mi
      // Combinación" callout that used to render here. He liked the copy but pointed out
      // it duplicates the "Colores disponibles / Fabricación Personalizada" tabs already
      // above it - the same destination, said twice, just to fill space ("bloque de texto
      // al cuete"). Removing it does not lose any capability; the tabs already do this.
      const list = host.querySelector('.scs-cb-list');
      this.combos.forEach((cb, i) => list.appendChild(this.comboOption(cb, i)));
      const tog = host.querySelector('.scs-cb-toggle');
      tog.addEventListener('click', () => {
        const open = list.hidden;
        list.hidden = !open; tog.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      // A native <select> cannot render colour chips, so this is a custom listbox.
      // That means closing it on an outside click is our job.
      this._cbOutside = ev => { if (!host.contains(ev.target)) { list.hidden = true; tog.setAttribute('aria-expanded', 'false'); } };
      document.addEventListener('click', this._cbOutside);
      // Client 2026-08-20: picking "Color Personalizado" landed on "Colores disponibles"
      // showing no real photo (the generic product image, which read to him as belonging
      // to "Fabricación Personalizada" - "hay un cruce de jerarquías ahí"). If there is a
      // real combination on offer, the first available one should already be showing
      // rather than making the buyer open the dropdown first.
      //
      // render() runs more than once during initial setup (existing behaviour, not new
      // here), and each run rebuilds this pane's innerHTML from scratch, which wipes the
      // toggle/summary/media back to their empty starting markup even though this.combo
      // itself is untouched by the rebuild. ensureComboSelected() re-syncs on every
      // build rather than only picking a default when this.combo is still null: if a
      // combo is already selected, re-apply it so the fresh DOM matches; only fall back
      // to auto-selecting the first available one when nothing has ever been picked.
      // Either way this never overrides a pick already in progress - it only ever
      // restates what this.combo already says, or fills it in for the first time.
      // See shouldShowComboMedia(): during this first build the variant input can still be
      // carrying a custom id while the buyer is on Símil, so the media placement is deferred
      // to applyNativeState() rather than flashing the wrong thing for a frame.
      this._buildingCombos = true;
      try { this.ensureComboSelected(); } finally { this._buildingCombos = false; }
    }
    // Client verification 2026-08-21: item 6 (default preselect) worked on a fresh
    // build, but going to "Fabricación Personalizada" and back to "Colores
    // disponibles" dropped back to the blank placeholder - showPane('combos') only
    // toggles which pane is hidden, it never rebuilds .scs-pane-combos, so the earlier
    // fix (wired into buildCombosPane() only) never got a chance to run. Leaving
    // 'combos' already clears this.combo on purpose (see showPane()) so the buyer's
    // custom pick does not travel with them - but returning should land back on the
    // same sensible default a fresh visit gets, not a blank state. Called from both
    // buildCombosPane() (a real rebuild) and showPane() (a visibility toggle only).
    /* May a combination's photo/strip be written into the SHARED product gallery right now?
     *
     * Two separate conditions, both learned the hard way:
     *  - On a NATIVE-variant product the gallery belongs to whichever "Opción" is active, so
     *    combination media may only show under Color Personalizado. A standalone product has
     *    no Opción at all, and there `isCustomSelected()` is false by construction, so gating
     *    on it alone would hide the media on those products forever.
     *  - During the first build the variant input can still be carrying a custom variant id
     *    while the buyer is actually on Símil, so `isCustomSelected()` briefly lies. That
     *    produced a visible flash of the combination strip on load. buildCombosPane() runs
     *    BEFORE initNative(), so defer to applyNativeState(), which runs immediately after
     *    and is the single authority once the option state has settled. */
    shouldShowComboMedia() {
      if (!this.native) return true;
      if (this._buildingCombos) return false;
      return this.isCustomSelected();
    }
    /* Client 2026-08-21: "en Simil original y Colores disponibles si debe estar habilitado ...
     * Y esos si es necesario que muestre el cambio de lo seleccionado."
     *
     * Scoping the old suppression per tab was not enough, because the jump was never ours to
     * begin with: it was a side effect of Dawn's variant swap, so it only fired when the picked
     * combination happened to land on a DIFFERENT price variant. Two combinations in the same
     * price group leave the variant untouched, Dawn never moves the media, and nothing scrolls.
     * Measured on the Wave: C08 -> B10 crossed a variant and scrolled; B10 <-> B14 did not, five
     * picks in a row. That is exactly what the client hit - it worked once and then stopped.
     *
     * Owning the scroll here makes every pick behave the same regardless of pricing. Wired to the
     * option CLICK, never to selectCombo() itself, which also runs for the automatic first pick
     * and for ?combo= deep links - scrolling there would yank the page on load. */
    /* Reported three times as "it works and then it doesn't". Two defects, both here.
     *
     * 1. The "already visible" test used to be `r.bottom > 80 && r.top < innerHeight - 80`, which
     *    is true while only the BOTTOM sliver of the photo is on screen. A photo scrolled 280px
     *    under the header counted as visible, so the method returned and left it there.
     * 2. A smooth scroll can be interrupted. Dawn re-renders the section after a variant change,
     *    the page height moves under the animation, and the browser stops it partway. It settled
     *    inside the tolerance above, so nothing ever corrected it. Measured on the Wave's Simil:
     *    y 688 -> 282 with 282px of the photo hidden, every single time.
     *
     * Now "framed" means the TOP of the photo is below the bar, and the reveal re-asserts itself
     * on any media mutation within a short window after the pick, which is exactly when Dawn's
     * re-render lands. Idempotent: once framed it returns immediately. */
    mediaFramed(m, head) {
      const r = m.getBoundingClientRect();
      return r.top >= head - 2 && r.top < window.innerHeight - 40;
    }
    revealMediaIfNeeded(reassert) {
      const m = document.querySelector('media-gallery') || document.querySelector('.product__media');
      if (!m || typeof window.scrollTo !== 'function') return;
      const sh = document.querySelector('sticky-header') || document.querySelector('.header-wrapper');
      const head = sh ? Math.round(sh.getBoundingClientRect().height) : 0;
      // Only a fresh pick opens the correction window; the re-assert must not extend it forever.
      if (!reassert) this._revealUntil = Date.now() + 1400;
      if (this.mediaFramed(m, head)) return;
      const r = m.getBoundingClientRect();
      let top = Math.max(0, window.scrollY + r.top - head - 8);
      if (top < head + 24) top = 0;   // it was already at the top of the page: go all the way
      try { window.scrollTo({ top: top, behavior: 'smooth' }); }
      catch (e) { window.scrollTo(0, top); }
    }
    ensureComboSelected() {
      if (!this.combosEnabled() || !Array.isArray(this.combos)) return;
      const idx = this.combo ? this.combos.indexOf(this.combo) : -1;
      if (idx !== -1) {
        this.selectCombo(idx);
      } else if (!this.combo) {
        /* Client 2026-08-20: "ese color por ejemplo no es el mas lindo y vistoso. Me
         * gustaria poder poner el mas lindo y el mas vendido ... Entonces no dejo que el
         * sistema elija solo porque ahora esta eligiendo por ubicacion. Y el primer color
         * es amarillo." So an entry he has ticked as `predeterminada` wins; only when he
         * has not chosen one do we fall back to the first available, which is what he was
         * complaining about. A default that is out of stock is skipped rather than shown,
         * for the same reason any other unavailable combo is. */
        let pick = this.combos.findIndex(cb => cb && cb.predeterminada === true && !this.comboUnavailable(cb));
        if (pick === -1) pick = this.combos.findIndex(cb => !this.comboUnavailable(cb));
        if (pick !== -1) this.selectCombo(pick);
      }
    }
    comboOption(cb, i) {
      const off = this.comboUnavailable(cb), C = this.cfg.combos || {};
      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'scs-cb-opt' + (off ? ' is-off' : '');
      row.setAttribute('role', 'option');
      row.dataset.i = String(i);
      row.appendChild(this.comboChips(cb));
      const txt = document.createElement('span');
      txt.className = 'scs-cb-opt-txt';
      const t = document.createElement('span'); t.className = 'scs-cb-opt-t'; t.textContent = this.comboLabel(cb); txt.appendChild(t);
      const sub = this.comboSubtitle(cb);
      if (sub) { const n = document.createElement('span'); n.className = 'scs-cb-opt-n'; n.textContent = sub; txt.appendChild(n); }
      if (off) {
        const b = document.createElement('span'); b.className = 'scs-cb-opt-off';
        b.textContent = C.unavailableMsg ?? 'Momentáneamente no disponible';
        txt.appendChild(b);
      }
      row.appendChild(txt);
      // Unavailable combos stay VISIBLE (so the buyer sees it exists) but cannot be
      // chosen. That is the client's instruction for combos specifically.
      if (off) { row.disabled = true; row.setAttribute('aria-disabled', 'true'); }
      else row.addEventListener('click', () => { this.selectCombo(i); this.revealMediaIfNeeded(); });
      return row;
    }
    comboChips(cb) {
      const wrap = document.createElement('span');
      wrap.className = 'scs-cb-chip-row';
      if (this.cfg.type === 'cut') {
        this.comboColors(cb).forEach(s => {
          const c = document.createElement('span');
          c.className = 'scs-cb-chip';
          c.style.backgroundImage = this.swatchBg(s);
          wrap.appendChild(c);
        });
      } else {
        (Array.isArray(cb.hex) ? cb.hex : []).slice(0, 4).forEach(h => {
          const c = document.createElement('span');
          c.className = 'scs-cb-chip';
          c.style.background = this.normHex(h);
          wrap.appendChild(c);
        });
      }
      return wrap;
    }
    selectCombo(i) {
      const cb = this.combos[i];
      if (!cb || this.comboUnavailable(cb)) return;
      this.combo = cb;
      if (this.cfg.type === 'cut') {
        // Replay the ordinary selection: a combo's colour carries its own group, and
        // the group is what drives the price variant, so both must be applied.
        const cols = this.comboColors(cb);
        const max = this.secondActive() ? 2 : 1;
        for (let k = 0; k < max; k++) {
          const s = cols[k];
          if (!s) { this.state.color[k] = null; continue; }
          this.state.color[k] = s.code;
          if (s.cat) this.state.cat[k] = s.cat;
          const sel = this.qa('.scs-cat')[k]; if (sel && s.cat) sel.value = s.cat;
          const fld = this.qa('.scs-swatch-field')[k];
          if (fld) fld.querySelector('.scs-cf-text').textContent = s.code + ' - ' + s.name;
          this.updatePreview(k);
        }
      } else {
        (Array.isArray(cb.hex) ? cb.hex : []).slice(0, this.state.printed.length).forEach((h, k) => {
          const hex = this.normHex(h);
          this.state.printed[k] = this.hexToState(hex);
          this.state.printedSeedHex[k] = hex;
          const dot = this.q(`.scs-dot[data-i="${k}"]`); if (dot) dot.style.background = hex;
          const hx = this.q(`[data-hx="${k}"]`); if (hx) hx.textContent = hex.toUpperCase();
        });
      }
      // Reflect the pick in the closed control, and show the combo's own media.
      // R7-5: desde que las pestañas pueden no dibujarse, este panel puede no existir. Hoy no hay
      // ningun camino que llegue aca en ese caso (sin pestañas no hay opciones que clickear, y
      // ensureComboSelected y el deep link ya pasan por combosEnabled), pero sin esta guarda un
      // camino nuevo tiraria un TypeError que se lleva puesto el selector entero.
      const host = this.q('.scs-pane-combos');
      if (!host) return;
      const tog = host.querySelector('.scs-cb-toggle');
      /* Client 2026-08-21: "queda mejor abreviado ejemplo C10 + A22 y agrandar la muestra del
       * vinilo". The closed control now shows only the reference (his own `titulo` when he set
       * one, otherwise the codes joined, e.g. "A22 + C23"). The full colour names are not lost:
       * they are spelled out directly underneath in the Color 1 / Color 2 summary, which is the
       * repetition he was pointing at. */
      tog.querySelector('.scs-cb-text').textContent = this.comboLabel(cb);
      const chips = tog.querySelector('.scs-cb-chips'); chips.innerHTML = ''; chips.appendChild(this.comboChips(cb));
      const list = host.querySelector('.scs-cb-list'); list.hidden = true; tog.setAttribute('aria-expanded', 'false');
      list.querySelectorAll('.scs-cb-opt').forEach(o => o.classList.toggle('is-sel', +o.dataset.i === i));
      this.renderComboSummary(cb);
      const media = host.querySelector('.scs-cb-media');
      media.innerHTML = '';
      /* Client 2026-08-20: "ahora cuando ingreso en el celular y en la pc me aparece asi
       * como mezclado ... vista predeterminada aun no se ha elegido nada aparece lo de
       * Colores disponibles", with a screenshot of Simil Original showing a combination's
       * photo strip. Both calls below write into the SHARED product media gallery, not
       * into our own pane, so they must only run while "Color Personalizado" is the
       * active Opcion. Under Simil the buyer is looking at the simil kit and a
       * combination's photo has no business being there.
       * This was latent until combos began auto-selecting on load: before that nothing
       * was ever selected on a fresh visit, so nothing leaked. applyNativeState() puts
       * them back or clears them whenever the Opcion changes. The selector-column block
       * is only a fallback for a product template with no media gallery at all. */
      if (this.shouldShowComboMedia()) {
        this.swapProductImage(cb.imagen || (cb.fotos || [])[0]);
        if (!this.placeComboStrip(cb)) {
          this.renderGalleryMedia(media, null, this.galLabels(), { fotos: (cb.fotos || []).filter(Boolean), videos: (cb.videos || []).filter(Boolean) });
        }
      }
      this.recalc();
    }
    /* Client spec page 2: under the closed selector he lists the combination broken out,
     * one line per colour plus the detalle:
     *   Color 1: C31 - Holograpic Burgundy
     *   Color 2: C34 - Holograpic Silver
     *   Detalle: -
     * El guion venia de su propia maqueta: la fila Detalle aparecia igual pero vacia, para que el
     * bloque no cambiara de alto entre combinaciones.
     * R6-2, cliente 2026-08-26: se arrepintio y pidio lo contrario. "si no hay detalle que no
     * muestre nada directamente talvez estaria bueno si quitarlo si no suma nada. Y que aparezca
     * solo si hay cargado en la receta algo en detalles". Asi que la fila ya no se emite cuando
     * viene vacia. NO volver a poner el guion mirando la maqueta vieja: la maqueta quedo atras. */
    renderComboSummary(cb) {
      const host = this.q('.scs-pane-combos'); if (!host) return;
      const sum = host.querySelector('.scs-cb-summary');
      const C = this.cfg.combos || {}, word = (this.cfg.labels || {}).colorWord ?? 'Color';
      sum.innerHTML = '';
      const row = (k, v, dot) => {
        const r = this.mk('div', 'scs-cb-sum-row');
        const kk = this.mk('span', 'scs-cb-sum-k'); kk.textContent = k + ':';
        const vv = this.mk('span', 'scs-cb-sum-v');
        // Su maqueta lista cada tono con su muestra al lado, no el codigo suelto.
        if (dot) { const d = this.mk('span', 'scs-cb-sum-dot'); d.style.background = dot; vv.appendChild(d); }
        vv.appendChild(document.createTextNode(v));
        r.append(kk, vv); sum.appendChild(r);
      };
      if (this.cfg.type === 'cut') {
        // List every colour the COMBO itself carries, not just however many the
        // product's own selector applies to state/pricing (cfg.cut.secondSelector).
        // Client report 2026-08-20: a single-selector product (e.g. Honda Wave S,
        // secondSelector false) can still have a client-authored 2-colour combo in
        // Combinaciones (A22+C23) - selectCombo() rightly only applies Color 1 to
        // state for pricing, but this summary is read for PRODUCTION reference, so a
        // second colour actually present in the combo's own data must not be hidden.
        const cols = this.comboColors(cb), multi = cols.length > 1;
        cols.forEach((s, k) => row(word + (multi ? ' ' + (k + 1) : ''), s.code + ' - ' + s.name));
      } else {
        /* Se listan TODOS los tonos de la combinacion, hasta cuatro, y no solo tantos como
         * selectores tenga el producto. Su captura mostraba un desplegable con cuatro hex y una
         * receta con tres: "que aparezcan los 4 tonos". Misma logica que en corte, donde la receta
         * se lee como referencia de produccion. */
        (Array.isArray(cb.hex) ? cb.hex : []).slice(0, 4)
          .forEach((h, k) => row(word + ' ' + (k + 1), this.normHex(h).toUpperCase(), this.normHex(h)));
      }
      // R6-2: solo si la receta trae algo cargado. Con 96 de las 100 combinaciones del catalogo
      // sin detalle, esta fila era casi siempre un guion suelto.
      const det = cb.detalle ? String(cb.detalle).trim() : '';
      if (det) row(C.detalleWord ?? 'Detalle', det);
      sum.hidden = false;
    }
    clearCombo() {
      this.combo = null;
      this.restoreProductImage();
      const host = this.q('.scs-pane-combos');
      if (!host) return;
      const tog = host.querySelector('.scs-cb-toggle');
      const C = this.cfg.combos || {};
      tog.querySelector('.scs-cb-text').textContent = C.placeholder ?? 'Elegí una combinación';
      tog.querySelector('.scs-cb-chips').innerHTML = '';
      host.querySelectorAll('.scs-cb-opt').forEach(o => o.classList.remove('is-sel'));
      const sum = host.querySelector('.scs-cb-summary'); sum.innerHTML = ''; sum.hidden = true;
      host.querySelector('.scs-cb-media').innerHTML = '';
    }
    /* Showing the combination's photo as the main image is the one genuinely
     * theme-specific thing here. It is done by OVERLAYING the active media slide,
     * never by rewriting the theme's own <img>.
     *
     * Rewriting it was the first attempt and it is wrong twice over. Dawn/Spotlight
     * renders every product image as its own slide, so a plain document query hits
     * slide 1 rather than the one on screen; and `media-gallery.js` owns those
     * elements and re-runs `setActiveMedia` on variant changes, which our own pricing
     * code triggers constantly. An overlay touches none of that: no srcset to strip,
     * no state to corrupt, and removing it is complete by construction. */
    mediaHost() {
      const g = document.querySelector('media-gallery');
      const scope = g ? (g.querySelector('[id^="GalleryViewer"]') || g) : document;
      const slide = scope.querySelector('.product__media-item.is-active') || scope.querySelector('.product__media-item');
      const host = slide ? slide.querySelector('.product__media') : null;
      // Fallback for a product template without Dawn's gallery structure.
      return host || document.querySelector('.product__media') || null;
    }
    swapProductImage(url) {
      if (!url) return;
      this._shotUrl = String(url);
      this.placeComboShot();
    }
    // Idempotent: safe to call again after the theme changes the active slide, which
    // is exactly what re-seats the photo instead of stranding it on a hidden slide.
    placeComboShot() {
      if (!this._shotUrl) return;
      const host = this.mediaHost();
      if (!host) return;                                  // no gallery found: skip, nothing else breaks
      if (this._shot && this._shot.parentNode === host && this._shot.dataset.src === this._shotUrl) return;
      this.removeComboShot();
      const url = this._shotUrl;
      let node = this.videoNode(url);                     // null unless it really is a video
      if (!node) {
        node = document.createElement('img');
        node.setAttribute('alt', '');
        node.setAttribute('decoding', 'async');
        node.src = url;                                   // the hero image, so not lazy
      }
      node.classList.add('scs-combo-shot');
      node.dataset.src = url;
      /* Client 2026-08-20: "Solo Colores Disponibles no funciona ... no se puede recorrer
       * la imagen agrandarla ni nada". The theme's zoom is switched off over this photo on
       * purpose (see the note below) because its modal only knows the theme's own media and
       * would enlarge the ORIGINAL kit picture, not this combination. So give the photo its
       * own viewer instead of leaving the tap dead. Videos keep their native controls. */
      if (node.tagName === 'IMG') {
        node.classList.add('scs-shot-zoomable');
        node.addEventListener('click', ev => {
          ev.preventDefault();
          ev.stopPropagation();
          /* Cliente 2026-08-22: "cuando agrandas la imagen de vinilo impreso 'colores disponibles'
           * abajo aparecen los 4 codigos hex en texto. Talvez sacarlos porque el cliente no va a
           * entender". Es el mismo pedido de T5 pero en la vista agrandada, que quedo usando el
           * `comboTitle()` viejo: en impreso ese devuelve la lista de hex encadenada. Se pasa a
           * `comboLabel()`, que es el rotulo pensado para el comprador (vacio en impreso salvo que
           * el haya cargado un `titulo`). `comboTitle()` no se toca porque sigue siendo la
           * referencia interna de produccion. */
          this.openPhotoZoom(url, this.combo ? this.comboLabel(this.combo) : '');
        });
      }
      host.appendChild(node);
      this._shot = node;
      // Dawn covers the whole media area with an invisible zoom hit-area
      // (.product__media-toggle::after, z-index 2) that sits AFTER our overlay in the
      // DOM, so at equal z-index it swallows every click: tapping our video opened the
      // theme's zoom modal showing the ORIGINAL image instead. Flag the container so the
      // CSS can lift our overlay above it and switch the zoom target off while it is up.
      // Zoom is disabled deliberately here: the modal only knows the theme's own media,
      // so it would enlarge the wrong picture anyway.
      this._shotScope = host.closest('.product-media-container') || host.parentNode;
      if (this._shotScope) this._shotScope.classList.add('scs-has-shot');
      // 2026-10-01: ya no se oculta la galeria al entrar, asi que no hay nada que "revelar" aca. La
      // foto nativa se ve al instante y esta .scs-combo-shot (absolute, z-index 3) la tapa al cargar.
      // Ver el comentario del anti-parpadeo retirado en main-product.liquid.
    }
    removeComboShot() {
      if (this._shot && this._shot.parentNode) this._shot.parentNode.removeChild(this._shot);
      this._shot = null;
      if (this._shotScope) { this._shotScope.classList.remove('scs-has-shot'); this._shotScope = null; }
    }
    removeComboStrip() {
      if (this._strip && this._strip.parentNode) this._strip.parentNode.removeChild(this._strip);
      this._strip = null;
    }
    restoreProductImage() { this.removeComboShot(); this.removeComboStrip(); this._shotUrl = null; }

    /* Spec page 2: a strip of that combination's photos and video sits under the main
     * image. His mockup draws it where a theme thumbnail strip would be, but this store's
     * gallery layout is "stacked" and renders NO thumbnail strip on any product, so there
     * is nothing to append to. Changing the theme's gallery layout would alter every
     * product page store-wide, which is the client's decision, not ours. So we draw our
     * own strip directly beneath the active slide and leave his gallery alone.
     *
     * Returns false when there is no media gallery to attach to, so the caller can fall
     * back to rendering the media inside the selector column instead. */
    placeComboStrip(cb) {
      this.removeComboStrip();
      const items = (cb.fotos || []).filter(Boolean).concat((cb.videos || []).filter(Boolean));
      if (!items.length) return true;                    // nothing to show is not a failure
      const host = this.mediaHost();
      const anchor = host && host.closest('.product-media-container');
      if (!anchor) return false;
      const C = this.cfg.combos || {};
      const strip = this.mk('div', 'scs-combo-strip');
      const row = this.mk('div', 'scs-combo-thumbs');
      // He draws four tiles with the last reading "+2". Keep that: show three, then a
      // counter tile that expands the rest in place rather than hiding them for good.
      const CAP = 4;
      const overflow = items.length > CAP ? items.length - (CAP - 1) : 0;
      const shown = overflow ? items.slice(0, CAP - 1) : items;
      shown.forEach(u => row.appendChild(this.comboThumb(u)));
      if (overflow) {
        const more = this.mk('button', 'scs-combo-thumb scs-combo-more');
        more.type = 'button';
        more.textContent = '+' + overflow;
        more.addEventListener('click', () => {
          more.remove();
          items.slice(CAP - 1).forEach(u => row.appendChild(this.comboThumb(u)));
        });
        row.appendChild(more);
      }
      strip.appendChild(row);
      const cap = this.mk('div', 'scs-combo-cap');
      cap.textContent = C.stripCaption ?? 'Imágenes de esa combinación elegida';
      strip.appendChild(cap);
      anchor.insertAdjacentElement('afterend', strip);
      this._strip = strip;
      return true;
    }
    // One tile. Tapping it makes that photo or video the main image.
    comboThumb(url) {
      const b = this.mk('button', 'scs-combo-thumb');
      b.type = 'button';
      b.setAttribute('aria-label', 'Ver esta imagen');
      const isVid = !!this.videoNode(url);
      if (isVid) {
        const yt = String(url).match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
        if (yt) b.appendChild(this.lazyImg('https://i.ytimg.com/vi/' + yt[1] + '/hqdefault.jpg'));
        const ic = this.mk('span', 'scs-combo-play'); ic.textContent = '▶'; b.appendChild(ic);
      } else {
        b.appendChild(this.lazyImg(url));
      }
      b.addEventListener('click', () => { this.swapProductImage(url); this.markComboThumb(b); });
      return b;
    }
    markComboThumb(active) {
      if (!this._strip) return;
      this._strip.querySelectorAll('.scs-combo-thumb').forEach(x => x.classList.toggle('is-sel', x === active));
    }

    /* ---------- colour gallery overlay (client spec point 3) ----------
     * A shared, universal gallery over the product page: the same 84-colour base the
     * palette uses, plus the real photos/videos the client shoots. It never navigates
     * away and never touches the form, so the in-progress selection survives it.
     * The gallery REMEMBERS which selector opened it (galTarget) and assigns there. */
    galleryEnabled() {
      if (this.cfg.type !== 'cut') return false;                       // impresos: colours are free HEX, no gallery
      if (this.cfg.galeria && this.cfg.galeria.enabled === false) return false;
      return (this.cfg.cut.swatches || []).length > 0;
    }
    galleryOpen() { return !!(this.galOverlay && this.galOverlay.classList.contains('is-show')); }
    galLabels() {
      const G = this.cfg.galeria || {};
      return {
        title: G.title ?? 'Galería de colores',
        subtitle: G.subtitle || '',
        detailNote: G.detailNote || '',
        choose: G.chooseLabel ?? 'Elegir este color',
        noMedia: G.noMediaMsg ?? 'Todavía no cargamos fotos reales de este color.',
        assign: G.assignLabel ?? 'Asignar a:',
        photos: G.photosLabel ?? 'Fotos reales',
        videos: G.videosLabel ?? 'Videos',
        // Screen-reader label for a photo tile. Not a section heading, so it is not
        // exposed as a theme setting; it just must never be an empty accessible name.
        viewPhoto: G.viewPhotoLabel ?? 'Ver foto en grande'
      };
    }
    // Generic button (no selector context): fill the first colour still empty, else Color 1.
    // The detail view lets the buyer override this on two-colour products.
    defaultGalTarget() {
      if (!this.state.color[0]) return 0;
      if (this.secondActive() && !this.state.color[1]) return 1;
      return 0;
    }
    openGallery(i) {
      if (!this.galOverlay || !this.galleryEnabled()) return;
      ACTIVE = this;
      this.close(); // dismiss the palette popover behind the overlay
      this.galTarget = (i === null || i === undefined || isNaN(+i)) ? this.defaultGalTarget() : +i;
      this.galCat = this.state.cat[this.galTarget] || (((this.cfg.cut.categories || [])[0] || {}).id || '');
      const GL = this.galLabels();
      this.galOverlay.querySelector('.scs-gal-ttl').textContent = GL.title;
      const sub = this.galOverlay.querySelector('.scs-gal-sub');
      sub.textContent = GL.subtitle;
      sub.hidden = !GL.subtitle;
      this.galleryBack();          // always land on the grid, never a stale detail
      this.buildGalleryTabs();
      this.buildGalleryGrid();
      this.galOverlay.classList.add('is-show');
      // Lock the page behind the overlay so mobile scroll stays inside it.
      this._galScrollY = window.scrollY;
      document.documentElement.classList.add('scs-gal-lock');
      // Client video 2026-08-20: an Android edge-swipe-back while the gallery was open
      // exited the whole product page instead of just closing the popup, because the
      // popup never occupied its own entry in browser history - the OS gesture is a
      // real back navigation, and with nothing pushed there was nothing to "consume"
      // but the page itself. Push one entry per open so that gesture lands here first.
      if (!this._galHistoryOpen) {
        history.pushState({ scsGal: true }, '', location.href);
        this._galHistoryOpen = true;
      }
    }
    closeGallery() {
      if (!this.galOverlay) return;
      this.galOverlay.classList.remove('is-show');
      document.documentElement.classList.remove('scs-gal-lock');
      this.galDetail = null;
      this.galVideo = null;
      this.galPhoto = null;
      // Pop the entry openGallery() pushed - unless we're already here BECAUSE of a
      // popstate (the back gesture/button itself), which already consumed it; calling
      // history.back() again in that case would eat one more page beyond this one.
      if (this._galHistoryOpen && !this._galClosingFromPopstate) history.back();
      this._galHistoryOpen = false;
    }
    // "Volver": back to the colour grid. "✕" (closeGallery) exits entirely.
    galleryBack() {
      if (!this.galOverlay) return;
      // One step at a time: from a video or an enlarged photo back to its colour,
      // only then to the grid.
      if (this.galDetail) {
        if (this.galVideo || this.galPhoto) {
          const c = this.galDetail;
          this.galVideo = null; this.galPhoto = null;
          this.openGalleryDetail(c);
          return;
        }
      }
      this.galVideo = null;
      this.galPhoto = null;
      this.galDetail = null;
      this.galOverlay.querySelector('.scs-gal-grid').hidden = false;
      this.galOverlay.querySelector('.scs-gal-tabs').hidden = false;
      const d = this.galOverlay.querySelector('.scs-gal-detail');
      d.hidden = true; d.innerHTML = '';
      this.galOverlay.querySelector('.scs-gal-back').hidden = true;
      this.galSubtitle(true);
      this.galOverlay.querySelector('.scs-gal-scroll').scrollTop = 0;
    }
    /* The subtitle sells the idea of BROWSING ("explora nuestros colores..."), so it is
     * wrong on a single colour's page and it is the line the back button was colliding
     * with on a phone. Hidden there, restored on the way back. */
    galSubtitle(show) {
      const sub = this.galOverlay && this.galOverlay.querySelector('.scs-gal-sub');
      if (!sub) return;
      const txt = (this.galLabels() || {}).subtitle;
      sub.hidden = !(show && txt);
    }
    buildGalleryTabs() {
      const e = this.esc, tabs = this.galOverlay.querySelector('.scs-gal-tabs');
      tabs.innerHTML = (this.cfg.cut.categories || [])
        .map(c => `<button type="button" class="${c.id === this.galCat ? 'is-active' : ''}" data-cat="${e(c.id)}">${e(c.name)}</button>`).join('');
      tabs.querySelectorAll('button').forEach(b => b.addEventListener('click', () => {
        this.galCat = b.dataset.cat; this.buildGalleryTabs(); this.buildGalleryGrid();
        this.galOverlay.querySelector('.scs-gal-scroll').scrollTop = 0;
      }));
    }
    buildGalleryGrid() {
      const grid = this.galOverlay.querySelector('.scs-gal-grid');
      grid.innerHTML = '';
      (this.cfg.cut.swatches || []).filter(s => s.cat === this.galCat).forEach(s => {
        const oos = this.isOos(s.code);
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'scs-gal-sw' + (oos ? ' scs-oos' : '');
        const ph = document.createElement('span');
        ph.className = 'scs-gal-ph';
        if (s.img) {
          // A real <img> (not a CSS background) so the browser lazy-loads the tail of
          // an 84-colour catalog instead of fetching every PNG on open.
          ph.appendChild(this.lazyImg(String(s.img)));
        } else { ph.style.background = (s.css || '#eee'); }
        if (oos) { const band = document.createElement('span'); band.className = 'scs-oos-badge'; band.textContent = 'Sin stock'; ph.appendChild(band); }
        const cap = document.createElement('span');
        cap.className = 'scs-gal-cap';
        cap.textContent = s.code + ' - ' + s.name;
        b.append(ph, cap);
        b.addEventListener('click', () => this.openGalleryDetail(s.code));
        grid.appendChild(b);
      });
    }
    // An 84-colour catalog plus his photo base is a lot of image weight on a phone,
    // and he sells ~80% on mobile. Set as ATTRIBUTES, not properties: that is what the
    // browser's lazy loader reads, and it stays visible in the DOM for debugging.
    lazyImg(src) {
      const im = document.createElement('img');
      im.setAttribute('loading', 'lazy');
      im.setAttribute('decoding', 'async');
      im.setAttribute('alt', '');
      im.src = src;
      return im;
    }
    // Photos/videos for a colour. Codes are matched case-insensitively so the feed
    // can be authored as "a01" or "A01" without silently showing nothing.
    mediaFor(code) {
      const key = String(code == null ? '' : code).trim().toUpperCase();
      if (!this._mediaIdx) {
        this._mediaIdx = {};
        Object.keys(this.media || {}).forEach(k => { this._mediaIdx[String(k).trim().toUpperCase()] = this.media[k]; });
      }
      return this.normMedia(this._mediaIdx[key] || {});
    }
    /* Accepts both feed shapes and returns one.
     *   photo: "url"            -> { t: url, f: url }
     *          {t,f}            -> as given, either key alone is enough
     *   video: "url"            -> { u: url, p: '' }
     *          {u,p}            -> as given
     * The object form is what the panel emits (small file for the grid, big file for
     * the lightbox). The string form is what the hand-authored theme setting and
     * scs-combos.liquid emit, and it must keep working, so it degrades to "same file
     * for both" rather than being dropped. Anything else is discarded silently: a bad
     * entry should cost one tile, not the whole gallery. */
    normMedia(m) {
      const list = v => (Array.isArray(v) ? v : []);
      const str = x => (typeof x === 'string' ? x.trim() : '');
      const fotos = list(m.fotos || m.photos).map(x => {
        const s = str(x);
        if (s) return { t: s, f: s };
        if (x && typeof x === 'object') {
          const t = str(x.t), f = str(x.f);
          if (t || f) return { t: t || f, f: f || t };
        }
        return null;
      }).filter(Boolean);
      const videos = list(m.videos).map(x => {
        const s = str(x);
        if (s) return { u: s, p: '' };
        if (x && typeof x === 'object' && str(x.u)) {
          const o = { u: str(x.u), p: str(x.p) };
          // Shape, when the feed knows it. See videoNode for why it matters.
          if (Number(x.w) > 0 && Number(x.h) > 0) { o.w = Number(x.w); o.h = Number(x.h); }
          return o;
        }
        return null;
      }).filter(Boolean);
      return { fotos: fotos, videos: videos };
    }
    // Same list buildGalleryGrid() draws, in the same order - the thing a swipe or a
    // "next colour" control should walk is exactly what the grid itself shows.
    galCatSwatches() {
      return (this.cfg.cut.swatches || []).filter(s => s.cat === this.galCat);
    }
    // dir: +1 next, -1 previous. Clamps at either end rather than wrapping - a swipe
    // past the last colour in a category doing nothing reads as "that is the end",
    // which is the same thing the (invisible, until now) grid boundary already meant.
    galDetailStep(dir) {
      if (!this.galDetail) return;
      const list = this.galCatSwatches();
      const idx = list.findIndex(s => s.code === this.galDetail);
      if (idx === -1) return;
      const next = list[idx + dir];
      /* Client 2026-08-21: the swipe worked but "cambia el color sin ningun feel" - the panel was
       * rebuilt instantly, so nothing told the eye a swipe had happened and it read as the screen
       * glitching rather than moving. Slide the new colour in from the side the finger came from,
       * and rubber-band at the ends so hitting the last colour feels like a wall instead of a dead
       * gesture. Both are pure CSS animations on the panel that was just rebuilt. */
      if (next) {
        this.openGalleryDetail(next.code);
        this.galSlide(dir > 0 ? 'n' : 'p');
      } else {
        this.galSlide(dir > 0 ? 'bump-n' : 'bump-p');
      }
    }
    // Restart-safe: the class is removed and the layout flushed before re-adding, otherwise a
    // second swipe in the same direction would not replay the animation.
    galSlide(kind) {
      const d = this.galOverlay && this.galOverlay.querySelector('.scs-gal-detail');
      if (!d) return;
      d.classList.remove('scs-gal-slide-n', 'scs-gal-slide-p', 'scs-gal-bump-n', 'scs-gal-bump-p');
      void d.offsetWidth;
      d.classList.add('scs-gal-' + (kind.indexOf('bump') === 0 ? kind : 'slide-' + kind));
    }
    openGalleryDetail(code) {
      const s = this.swatch(code); if (!s) return;
      const e = this.esc, L = this.galLabels(), oos = this.isOos(code);
      this.galDetail = code;
      this.galVideo = null;
      this.galPhoto = null;
      const second = this.secondActive();
      const word = (this.cfg.labels && this.cfg.labels.colorWord) ?? 'Color';
      const d = this.galOverlay.querySelector('.scs-gal-detail');
      // Two-colour products: the buyer decides which selector this colour fills,
      // pre-set to whichever one opened the gallery.
      const pick = second
        ? `<div class="scs-gal-assign"><span>${e(L.assign)}</span>` +
            [0, 1].map(i => `<button type="button" class="${i === this.galTarget ? 'is-active' : ''}" data-t="${i}">${e(word)} ${i + 1}</button>`).join('') +
          `</div>` : '';
      d.innerHTML =
        `<div class="scs-gal-hero">${s.img ? `<img src="${e(s.img)}" alt="" decoding="async">` : ''}` +
          (oos ? `<span class="scs-oos-badge">Sin stock</span>` : '') + `</div>` +
        `<div class="scs-gal-name">${e(s.code)} - ${e(s.name)}</div>` +
        // Client spec page 4. He does not show the group here, but it is the buyer's
        // only hint at the price tier, so it stays below his line rather than vanishing.
        (L.detailNote ? `<div class="scs-gal-note">${e(L.detailNote)}</div>` : '') +
        `<div class="scs-gal-grp">${e(this.catName(s.cat))}${oos ? ' - Sin stock' : ''}</div>` +
        pick +
        `<button type="button" class="scs-gal-choose">${e(L.choose)}</button>` +
        `<div class="scs-gal-media"></div>`;
      this.renderGalleryMedia(d.querySelector('.scs-gal-media'), code, L);
      d.querySelectorAll('.scs-gal-assign button').forEach(b => b.addEventListener('click', () => {
        this.galTarget = +b.dataset.t;
        d.querySelectorAll('.scs-gal-assign button').forEach(x => x.classList.toggle('is-active', x === b));
      }));
      d.querySelector('.scs-gal-choose').addEventListener('click', () => this.chooseFromGallery(code));
      this.galOverlay.querySelector('.scs-gal-grid').hidden = true;
      this.galOverlay.querySelector('.scs-gal-tabs').hidden = true;
      d.hidden = false;
      this.galOverlay.querySelector('.scs-gal-back').hidden = false;
      this.galSubtitle(false);
      this.galOverlay.querySelector('.scs-gal-scroll').scrollTop = 0;
    }
    // `explicit` lets a combination pass its OWN photos/videos through the same
    // renderer the colour gallery uses, instead of looking them up by colour code.
    /* Third level of the overlay: grid -> colour detail -> video (spec page 4). Kept as a
     * real level so "Volver" and Esc step back one at a time instead of jumping to the
     * grid. The player is only built on open, preserving the rule that an 84-colour
     * gallery never pulls a third-party player it does not need. */
    openGalleryVideo(url, code, poster, w, h) {
      const d = this.galOverlay.querySelector('.scs-gal-detail');
      const L = this.galLabels(), s = this.swatch(code);
      this.galVideo = url;
      this.galPhoto = null;
      d.innerHTML = '';
      const wrap = this.mk('div', 'scs-gal-vwrap');
      const node = this.videoNode(url, poster, w, h);
      if (node) { node.classList.add('scs-gal-vbig'); wrap.appendChild(node); }
      d.appendChild(wrap);
      if (s) { const nm = this.mk('div', 'scs-gal-name'); nm.textContent = s.code + ' - ' + s.name; d.appendChild(nm); }
      const btn = this.mk('button', 'scs-gal-choose');
      btn.type = 'button'; btn.textContent = L.choose;
      btn.addEventListener('click', () => this.chooseFromGallery(code));
      d.appendChild(btn);
      this.galOverlay.querySelector('.scs-gal-scroll').scrollTop = 0;
    }
    renderGalleryMedia(host, code, L, explicit) {
      // `explicit` arrives straight from a combination's own feed, so it goes through
      // the same normaliser as the colour feed rather than being trusted as-is.
      const m = explicit ? this.normMedia(explicit) : this.mediaFor(code);
      if (!m.fotos.length && !m.videos.length) {
        const p = document.createElement('div'); p.className = 'scs-gal-empty'; p.textContent = L.noMedia; host.appendChild(p); return;
      }
      if (m.fotos.length) {
        const h = document.createElement('div'); h.className = 'scs-gal-sec'; h.textContent = L.photos; host.appendChild(h);
        const g = document.createElement('div'); g.className = 'scs-gal-photos';
        m.fotos.forEach((p, i) => {
          // The grid loads the small file; the full one is only fetched if he taps.
          // Inside the colour gallery that opens its own step, the same way a video
          // does, so "Volver" and Esc step back one level instead of closing.
          if (explicit) { g.appendChild(this.lazyImg(p.t)); return; }
          const tile = this.mk('button', 'scs-gal-popen');
          tile.type = 'button';
          tile.setAttribute('aria-label', L.viewPhoto ?? 'Ver foto en grande');
          tile.appendChild(this.lazyImg(p.t));
          tile.addEventListener('click', () => this.openGalleryPhoto(m.fotos, i, code));
          g.appendChild(tile);
        });
        host.appendChild(g);
      }
      if (m.videos.length) {
        const h = document.createElement('div'); h.className = 'scs-gal-sec'; h.textContent = L.videos; host.appendChild(h);
        const g = document.createElement('div');
        // Inside a colour, videos are tiles in the same grid shape as the photos,
        // which is also how his mock-up draws them. A combination's block still
        // stacks full-width players, so that layout is left alone.
        g.className = 'scs-gal-videos' + (explicit ? '' : ' scs-gal-vgrid');
        m.videos.forEach(v => {
          const n = this.videoNode(v.u, v.p, v.w, v.h); if (!n) return;
          // Inside the colour gallery a video opens its own expanded step (spec page 4).
          // Elsewhere (a combination's fallback block) it stays inline as before.
          if (explicit) { g.appendChild(n); return; }
          const tile = this.mk('button', 'scs-gal-vopen');
          tile.type = 'button';
          tile.setAttribute('aria-label', 'Ver video');
          // Poster order: the frame Shopify pulled from his own upload first, then a
          // YouTube thumbnail for a linked video. A tile with neither is the grey
          // play button, which is still a tile, so it never silently disappears.
          const yt = String(v.u).match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
          if (v.p) tile.appendChild(this.lazyImg(v.p));
          else if (yt) tile.appendChild(this.lazyImg('https://i.ytimg.com/vi/' + yt[1] + '/hqdefault.jpg'));
          const ic = this.mk('span', 'scs-gal-vic'); ic.textContent = '▶'; tile.appendChild(ic);
          tile.addEventListener('click', () => this.openGalleryVideo(v.u, code, v.p, v.w, v.h));
          g.appendChild(tile);
        });
        host.appendChild(g);
      }
    }
    /* Photo at full size, as its own overlay step. Same contract as openGalleryVideo:
     * this.galPhoto marks the level so closeGalleryLevel() steps back to the colour
     * detail rather than shutting the gallery. Arrows walk the colour's photos without
     * going back out to the grid, which is the whole point once he has 15 of them. */
    openGalleryPhoto(fotos, i, code) {
      const d = this.galOverlay.querySelector('.scs-gal-detail');
      const L = this.galLabels(), s = this.swatch(code);
      const n = fotos.length;
      const at = ((i % n) + n) % n;
      this.galPhoto = { fotos: fotos, i: at, code: code };
      d.innerHTML = '';
      const wrap = this.mk('div', 'scs-gal-pwrap');
      const im = document.createElement('img');
      im.className = 'scs-gal-pbig';
      im.setAttribute('decoding', 'async');
      im.setAttribute('alt', '');
      /* Client 2026-08-21 on a real phone: "cuando abro sus imagenes no cargan rapido la primera
       * vez". The big view was going straight to the 2048px original, so on mobile data there was
       * a blank white area for seconds with nothing to look at. The feed already carries a 600px
       * thumbnail AND the grid has just displayed it, so it is in cache: show that instantly, then
       * swap in the full-size one once it has decoded. The upgrade is invisible because the
       * dimensions are identical. Handles both feed shapes: gallery photos are {t,f} objects,
       * combination photos are plain URL strings. */
      const item = fotos[at];
      const fullSrc = (item && item.f) || item;
      const thumbSrc = (item && item.t) || fullSrc;
      im.src = thumbSrc;
      if (fullSrc && fullSrc !== thumbSrc) {
        wrap.classList.add('scs-gal-ploading');
        const pre = new Image();
        pre.onload = () => { im.src = fullSrc; wrap.classList.remove('scs-gal-ploading'); };
        pre.onerror = () => wrap.classList.remove('scs-gal-ploading');
        pre.decoding = 'async';
        pre.src = fullSrc;
      }
      // Warm the neighbours so ‹ / › feel instant instead of blanking again.
      if (n > 1) {
        [at - 1, at + 1].forEach(k => {
          const nb = fotos[((k % n) + n) % n];
          const u = (nb && nb.f) || nb;
          if (u) { const w = new Image(); w.decoding = 'async'; w.src = u; }
        });
      }
      wrap.appendChild(im);
      if (n > 1) {
        [['prev', '‹', -1], ['next', '›', 1]].forEach(([cls, ch, step]) => {
          const b = this.mk('button', 'scs-gal-p' + cls);
          b.type = 'button';
          b.textContent = ch;
          b.setAttribute('aria-label', cls === 'prev' ? 'Anterior' : 'Siguiente');
          b.addEventListener('click', () => this.openGalleryPhoto(fotos, at + step, code));
          wrap.appendChild(b);
        });
      }
      d.appendChild(wrap);
      if (n > 1) {
        const c = this.mk('div', 'scs-gal-pcount');
        c.textContent = (at + 1) + ' / ' + n;
        d.appendChild(c);
      }
      if (s) { const nm = this.mk('div', 'scs-gal-name'); nm.textContent = s.code + ' - ' + s.name; d.appendChild(nm); }
      const btn = this.mk('button', 'scs-gal-choose');
      btn.type = 'button'; btn.textContent = L.choose;
      btn.addEventListener('click', () => this.chooseFromGallery(code));
      d.appendChild(btn);
      this.galOverlay.querySelector('.scs-gal-scroll').scrollTop = 0;
    }
    // Direct files play inline. YouTube/Vimeo load their iframe only on tap, so an
    // 84-colour gallery never pulls third-party players it may not need.
    videoNode(url, poster, w, h) {
      const u = String(url).trim();
      const yt = u.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
      const vm = u.match(/vimeo\.com\/(?:video\/)?(\d+)/);
      if (yt || vm) {
        const src = yt ? 'https://www.youtube.com/embed/' + yt[1] + '?autoplay=1' : 'https://player.vimeo.com/video/' + vm[1] + '?autoplay=1';
        const w = document.createElement('button');
        w.type = 'button'; w.className = 'scs-gal-vplay'; w.setAttribute('aria-label', 'Reproducir video');
        if (yt) w.appendChild(this.lazyImg('https://i.ytimg.com/vi/' + yt[1] + '/hqdefault.jpg'));
        const ic = document.createElement('span'); ic.className = 'scs-gal-vic'; ic.textContent = '▶'; w.appendChild(ic);
        w.addEventListener('click', () => {
          const f = document.createElement('iframe');
          f.src = src; f.loading = 'lazy'; f.allow = 'autoplay; fullscreen; picture-in-picture'; f.setAttribute('allowfullscreen', '');
          f.className = 'scs-gal-vframe'; f.title = 'Video';
          w.replaceWith(f);
        });
        return w;
      }
      if (!/\.(mp4|webm|ogv|mov|m4v)(\?|#|$)/i.test(u)) return null;
      const v = document.createElement('video');
      v.controls = true; v.playsInline = true; v.preload = 'none'; v.src = u; v.className = 'scs-gal-video';
      // preload:'none' keeps a 17 MB clip off a phone's data until it is asked for,
      // but it also means the element has nothing to paint. Without a poster that is
      // a black rectangle, and the first thing he sees of his own video is nothing.
      if (poster) v.poster = poster;
      // ...and with nothing loaded the browser also has no idea how TALL the clip is,
      // so it lays out its default 2:1 box and a vertical video sits in it with empty
      // strips either side until you press play. That is the bug he photographed. The
      // feed carries the real dimensions, so state the shape up front and the element
      // is correct from the first paint, with no metadata request.
      if (Number(w) > 0 && Number(h) > 0) v.style.aspectRatio = Number(w) + ' / ' + Number(h);
      return v;
    }
    // Assign the colour to the remembered selector, exactly as if it had been picked
    // in the palette (same state, same preview, same line item properties, same gate).
    chooseFromGallery(code) {
      const i = this.galTarget || 0;
      const s = this.swatch(code); if (!s) return;
      // The colour carries its own category, which may not be the one that selector
      // is on. Move the selector to it, or the price key and the palette would disagree.
      if (s.cat && this.state.cat[i] !== s.cat) {
        this.state.cat[i] = s.cat;
        const sel = this.qa('.scs-cat')[i]; if (sel) sel.value = s.cat;
      }
      this.palTarget = i;
      this.pickSwatch(code);   // reuses the single assignment path
      this.closeGallery();
    }

    /* ---------- colour picker (printed): CMYK-native, HSV-style UI ---------- */
    // Ported from the client's reference tool. Looks like an HSV square + hue bar but is
    // CMYK-native: the hue bar walks ink mixtures (Y -> M+Y -> M -> C+M -> C -> C+Y), the
    // square X = ink saturation (0=white/pastel, 1=full ink) and Y = black (K). CMYK is
    // the source of truth and HEX is computed from it, so every colour is printable AND
    // pastels are reachable (low saturation) -- which was the whole point.
    openCmyk(i) {
      this.cmykTarget = i;
      ACTIVE = this;
      if (!this.state.printed[i]) this.state.printed[i] = { hue: 0, sat: 0, k: 0 };
      this.paintPicker();
      // Untouched default: show the client's EXACT configured hex until the first drag.
      const seed = this.state.printedSeedHex[i];
      if (seed) { const hc = this.popCmyk.querySelector('.scs-code-hex'); if (hc) hc.textContent = seed.toUpperCase(); this.setCurHex(seed); }
      this.show(this.popCmyk, this.q(`.scs-dot[data-i="${i}"]`));
    }
    // The colour for a stored {hue,sat,k}: CMYK (source of truth) -> RGB/HEX.
    colorFromState(st) {
      const b = this.hueToBaseCMY(st.hue || 0), s = st.sat || 0;
      const cmyk = { c: Math.round(b[0] * s * 100), m: Math.round(b[1] * s * 100), y: Math.round(b[2] * s * 100), k: Math.round((st.k || 0) * 100) };
      return { cmyk, hex: this.cmykToHex(cmyk) };
    }
    paintPicker() {
      const st = this.state.printed[this.cmykTarget], { cmyk, hex } = this.colorFromState(st);
      const sq = this.popCmyk.querySelector('.scs-sv'); if (sq) sq.style.backgroundColor = this.hueSolidHex(st.hue || 0);
      const svk = this.popCmyk.querySelector('.scs-sv .scs-knob'); if (svk) { svk.style.left = ((st.sat || 0) * 100) + '%'; svk.style.top = ((st.k || 0) * 100) + '%'; svk.style.background = hex; }
      const hk = this.popCmyk.querySelector('.scs-hue .scs-knob'); if (hk) hk.style.top = ((st.hue || 0) / 360 * 100) + '%';
      const cc = this.popCmyk.querySelector('.scs-code-cmyk'); if (cc) cc.textContent = this.cmykStr(cmyk);
      const hc = this.popCmyk.querySelector('.scs-code-hex'); if (hc) hc.textContent = hex.toUpperCase();
      this.setCurHex(hex);
      return { cmyk, hex };
    }
    // El hex "de arriba" ahora es un <input>: se escribe el value, no el textContent. Robusto por si
    // una version vieja del popover todavia tuviera un <span>.
    setCurHex(hex) {
      const hh = this.popCmyk.querySelector('.scs-cur-hex'); if (!hh) return;
      const v = String(hex || '').toUpperCase();
      if (hh.tagName === 'INPUT') { if (document.activeElement !== hh) hh.value = v; } else hh.textContent = v;
    }
    // Cliente 2026-09-24: aplica un hex escrito a mano en el selector de impreso. Lo posiciona en la
    // rueda (aproximado) y lo guarda como valor EXACTO (printedSeedHex), asi el pedido lo lleva tal
    // cual. Un valor invalido no hace nada (repone el que estaba).
    applyTypedHex(raw) {
      const i = this.cmykTarget;
      const s = String(raw || '').trim().replace(/^#/, '');
      const hh = this.popCmyk.querySelector('.scs-cur-hex');
      if (!/^([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(s)) {
        // invalido: reponer lo que estaba mostrado
        const cur = this.state.printedSeedHex[i] || (this.state.printed[i] ? this.colorFromState(this.state.printed[i]).hex : '#ffffff');
        if (hh && hh.tagName === 'INPUT') hh.value = cur.toUpperCase();
        return;
      }
      const hex = this.normHex(s);                       // -> #rrggbb
      this.state.printed[i] = this.hexToState(hex);      // posiciona los knobs (aprox)
      this.state.printedSeedHex[i] = hex;                // valor EXACTO para mostrar y para el pedido
      this.paintPicker();                                // mueve knobs; pinta displays con el modelo (aprox)
      // Los displays y la muestra van con el hex EXACTO tipeado, no con el aprox del modelo.
      const up = hex.toUpperCase();
      const hc = this.popCmyk.querySelector('.scs-code-hex'); if (hc) hc.textContent = up;
      if (hh && hh.tagName === 'INPUT') hh.value = up;
      const svk = this.popCmyk.querySelector('.scs-sv .scs-knob'); if (svk) svk.style.background = hex;
      const dot = this.q(`.scs-dot[data-i="${i}"]`); if (dot) dot.style.background = hex;
      const hx = this.q(`[data-hx="${i}"]`); if (hx) hx.textContent = up;
      this.recalc();
    }
    // A drag on the square (x=saturation, y=black) or hue bar commits the pick.
    pickFromPicker() {
      const { hex } = this.paintPicker();
      const i = this.cmykTarget;
      this.state.printedSeedHex[i] = null; // configured default now overridden by the buyer
      const dot = this.q(`.scs-dot[data-i="${i}"]`); if (dot) dot.style.background = hex;
      const hx = this.q(`[data-hx="${i}"]`); if (hx) hx.textContent = hex.toUpperCase();
      this.recalc();
    }

    /* ---------- shared popovers (route to ACTIVE instance) ---------- */
    ensurePopovers() {
      const existing = document.querySelector('.scs-backdrop');
      if (existing) {
        this.backdrop = existing; this.popPalette = document.querySelector('.scs-palette'); this.popCmyk = document.querySelector('.scs-cmyk-pop');
        this.zoomOverlay = document.querySelector('.scs-zoom');
        if (this.zoomOverlay) {
          this.zoomImg = this.zoomOverlay.querySelector('.scs-zoom-img');
          this.zoomCap = this.zoomOverlay.querySelector('.scs-zoom-cap');
          this.zoomPh = this.zoomOverlay.querySelector('.scs-zoom-ph');
          // Un overlay dibujado por una version anterior no trae el <img>; se lo agrega en vez de
          // quedarse sin foto, que es peor que no tener zoom.
          if (!this.zoomPh && this.zoomImg) {
            this.zoomPh = document.createElement('img');
            this.zoomPh.className = 'scs-zoom-ph'; this.zoomPh.alt = '';
            this.zoomImg.insertAdjacentElement('afterend', this.zoomPh);
          }
        }
        this.galOverlay = document.querySelector('.scs-gal');
        return;
      }
      this.backdrop = this.mk('div', 'scs-backdrop');
      this.popPalette = this.mk('div', 'scs-pop scs-palette');
      this.popPalette.innerHTML = `<div class="scs-head"><span class="scs-ttl">Seleccioná un color</span><button class="scs-x" type="button" aria-label="Cerrar">✕</button></div><div class="scs-cats"></div><div class="scs-grid"></div>` +
        `<button type="button" class="scs-pal-gal" hidden>Ver fotos reales de los colores</button>`;
      this.popCmyk = this.mk('div', 'scs-pop scs-cmyk-pop');
      // Cliente 2026-09-24 (vinilo impreso): "quiero ingresar el codigo hex de corel para poner
      // directo ese color... reemplazar el de arriba por el espacio para el Hex escrito". El hex de
      // arriba pasa de ser un cartel a un campo editable: pega el codigo y queda EXACTO, sin tener
      // que acertarlo arrastrando ("muevo 1mm y cambia por completo el codigo"). applyTypedHex() lo
      // aplica y lo guarda como valor exacto (printedSeedHex), asi el pedido lleva ese hex tal cual.
      this.popCmyk.innerHTML = `<div class="scs-head"><span class="scs-ttl">Seleccioná un color</span><input class="scs-cur-hex" type="text" spellcheck="false" autocomplete="off" autocapitalize="off" maxlength="7" value="#FFFFFF" aria-label="Código hex, por ejemplo #0BA89A" title="Pegá el código hex y presioná Enter"><button class="scs-x" type="button" aria-label="Cerrar">✕</button></div>` +
        `<div class="scs-sv-row"><div class="scs-sv"><div class="scs-knob"></div></div><div class="scs-hue"><div class="scs-knob"></div></div></div>` +
        `<div class="scs-cmyk-codes"><div class="scs-codebox scs-cmyk-box"><span class="scs-code-k">CMYK</span><span class="scs-code-v scs-code-cmyk">C0 M0 Y0 K0</span></div><div class="scs-codebox scs-hex-box"><span class="scs-code-k">HEX</span><span class="scs-code-v scs-code-hex">#FFFFFF</span></div></div>`;
      // Click-to-enlarge overlay (client #16): its own dimmed backdrop (the palette
      // backdrop is transparent). Shared across instances; click/✕/Esc closes.
      this.zoomOverlay = this.mk('div', 'scs-zoom');
      /* R5-5, cliente 2026-08-26: la foto de la combinacion "abre pero no puedo hacer zoom ni nada",
       * al reves que Simil Original y Elegir mis colores, que abren en el visor del tema y ahi si
       * amplian. La muestra de color se sigue pintando como background de un div, que para un disco
       * redondo esta bien, pero un background no se puede llevar a tamaño natural ni scrollear. Para
       * las FOTOS va un <img> de verdad, que es lo que permite repetir el mismo gesto del tema:
       * clic para pasar a tamaño natural, clic de nuevo para volver. */
      this.zoomOverlay.innerHTML = `<div class="scs-zoom-inner"><div class="scs-zoom-img"></div><img class="scs-zoom-ph" alt="">` +
        `<div class="scs-zoom-cap"></div><button class="scs-zoom-x" type="button" aria-label="Cerrar">✕</button></div>`;
      this.zoomImg = this.zoomOverlay.querySelector('.scs-zoom-img');
      this.zoomPh = this.zoomOverlay.querySelector('.scs-zoom-ph');
      this.zoomCap = this.zoomOverlay.querySelector('.scs-zoom-cap');
      const closeZoom = () => {
        this.zoomOverlay.classList.remove('is-show');
        // Leave the overlay in its default (round swatch) shape for the next opener.
        this.zoomOverlay.classList.remove('scs-zoom-photo', 'is-zoomed');
        if (this.zoomPh) { this.zoomPh.classList.remove('scs-zoomed'); this.zoomOverlay.scrollTop = this.zoomOverlay.scrollLeft = 0; }
        this.lockPage(false);
      };
      /* Mismo gesto que en el visor del tema (ver el toggle de .scs-zoomed sobre
       * .product-media-modal__content > img): clic amplia a tamaño natural y deja scrollear, clic de
       * nuevo vuelve. Solo en escritorio, igual que alla, porque en el celular se hace pinch y el
       * cliente ya dijo que ahi esta bien. `is-zoomed` va en el overlay porque es el que tiene que
       * pasar a scrollear y dejar de centrar. */
      this.zoomOverlay.addEventListener('click', ev => {
        if (!ev.target.classList.contains('scs-zoom-ph')) return;
        if (!window.matchMedia('(min-width: 750px)').matches) return;
        const on = ev.target.classList.toggle('scs-zoomed');
        this.zoomOverlay.classList.toggle('is-zoomed', on);
        if (!on) this.zoomOverlay.scrollTop = this.zoomOverlay.scrollLeft = 0;
      });
      this.zoomOverlay.addEventListener('click', ev => { if (ev.target === this.zoomOverlay || ev.target.classList.contains('scs-zoom-inner') || ev.target.classList.contains('scs-zoom-x')) closeZoom(); });
      document.addEventListener('keydown', ev => { if (ev.key === 'Escape') closeZoom(); });

      // Colour gallery overlay (client spec point 3). Deliberately an overlay over
      // the product page and NOT a separate page, so the buyer never loses the
      // selection in progress. Shared across instances, routed through ACTIVE.
      this.galOverlay = this.mk('div', 'scs-gal');
      this.galOverlay.innerHTML =
        `<div class="scs-gal-box" role="dialog" aria-modal="true" aria-label="Galería de colores">` +
          `<div class="scs-gal-head">` +
            `<button type="button" class="scs-gal-back" hidden>‹ Volver</button>` +
            `<span class="scs-gal-head-txt"><span class="scs-gal-ttl">Galería de colores</span>` +
              `<span class="scs-gal-sub" hidden></span></span>` +
            `<button type="button" class="scs-gal-x" aria-label="Cerrar">✕</button>` +
          `</div>` +
          `<div class="scs-gal-tabs"></div>` +
          `<div class="scs-gal-scroll"><div class="scs-gal-grid"></div><div class="scs-gal-detail" hidden></div></div>` +
        `</div>`;
      this.galOverlay.querySelector('.scs-gal-x').addEventListener('click', () => ACTIVE && ACTIVE.closeGallery());
      this.galOverlay.querySelector('.scs-gal-back').addEventListener('click', () => ACTIVE && ACTIVE.galleryBack());
      // Tapping the dimmed area outside the panel closes, same as ✕.
      this.galOverlay.addEventListener('click', ev => { if (ev.target === this.galOverlay && ACTIVE) ACTIVE.closeGallery(); });
      document.addEventListener('keydown', ev => {
        if (!ACTIVE || !ACTIVE.galleryOpen()) return;
        // Arrows walk an enlarged photo, but only while one is open, so they stay
        // free for the browser everywhere else in the overlay.
        if (ACTIVE.galPhoto && (ev.key === 'ArrowLeft' || ev.key === 'ArrowRight')) {
          const g = ACTIVE.galPhoto;
          if (g.fotos.length > 1) {
            ev.preventDefault();
            ACTIVE.openGalleryPhoto(g.fotos, g.i + (ev.key === 'ArrowRight' ? 1 : -1), g.code);
          }
          return;
        }
        if (ev.key !== 'Escape') return;
        // Esc mirrors the two buttons: step back out of a colour first, then close.
        if (ACTIVE.galVideo || ACTIVE.galPhoto || ACTIVE.galDetail) ACTIVE.galleryBack(); else ACTIVE.closeGallery();
      });
      // The other half of the history trap started in openGallery(): the OS/browser
      // back navigation already happened by the time this fires, so just fold the
      // overlay's own state back closed to match - do not call history.back() again.
      window.addEventListener('popstate', () => {
        if (!ACTIVE || !ACTIVE.galleryOpen()) return;
        /* REVERTED 2026-08-21 to this simpler behaviour, deliberately.
         *
         * He asked for the gesture to walk back through the popup's own levels ("una
         * ventana emergente en la cual puedo retroceder dentro de ella"). That was tried
         * by calling galleryBack() here and re-arming a fresh history entry with
         * pushState during the popstate. It made things WORSE on a real Android device:
         * his video shows a stutter and then the browser leaving the product page
         * entirely and landing on /collections. Re-arming an entry from inside a popstate
         * handler races the OS back-swipe, which has already committed a navigation, so
         * the sentinel is not reliably there for the next gesture.
         *
         * Closing the whole popup in one step is the behaviour that was actually verified
         * working: back closes the gallery and stays on the product page, which is the
         * defect he originally reported. Stepping back level by level is a nicety on top
         * of that, and it is not worth trading away staying on the page. Doing it properly
         * needs one pushed entry per level (grid / colour / photo) plus a guard so our own
         * "Volver" and "✕" do not double-step, and it needs testing on a real device
         * before it ships again. */
        ACTIVE._galClosingFromPopstate = true;
        ACTIVE.closeGallery();
        ACTIVE._galClosingFromPopstate = false;
      });
      // Client 2026-08-20: "estaría comodo poder cambiar de color deslizando con el
      // dedo... sería como la navegación de fotos de la galería del teléfono." Swipe
      // left/right on a colour's detail screen steps to the next/previous colour in
      // the same category, standard phone-gallery direction (swipe left = next).
      // Scoped to .scs-gal-scroll (not the whole overlay) so it never fights the
      // dimmed-backdrop tap-to-close, and gated to "looking at one colour, nothing
      // deeper open" so it never fights the enlarged-photo viewer's own arrow-key
      // navigation (galPhoto) or a playing video (galVideo).
      const scroll = this.galOverlay.querySelector('.scs-gal-scroll');
      let touchStartX = null, touchStartY = null;
      scroll.addEventListener('touchstart', ev => {
        if (ev.touches.length !== 1) return;
        touchStartX = ev.touches[0].clientX;
        touchStartY = ev.touches[0].clientY;
      }, { passive: true });
      scroll.addEventListener('touchend', ev => {
        if (touchStartX === null || !ACTIVE) return;
        const t = ev.changedTouches[0];
        const dx = t.clientX - touchStartX, dy = t.clientY - touchStartY;
        touchStartX = touchStartY = null;
        const SWIPE_MIN = 50;
        if (Math.abs(dx) < SWIPE_MIN || Math.abs(dx) < Math.abs(dy)) return;
        if (!ACTIVE.galDetail || ACTIVE.galVideo || ACTIVE.galPhoto) return;
        ACTIVE.galDetailStep(dx < 0 ? 1 : -1);
      }, { passive: true });

      document.body.append(this.backdrop, this.popPalette, this.popCmyk, this.zoomOverlay, this.galOverlay);
      /* Desktop lightbox: click the image to drop the size cap and pan around it, click
       * again to fit. The cap alone (added for "que no quede de un tamano extra gigante")
       * had removed magnification entirely, which he reported as "no agranda ni da opcion
       * de agrandar". Delegated on the document because the theme builds and rebuilds the
       * modal's contents itself, so there is nothing stable to bind to at setup time. */
      /* The theme's lightbox closes when the click lands on the modal ELEMENT itself
       * (`if (event.target === this) this.hide()` in global.js). That is a sane default until you
       * notice that these product photos have a WHITE background and so does the modal, so the
       * edge of the picture is invisible: aiming at the image and missing by a few pixels silently
       * exits instead of zooming. Reported 2026-08-21: "at middle after click it lands me back to
       * main image product rather than staying at that image with zoom in zoom out until I click
       * the top right cross".
       * So: swallow background clicks in the CAPTURE phase, before the theme's own listener on the
       * element can run, and leave ✕ / Escape / the backdrop button as the ways out. */
      /* Read from the theme's own global.js rather than guessed at, after two wrong attempts:
       *
       *   if (this.classList.contains('media-modal')) {
       *     this.addEventListener('pointerup', (event) => {
       *       if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model'))
       *         this.hide();
       *     });
       *   }
       *
       * So ANY mouse pointerup inside this modal closes it - including one on the photo itself -
       * and it fires on POINTERUP, before `click`. That is why zooming appeared to "work and then
       * drop back to the product page": the zoom class toggled on click, but the modal had already
       * been hidden a moment earlier. It also made the white space beside the picture an invisible
       * exit button, which is what the client kept hitting.
       * Swallowing the pointerup in the capture phase leaves ✕ and Escape as the ways out, which is
       * what was actually asked for. */
      document.addEventListener('pointerup', ev => {
        const t = ev.target;
        if (!t || !t.closest) return;
        if (!t.closest('.product-media-modal.media-modal')) return;
        if (t.closest('.product-media-modal__toggle')) return;   // the ✕ must keep working
        ev.stopPropagation();
      }, true);
      document.addEventListener('click', ev => {
        const img = ev.target.closest && ev.target.closest('.product-media-modal__content > img');
        if (!img) return;
        if (!window.matchMedia('(min-width: 750px)').matches) return;   // phones pinch-zoom instead
        img.classList.toggle('scs-zoomed');
      });
      const close = () => ACTIVE && ACTIVE.close();
      /* Cliente 2026-08-22, probando T4: con un color abierto, tocar OTRO circulo solo cerraba
       * el popover y habia que tocarlo una segunda vez para abrirlo. El backdrop es una capa
       * fija sobre toda la pantalla (`position:fixed;inset:0;z-index:100`), asi que el segundo
       * circulo queda DEBAJO y su propio `click` no llega nunca: el toque lo come el backdrop.
       * Se reencamina el toque a lo que haya bajo ese punto. Si es otro selector de color, el
       * popover salta a ese color en un solo toque; si es el mismo que ya estaba abierto, se
       * cierra (se conserva el toque-para-cerrar); si no hay ninguno, cierra como antes.
       * Vale para los circulos de impreso (`.scs-dot`) y para los campos de corte
       * (`.scs-swatch-field`), que tienen exactamente el mismo problema. */
      const reroute = (x, y) => {
        if (typeof document.elementsFromPoint !== 'function') return false;
        for (const el of document.elementsFromPoint(x, y)) {
          if (!el || !el.closest) continue;
          if (el.closest('.scs-pop')) return false;      // dentro del popover no se reencamina
          const dot = el.closest('.scs-dot');
          const fld = dot ? null : el.closest('.scs-swatch-field');
          const node = dot || fld;
          if (!node) continue;
          const host = node.closest('sticker-color-selector');
          if (!host || typeof host.openCmyk !== 'function') return false;
          const i = +node.dataset.i || 0;
          const openPop = dot ? this.popCmyk : this.popPalette;
          const openIdx = dot ? host.cmykTarget : host.palTarget;
          // Mismo selector ya abierto: es un toque para cerrar, no para cambiar.
          if (host === ACTIVE && openPop && openPop.classList.contains('is-show') && openIdx === i) return false;
          if (dot) host.openCmyk(i); else host.openPalette(i);
          return true;
        }
        return false;
      };
      this.backdrop.addEventListener('click', ev => { if (!reroute(ev.clientX, ev.clientY)) close(); });
      document.addEventListener('keydown', ev => { if (ev.key === 'Escape') close(); });
      this.popPalette.querySelector('.scs-x').addEventListener('click', close);
      // Opening the gallery from inside the palette keeps palTarget, so "Elegir este
      // color" lands on the selector the buyer was already filling.
      this.popPalette.querySelector('.scs-pal-gal').addEventListener('click', () => { if (ACTIVE) ACTIVE.openGallery(ACTIVE.palTarget); });
      this.popCmyk.querySelector('.scs-x').addEventListener('click', close);
      const svBox = this.popCmyk.querySelector('.scs-sv'), hueBar = this.popCmyk.querySelector('.scs-hue');
      hueBar.style.background = this.hueBarGradient();
      this.bindDrag(svBox, (x, y) => { if (!ACTIVE) return; const st = ACTIVE.state.printed[ACTIVE.cmykTarget]; if (!st) return; st.sat = x; st.k = y; ACTIVE.pickFromPicker(); });
      this.bindDrag(hueBar, (x, y) => { if (!ACTIVE) return; const st = ACTIVE.state.printed[ACTIVE.cmykTarget]; if (!st) return; st.hue = y * 360; ACTIVE.pickFromPicker(); });
      // Campo hex escribible (cliente 2026-09-24): al confirmar (Enter o salir del campo) se aplica.
      const hexIn = this.popCmyk.querySelector('.scs-cur-hex');
      if (hexIn && hexIn.tagName === 'INPUT') {
        hexIn.addEventListener('change', () => { if (ACTIVE) ACTIVE.applyTypedHex(hexIn.value); });
        hexIn.addEventListener('keydown', ev => { if (ev.key === 'Enter') { ev.preventDefault(); if (ACTIVE) ACTIVE.applyTypedHex(hexIn.value); hexIn.blur(); } });
        hexIn.addEventListener('focus', () => hexIn.select());
        hexIn.addEventListener('click', ev => ev.stopPropagation());
      }
    }
    /* T4, client 2026-08-21: "En vinilo impreso el selector se sale de la pantalla, en el editor del
     * tema no pasa pero en la web del celular real si" (adjuntos/2026-08-21-r4-08-*.png, foto de un
     * celular con la rueda CMYK cortada por el borde derecho).
     *
     * Habia dos errores juntos en el calculo:
     *  - Se mezclaban dos sistemas de coordenadas: `left` se escribia en coordenadas de PAGINA
     *    (`r.left + scrollX`) pero la correccion comparaba contra `window.innerWidth`, que es de
     *    VIEWPORT. Con cualquier desplazamiento horizontal el limite quedaba corrido.
     *  - `window.innerWidth` en un celular real es el viewport VISUAL: cambia con el zoom y con la
     *    barra del navegador, y puede ser mayor que el ancho de layout. En el iframe del editor de
     *    temas coinciden, y por eso ahi no se veia. `document.documentElement.clientWidth` es el
     *    ancho de layout y no se mueve.
     * Ademas se mide el ancho con `left` en cero, porque medirlo con la posicion anterior puesta
     * arrastraba el error de la apertura anterior. */
    show(pop, anchor) {
      this.close();
      this.backdrop.classList.add('is-show'); pop.classList.add('is-show');
      const M = 8;
      const vw = document.documentElement.clientWidth || window.innerWidth;
      pop.style.maxWidth = Math.max(200, vw - M * 2) + 'px';
      pop.style.left = '0px';
      const w = pop.getBoundingClientRect().width;
      const r = anchor.getBoundingClientRect();
      let left = r.left;                                   // viewport
      if (left + w > vw - M) left = vw - w - M;
      if (left < M) left = M;
      pop.style.top = (r.bottom + window.scrollY + 8) + 'px';
      pop.style.left = (left + window.scrollX) + 'px';     // pagina, una sola vez y al final
    }
    close() { if (this.backdrop) this.backdrop.classList.remove('is-show'); if (this.popPalette) this.popPalette.classList.remove('is-show'); if (this.popCmyk) this.popCmyk.classList.remove('is-show'); }

    bindDrag(el, fn) {
      const move = ev => { const r = el.getBoundingClientRect(); const p = ev.touches ? ev.touches[0] : ev; fn(Math.min(1, Math.max(0, (p.clientX - r.left) / r.width)), Math.min(1, Math.max(0, (p.clientY - r.top) / r.height))); };
      const up = () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); };
      el.addEventListener('mousedown', ev => { move(ev); document.addEventListener('mousemove', move); document.addEventListener('mouseup', up); });
      el.addEventListener('touchstart', ev => { move(ev); ev.preventDefault(); }, { passive: false });
      el.addEventListener('touchmove', ev => { move(ev); ev.preventDefault(); }, { passive: false });
    }

    /* ---------- colour math (CMYK-native, ported from the client's tool) ---------- */
    // The hue axis walks real ink combinations, not the RGB rainbow. Each anchor is the
    // pure-ink mix [C,M,Y] (max component = 1) at that angle.
    hueAnchors() { return [[0, [0, 0, 1]], [60, [0, 1, 1]], [120, [0, 1, 0]], [180, [1, 1, 0]], [240, [1, 0, 0]], [300, [1, 0, 1]], [360, [0, 0, 1]]]; }
    hueToBaseCMY(h) {
      const A = this.hueAnchors();
      for (let i = 0; i < A.length - 1; i++) {
        const d0 = A[i][0], a = A[i][1], d1 = A[i + 1][0], b = A[i + 1][1];
        if (h >= d0 && h <= d1) { const t = (h - d0) / (d1 - d0); return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t]; }
      }
      return [0, 0, 1];
    }
    // CMYK -> RGB with process-ink primaries + 96% black strength + 15% "ink look"
    // desaturation. Matches the client's tool exactly (e.g. C87 M0 Y35 K0 -> #30AE99).
    cmykToRgb(cm) {
      const C = (cm.c || 0) / 100, M = (cm.m || 0) / 100, Y = (cm.y || 0) / 100, K = (cm.k || 0) / 100;
      const INK = [[C, [0, 174, 239]], [M, [236, 0, 140]], [Y, [255, 241, 0]]];
      const rgb = [255, 255, 255];
      INK.forEach(pair => { const amt = pair[0], ink = pair[1]; for (let i = 0; i < 3; i++) rgb[i] *= (1 - amt * (1 - ink[i] / 255)); });
      for (let i = 0; i < 3; i++) rgb[i] *= (1 - K * 0.96);
      const gray = 0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2], D = 0.15;
      for (let i = 0; i < 3; i++) rgb[i] = rgb[i] + (gray - rgb[i]) * D;
      return rgb.map(v => Math.max(0, Math.min(255, Math.round(v))));
    }
    cmykToHex(cm) { const [r, g, b] = this.cmykToRgb(cm); const f = n => ('0' + n.toString(16)).slice(-2); return '#' + f(r) + f(g) + f(b); }
    hueSolidHex(h) { const b = this.hueToBaseCMY(h); return this.cmykToHex({ c: b[0] * 100, m: b[1] * 100, y: b[2] * 100, k: 0 }); }
    hueBarGradient() { return 'linear-gradient(to bottom,' + this.hueAnchors().map(a => `${this.hueSolidHex(a[0])} ${(a[0] / 360 * 100).toFixed(2)}%`).join(',') + ')'; }
    // Seed the picker from a configured HEX default (approximate {hue,sat,k}); exact
    // fidelity isn't needed since the dot shows the configured hex until the buyer picks.
    hexToState(hex) {
      hex = this.normHex(hex).replace('#', '');
      const r = parseInt(hex.slice(0, 2), 16) / 255, g = parseInt(hex.slice(2, 4), 16) / 255, b = parseInt(hex.slice(4, 6), 16) / 255;
      const k = 1 - Math.max(r, g, b); let c = 0, m = 0, y = 0;
      if (k < 0.999) { const d = 1 - k; c = (1 - r - k) / d; m = (1 - g - k) / d; y = (1 - b - k) / d; }
      const mx = Math.max(c, m, y), base = mx > 0 ? [c / mx, m / mx, y / mx] : [0, 0, 0];
      return { hue: this.baseCmyToHue(base), sat: mx, k };
    }
    baseCmyToHue(base) {
      const A = this.hueAnchors(); let best = 0, bestErr = Infinity;
      for (let i = 0; i < A.length - 1; i++) {
        const a = A[i][1], b = A[i + 1][1]; let num = 0, den = 0;
        for (let j = 0; j < 3; j++) { const ab = b[j] - a[j]; num += ab * (base[j] - a[j]); den += ab * ab; }
        let t = den > 0 ? num / den : 0; t = Math.max(0, Math.min(1, t));
        let err = 0; for (let j = 0; j < 3; j++) { const v = a[j] + (b[j] - a[j]) * t; err += (v - base[j]) * (v - base[j]); }
        if (err < bestErr) { bestErr = err; best = A[i][0] + t * (A[i + 1][0] - A[i][0]); }
      }
      return best;
    }
    cmykStr(cm) { return `C${Math.round(cm.c || 0)} M${Math.round(cm.m || 0)} Y${Math.round(cm.y || 0)} K${Math.round(cm.k || 0)}`; }

    /* ---------- utils ---------- */
    q(s) { return this.querySelector(s); }
    qa(s) { return Array.from(this.querySelectorAll(s)); }
    mk(t, c) { const el = document.createElement(t); el.className = c; return el; }
    esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, m => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[m])); }
    /* Nombre limpio para una propiedad del pedido.
     *
     * El carrito dibuja cada renglon como "<nombre>: <valor>", asi que un nombre que YA termina
     * en dos puntos sale como "Detalles:: Lineas negras". `detalles_label` viene justo asi
     * ("Detalles:"), porque el mismo texto se usa de etiqueta arriba del desplegable, donde los
     * dos puntos si corresponden.
     *
     * Eso ya estaba contemplado para Detalles y para nada mas. Los otros cuatro nombres que
     * salen de ajustes editables (la combinacion, su foto, su detalle, y el nombre del plazo)
     * no lo tenian: alcanzaba con que el escribiera "Combinacion:" en el editor para que el
     * carrito dijera "Combinacion::". Se centraliza aca para que valga para todos. */
    clave(v, porDefecto) {
      const t = String(v == null || v === '' ? (porDefecto || '') : v);
      return t.replace(/\s*:\s*$/, '').trim();
    }
  });
})();